<template>
  <div class="main">
    <div class="left-menu-container">
      <Menus
        :menu="menu"
        icon="https://dsmlib.logistics.systems/Icons/icons8-maintenance-64.png"
        @menuClicked="menuClicked"
    /></div>
    <div class="right-container">
      <div class="right-inner-container flex-shrink-0 d-flex justify-content-end align-items-center" style="gap: 8px">
        {{ activeDateString }}
        <ButtonFilter buttonText="Change Filter" filterPosition="right" :filterWidth="220" :isOpen.sync="filterOpen">
          <div class="d-flex flex-column">
            <BoxContainer
              title="Date"
              class="d-flex"
              :isCheckBox="true"
              :isChecked.sync="dateFilterActive"
              @checkBoxClicked="dateFilterCheckBoxClicked"
              :checkBoxIsDisabled="workshopJobsTableLoading"
            >
              <DateTimePicker
                placeholder="From"
                :value.sync="filterStartDate"
                :showTime="false"
                :isDisabled="!dateFilterActive || workshopJobsTableLoading"
              />
              <DateTimePicker
                placeholder="To"
                :value.sync="filterEndDate"
                :showTime="false"
                :isDisabled="!dateFilterActive || workshopJobsTableLoading"
              />
            </BoxContainer>
            <BoxContainer
              title="Job #"
              class="d-flex"
              :isCheckBox="true"
              :isChecked.sync="jobNoFilterActive"
              @checkBoxClicked="jobNoFilterCheckBoxClicked"
              :checkBoxIsDisabled="workshopJobsTableLoading"
            >
              <Dropdown
                placeholder="Start Job #"
                :selectedObject.sync="filterStartJobNo"
                :isDisabled="!jobNoFilterActive || workshopJobsTableLoading"
                :errorMessage.sync="filterStartJobNoError"
                :items="EMSJobNoList"
                itemKey="JobNo"
                @selected="startJobNoSelected"
              />
              <Dropdown
                placeholder="End Job #"
                :selectedObject.sync="filterEndJobNo"
                :isDisabled="!jobNoFilterActive || workshopJobsTableLoading"
                :errorMessage.sync="filterEndJobNoError"
                :items="EMSJobNoList"
                itemKey="JobNo"
                @selected="endJobNoSelected"
              />
            </BoxContainer>
            <Button class="ml-auto mt-2" @click="filterRefreshClick">Refresh</Button>
          </div>
        </ButtonFilter>
      </div>
      <div class="right-inner-container flex-grow-1 pt-0" style="min-height: 0">
        <TableBox
          :tableData="workshopJobsTableData"
          :selectedRow.sync="workshopJobsTableSelectedRows"
          title="Workshop Jobs"
          :dataType="workshopJobsTableDataType"
          :loading="workshopJobsTableLoading"
          :maxSelection="1"
          :viewMode="viewMode"
          :hideColumns="['TimeIn', 'TimeOut', 'IsLatest', 'canDelete']"
          :columnOrderViews.sync="workshopJobsTableColumnOrderViews"
          :columnOrderID="workshopJobsTableColumnOrderID"
        />
      </div>
    </div>

    <PopupEntry
      v-if="showPopup"
      :show.sync="showPopup"
      class="popup-entry"
      :updatedBy="lastUpdatedInfo"
      :createdBy="createdInfo"
      :title="popUpTitle"
      :viewMode="viewMode"
      :requiredError="popupEntryRequiredError"
      @requiredClicked="popupEntryRequiredClicked"
      :successMessages="popupEntrySuccessMessages"
      :errorMessages="popupEntryErrorMessages"
      @save="popupEntrySaveClicked"
    >
      <div class="top-info-container">
        <InputQuery
          placeholder="Inventory # - Inventory Code"
          :isRequired="true"
          :selectedObject.sync="jobInventoryValue"
          :errorMessage.sync="jobInventoryValueError"
          :items="vehicleList"
          itemKey="label"
          :tableBoxHideColumns="['label']"
          :isDisabled="viewMode || menuMode == 'Edit'"
          ref="inventoryNo"
          @selected="inventoryNoSelected"
        />
        <DateTimePicker
          placeholder="Job Date"
          :isRequired="true"
          :value.sync="jobDateValue"
          :errorMessage.sync="jobDateValueError"
          :isDisabled="viewMode"
          ref="jobDate"
        />
      </div>

      <div class="tab-component-container">
        <Tab
          class="tab-component"
          :tabs="tabItems"
          :tabIndex.sync="selectedTabIndex"
          @tabClicked="tabClick"
          :preventTabSwitch="true"
        >
          <div class="tab-container" slot="Job Details">
            <InputQuery
              placeholder="Trailer"
              :selectedObject.sync="trailerNoValue"
              :items="trailerList"
              itemKey="label"
              :tableBoxHideColumns="['label']"
              :isDisabled="!jobInventoryValue || viewMode"
            />
            <InputQuery
              placeholder="Requested By"
              :selectedObject.sync="requestedByValue"
              :items="employeeList"
              itemKey="label"
              :tableBoxHideColumns="['label', 'Employee']"
              :isDisabled="!jobInventoryValue || viewMode"
            />
            <Dropdown
              placeholder="Repair Type"
              :isRequired="true"
              :selectedObject.sync="repairTypeValue"
              :errorMessage.sync="repairTypeValueError"
              :items="repairTypeOptions"
              itemKey="label"
              :isDisabled="!jobInventoryValue || viewMode"
              ref="repairType"
            />
            <RadioButtonSet
              labelKey="label"
              :isHorizontal="true"
              :showBox="true"
              :isRequired="true"
              title="Internal / External"
              :buttons="intExtOptions"
              style="margin: 0px 0px 10px 5px; display: flex"
              :selected.sync="internalExternalValue"
              :errorMessage.sync="internalExternalValueError"
              :isDisabled="!jobInventoryValue || viewMode"
              ref="intext"
            />
            <Input
              placeholder="Service Person"
              :inputValue.sync="servicePersonValue"
              :isDisabled="!jobInventoryValue || viewMode"
              validator="text,200"
            />
            <InputQuery
              placeholder="Workshop"
              :selectedObject.sync="workshopValue"
              :errorMessage.sync="workshopValueError"
              :items="supplierList"
              itemKey="label"
              :tableBoxHideColumns="['label']"
              :isDisabled="!jobInventoryValue || viewMode"
            />
            <Input
              placeholder="Mileage (KM)"
              :inputValue.sync="mileageValue"
              :isRequired="true"
              :errorMessage.sync="mileageValueError"
              :isDisabled="!jobInventoryValue || viewMode"
              ref="mileage"
              validator="positiveInteger,7"
            />
            <!-- Notes for Mileage -->
            <!-- should be autofill from inventory (vehicle) -->
            <!-- this field will be saved into a real column in db -->
            <!-- validator set to 7 digits integer, but not sure if adding a new 'distance' to real validator is needed -->
            <!-- if set to real, by default it will only allow 5,2 which I think this input will easily exceed 5 whole digits -->
            <Input
              placeholder="Remark"
              :inputValue.sync="remark1Value"
              :isDisabled="!jobInventoryValue || viewMode"
              validator="text,200"
            />
            <div style="display: flex; gap: 16px; margin-left: 5px; margin-bottom: 5px">
              <DateTimePicker
                placeholder="Date/Time In"
                :value.sync="dateTimeInValue"
                :errorMessage.sync="dateTimeInValueError"
                :isDisabled="!jobInventoryValue || viewMode"
                ref="dateTimeIn"
                :isRequired="true"
              />
              <!-- defaults to today -->
              <DateTimePicker
                placeholder="Date/Time Out"
                :value.sync="dateTimeOutValue"
                :isDisabled="!jobInventoryValue || viewMode"
                ref="dateTimeOut"
              />
            </div>
            <CheckBoxV2
              title="Confirm Job"
              style="margin-left: 5px; margin-bottom: 5px"
              :isChecked.sync="confirmJobValue"
              :isDisabled="!jobInventoryValue || viewMode"
            />
            <div
              v-if="jobInventoryValue"
              style="margin-left: 5px; display: flex; gap: 5px; align-items: baseline; flex-wrap: nowrap"
            >
              <RadioButtonSet
                labelKey="label"
                :isHorizontal="true"
                :showBox="true"
                title="Inventory Availability"
                :buttons="availabilityOptions"
                style="display: flex; flex-shrink: 0"
                :selected.sync="inventoryAvailabilityValue"
                :isDisabled="!availabilityEnabled || viewMode"
              />
              <Input
                v-show="inventoryAvailabilityValue ? inventoryAvailabilityValue.keyValue == '0' : false"
                placeholder="Inventory Remark"
                :isRequired="true"
                :inputValue.sync="inventoryUnavailableRemarkValue"
                :errorMessage.sync="inventoryUnavailableRemarkValueError"
                :isDisabled="!availabilityEnabled || viewMode"
                validator="text,150"
                ref="inventoryUnavailableRemark"
              />
            </div>
          </div>
          <div class="tab-container table-tab-container" :slot="docTabTitle">
            <div class="table-container">
              <div class="cta-group" v-if="!viewMode">
                <Button @click="newDocClick" :disabled="docSelectedItem || !jobInventoryValue">Add</Button>
                <Button
                  @click="docDeleteClick"
                  :disabled="docSelectedItem || docTableSelectedRows.length < 1 || !jobInventoryValue"
                  >Delete</Button
                >
              </div>
              <TableBox
                :tableData="docTableDataFiltered"
                style="height: auto; flex-grow: 1; min-height: 0"
                :selectedRow.sync="docTableSelectedRows"
                :maxSelection="1"
                :hideColumns="['JobNo', 'Time Received', 'action', 'PicDocFile', 'PicDocType', 'seqNo']"
                :dataType="docTableDataType"
                :additionalButtons="tableAdditionalButton"
                @buttonClicked="tableRowClicked"
                title="Documents"
                :columnOrderViews.sync="docTableColumnOrderViews"
                :columnOrderID="docTableColumnOrderID"
              />
            </div>
            <div v-if="docSelectedItem">
              <div v-if="!viewMode" class="cta-group">
                <Button @click="docSaveClick">Save</Button>
                <Button @click="tabFieldsCancel">Cancel</Button>
              </div>
              <div v-if="viewMode" class="cta-group">
                <Button @click="tabFieldsCancel">Close</Button>
              </div>
              <Input
                placeholder="Reference No"
                :isRequired="true"
                :isDisabled="!jobInventoryValue || viewMode"
                :inputValue.sync="docFields.docReferenceNoValue"
                :errorMessage.sync="docReferenceNoError"
                validator="text,20"
              />
              <Dropdown
                placeholder="Doc Type"
                :isRequired="true"
                :isDisabled="!jobInventoryValue || viewMode"
                :items="docTypeList"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                :selectedObject.sync="docFields.docDocTypeValue"
                :errorMessage.sync="docDocTypeError"
              />
              <DateTimePicker
                placeholder="Doc Date"
                :isRequired="true"
                :showTime="false"
                :isDisabled="!jobInventoryValue || viewMode"
                :value.sync="docFields.docDocDateValue"
                :errorMessage.sync="docDocDateError"
              />
              <DateTimePicker
                placeholder="Date Received"
                :isRequired="true"
                :isDisabled="!jobInventoryValue || viewMode"
                :value.sync="docFields.docDateReceivedValue"
                :errorMessage.sync="docDateReceivedError"
              />
              <!-- submit as date and time seperate -->
              <Dropdown
                placeholder="Doc Code"
                :isRequired="true"
                :isDisabled="!jobInventoryValue || viewMode"
                :items="docCodeList"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                :selectedObject.sync="docFields.docDocCodeValue"
                :errorMessage.sync="docDocCodeError"
              />

              <div style="margin-top: 5px">Upload Attachment:</div>

              <div v-if="docFields.docPicDocValue" style="display: flex; gap: 10px; align-items: center">
                <Button @click="docFileDownload">Download</Button>
                <div>{{ docFields.docPicDocName }}</div>
                <Tooltip
                  class="close-icon"
                  icon="https://dsmapi.logistics.systems/dsm6gIcons/icons8-close-90.png"
                  tooltipText="Clear all selected files"
                  @click.native="docFileChanged([])"
                />
              </div>

              <Input
                placeholder="Pic / Doc"
                textType="file"
                :isDisabled="!jobInventoryValue || viewMode"
                :inputValue.sync="docFields.docPicDocName"
                ref="PicDocClear"
                @fileChanged="docFileChanged"
                v-else
              />

              <Input
                placeholder="Remark"
                :isDisabled="!jobInventoryValue || viewMode"
                :inputValue.sync="docFields.docRemarkValue"
                style="margin-top: 5px"
                validator="text,200"
              />
            </div>
          </div>
          <div class="tab-container table-tab-container" :slot="taskTabTitle">
            <div class="table-container">
              <div class="cta-group" v-if="!viewMode">
                <Button @click="newTaskClick" :disabled="taskSelectedItem || !jobInventoryValue">Add</Button>
                <Button
                  @click="taskDeleteClick"
                  :disabled="taskSelectedItem || taskTableSelectedRows.length < 1 || !jobInventoryValue"
                  >Delete</Button
                >
              </div>
              <TableBox
                :tableData="taskTableDataFiltered"
                style="height: auto; flex-grow: 1; min-height: 0"
                :maxSelection="1"
                :selectedRow.sync="taskTableSelectedRows"
                :hideColumns="['JobNo', 'Time Start', 'Time End', 'action', 'seqNo']"
                :dataType="taskTableDataType"
                :additionalButtons="tableAdditionalButton"
                @buttonClicked="tableRowClicked"
                title="Tasks"
                :columnOrderViews.sync="taskTableColumnOrderViews"
                :columnOrderID="taskTableColumnOrderID"
              />
            </div>
            <div v-if="taskSelectedItem">
              <div v-if="!viewMode" class="cta-group">
                <Button @click="taskSaveClick">Save</Button>
                <Button @click="tabFieldsCancel">Cancel</Button>
              </div>
              <div v-if="viewMode" class="cta-group">
                <Button @click="tabFieldsCancel">Close</Button>
              </div>
              <InputQuery
                placeholder="Service Code"
                :isRequired="true"
                :items="taskServicesList"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                :selectedObject.sync="taskFields.taskServiceValue"
                :errorMessage.sync="taskServiceError"
                :isDisabled="!jobInventoryValue || viewMode"
              />
              <InputQuery
                placeholder="Task"
                :isRequired="true"
                :items="taskServicesTaskList"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                :selectedObject.sync="taskFields.taskServiceTaskValue"
                :errorMessage.sync="taskServiceTaskError"
                :isDisabled="!jobInventoryValue || viewMode"
              />
              <Input
                placeholder="Service Person"
                :inputValue.sync="taskFields.taskServicePersonValue"
                :isDisabled="!jobInventoryValue || viewMode"
                validator="text,200"
              />
              <DateTimePicker
                placeholder="Date/Time Start"
                :value.sync="taskFields.taskDateTimeStartValue"
                :isDisabled="!jobInventoryValue || viewMode"
              />
              <!-- submit date and time seperately -->
              <DateTimePicker
                placeholder="Date/Time End"
                :value.sync="taskFields.taskDateTimeEndValue"
                :isDisabled="!jobInventoryValue || viewMode"
              />
              <!-- submit date and time seperately -->
              <Input
                placeholder="Remark"
                :inputValue.sync="taskFields.taskRemarkValue"
                :isDisabled="!jobInventoryValue || viewMode"
                validator="text,200"
              />
            </div>
          </div>
          <div class="tab-container table-tab-container" :slot="partTabTitle">
            <div class="table-container">
              <div class="cta-group" v-if="!viewMode">
                <div
                  v-if="
                    !isLatestTyreJob &&
                    partTableSelectedRows[0] &&
                    partTableSelectedRows[0].tyreSeqNo &&
                    !partSelectedItem
                  "
                  style="color: red"
                  >Job is not latest for {{ jobInventoryValue['Inventory #'] }}! Unable to delete part.</div
                >

                <Button @click="newPartClick" :disabled="partSelectedItem || !jobInventoryValue">Add</Button>
                <Button
                  @click="partDeleteClick"
                  :disabled="
                    partSelectedItem ||
                    partTableSelectedRows.length < 1 ||
                    !jobInventoryValue ||
                    (isLatestTyreJob == false && partTableSelectedRows[0] && partTableSelectedRows[0].tyreSeqNo)
                  "
                  >Delete</Button
                >
              </div>
              <TableBox
                :tableData="partTableDataFiltered"
                style="height: auto; flex-grow: 1; min-height: 0"
                :maxSelection="1"
                :selectedRow.sync="partTableSelectedRows"
                :hideColumns="['seqNo', 'tyreSeqNo']"
                :dataType="partTableDataType"
                :additionalButtons="tableAdditionalButton"
                @buttonClicked="tableRowClicked"
                title="Parts / Labour"
                :columnOrderViews.sync="partTableColumnOrderViews"
                :columnOrderID="partTableColumnOrderID"
              />
            </div>
            <div v-if="partSelectedItem" class="part-fields-container">
              <div v-if="!viewMode" class="cta-group">
                <Button @click="partSaveClick">Save</Button>
                <Button @click="tabFieldsCancel">Cancel</Button>
              </div>
              <div v-if="viewMode" class="cta-group">
                <Button @click="tabFieldsCancel">Close</Button>
              </div>
              <div style="display: flex; gap: 5px;">
                <InputQuery
                  placeholder="Type"
                  :isRequired="true"
                  :selectedObject.sync="partFields.partTypeValue"
                  :errorMessage.sync="partTypeError"
                  :items="partTypeListNoTyres"
                  itemKey="label"
                  :isDisabled="!jobInventoryValue || viewMode"
                  :tableBoxHideColumns="[
                    'label',
                    'Expense Code',
                    'Unique Code',
                    'IsExpenses',
                    'isExpense',
                    'Unit Price',
                    'SuppCode',
                    'Spp_Name',
                    'ProdTxnID',
                    'Active',
                  ]"
                  @selected="partTypeSelected"
                />
              </div>
              <div style="margin-top: 8px; margin-bottom: 4px">
                <DropdownSearch
                  placeholder="Warehouse Item"
                  primaryKey="label"
                  primaryLabel="Serial No (Warehouse Code)"
                  :items="warehouseItemListFiltered"
                  :selectedObject.sync="partFields.partWarehouseItemValue"
                  :search.sync="partFields.partWarehouseItemSearch"
                  :searchKeys="warehouseItemSearchKeys"
                  :inlineSecondaryFields="warehouseItemInlineSecondaryFields"
                  :secondaryFields="warehouseItemSecondaryFields"
                  :hideEmptySecondaryFields="true"
                  :showAllSecondaryFields="true"
                  :showAllResultsOnSelectedFocus="true"
                  :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                  :minimumSearchLength="1"
                  @selected="warehouseItemSelected"
                />
              </div>
              <div
                style="margin-top: 4px; margin-bottom: 8px; padding: 3px 5px; color: #664d03; background-color: #fff3cd; border: 1px solid #ffecb5; font-size: 11px; line-height: 14px"
              >
                <b>Note:</b> The previous <b>Taken From</b> and <b>Serial No</b> fields have been combined into <b>Warehouse Item</b>. For items not taken from warehouse, details can still be entered manually.
              </div>
              <div style="width: 100%">
                <Input
                  placeholder="Serial No"
                  :isRequired="serialNoRequired"
                  :inputValue.sync="partFields.partSerialNoInputValue"
                  :errorMessage.sync="partSerialNoError"
                  :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                  validator="text,25"
                  @inputChanged="partSerialNoInputChanged"
                />
              </div>
              <div class="flex-container" style="align-items: baseline">
                <div class="w-100">
                  <Input
                    placeholder="Part No"
                    :inputValue.sync="partFields.partPartNoValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Size"
                    :inputValue.sync="partFields.partSizeValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="text,25"
                  />
                  <InputQuery
                    placeholder="Warehouse"
                    :selectedObject.sync="partFields.partTakenFromValue"
                    :search.sync="partFields.partTakenFromInputValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :items="warehouseList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                    @selected="partWarehouseSelected"
                  />
                  <DateTimePicker
                    placeholder="MFG Date"
                    :value.sync="partFields.partMFGDateValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :showTime="false"
                  />
                  <DateTimePicker
                    placeholder="Date Purchase"
                    :value.sync="partFields.partDatePurchaseValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :showTime="false"
                  />
                  <Input
                    placeholder="Invoice No"
                    :inputValue.sync="partFields.partInvoiceNoValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="text,25"
                  />
                  <!-- <InputQuery
                    placeholder="Service Code"
                    :selectedObject.sync="partServiceCodeValue"
                    :isDisabled="!jobInventoryValue || viewMode || !(partFields.partTypeValue)"
                  /> -->
                </div>
                <div class="w-100">
                  <Input
                    placeholder="Replace"
                    :inputValue.sync="partFields.partReplaceValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    v-if="partTyreMode"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Model"
                    :inputValue.sync="partFields.partModelValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="text,60"
                  />
                  <InputQuery
                    placeholder="Made In"
                    :selectedObject.sync="partFields.partMadeInValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :items="countryList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                  />
                  <InputQuery
                    placeholder="Manufacturer"
                    :selectedObject.sync="partFields.partManufacturerValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :items="manufacturerList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                  />
                  <InputQuery
                    placeholder="Supplier"
                    :selectedObject.sync="partFields.partPurchaseFromValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :items="supplierList"
                    itemKey="label"
                  />
                  <Input
                    placeholder="PO No."
                    :inputValue.sync="partFields.partPONoValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Insurance Claim No."
                    :inputValue.sync="partFields.partInsuranceNoValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="text,25"
                  />
                </div>
              </div>
              <div style="display: flex; align-items: baseline; gap: 5px">
                <div style="flex: 1; min-width: 0">
                  <Input
                    placeholder="Quantity"
                    :inputValue.sync="partFields.partQuantityValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                    validator="positiveDecimal,,2"
                    :errorMessage.sync="partQuantityError"
                    @focusChanged="partQuantityFocusChanged"
                  />
                  <div
                    v-if="partQuantityMaximum !== null"
                    style="color: darkslategray; font-size: 11px; line-height: 14px; white-space: nowrap"
                    >Available quantity: {{ partQuantityMaximum }}</div
                  >
                </div>
                <div style="flex: 1; min-width: 0">
                  <Input
                    :placeholder="`Unit Price (${localCurrency})`"
                    :inputValue.sync="partFields.partUnitPriceValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="money"
                  />
                </div>
                <div style="flex: 1; min-width: 0">
                  <Input
                    :placeholder="`Total Cost (${localCurrency})`"
                    :inputValue="partTotalCostValue"
                    :isDisabled="true"
                  />
                </div>
              </div>
              <Input
                placeholder="Remark"
                :inputValue.sync="partFields.partRemarkValue"
                :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                validator="text,200"
                style="margin-top: 8px"
              />
            </div>
          </div>
          <div class="tab-container tyre-config-tab" :slot="tyreTabTitle">
            <div v-if="!isLatestTyreJob" class="tyre-history-notice">
              Only tyre changes recorded for this older job are shown. To view the current tyre configuration, create a
              new workshop job or open the Vehicle page.
            </div>
            <div class="tyre-config-tab-container">
              <BoxContainer title="Tyre Positions" class="tyre-config-wrapper">
                <TyreGrid
                  :tyreLayout="tyreConfigLayout"
                  layoutType="LeftRight"
                  :selectedTyre.sync="tyreConfigLayoutSelectedItem"
                  @tyreClicked="tyreGridClicked"
                />
                <Dropdown
                  placeholder="Selected Tyre Position"
                  :items="tyreConfigLayout"
                  :selectedObject.sync="tyrePositionSelectedValue"
                  itemKey="label"
                  @selected="tyreDropdownSelected"
                />
              </BoxContainer>
              <div class="table-wrapper">
                <div class="cta-group">
                  <Button
                    @click="tyreConfigPopupOpen"
                    :disabled="viewMode ? !tyreConfigTableSelectedRow[0] : !tyrePositionSelectedValue"
                    >{{ tyreConfigButtonText }}</Button
                  >
                  <Button
                    v-if="!viewMode && tyreChangeAllowed"
                    :disabled="tyreConfigTableSelectedRow.length < 1"
                    @click="tyreConfigDeleteClicked"
                    >Delete</Button
                  ></div
                >
                <TableBox
                  :tableData="tyreConfigUpdateListFiltered"
                  :selectedRow.sync="tyreConfigTableSelectedRow"
                  title="Tyre Changes"
                  :dataType="tyreConfigUpdateListDataType"
                  @rowSelected="tyreTableRowSelected"
                  :hideColumns="['seqno', 'tyreindex', 'action', 'otherjobno', 'Date Changed', 'rotatedwith']"
                  :columnOrderViews.sync="tyreConfigTableColumnOrderViews"
                  :columnOrderID="tyreConfigTableColumnOrderID"
                />
              </div>
            </div>
          </div>
        </Tab>
      </div>
    </PopupEntry>
    <div class="tyre-config" v-if="tyreConfigPopupShow && showPopup">
      <div class="tyre-config-inner">
        <BoxContainer :title="`Current Tyre [${tyrePopupSelectedPosition}]`" class="tyre-config-box">
          <Dropdown
            placeholder="Set Current Tyre Status"
            :items="tyreStatusOptions"
            :selectedObject.sync="tyreStatusValue"
            itemKey="label"
            :isRequired="true"
            :errorMessage.sync="tyreStatusValueError"
            :isDisabled="tyreStatusOptions.length == 1 || !tyreConfigCanEditTyreChange || tyreConfigRotateOtherDisabled"
            @selected="tyreStatusSelected"
          />
          <InputQuery
            placeholder="Send to warehouse"
            :isRequired="true"
            v-if="tyreStatusValue ? tyreStatusValue.keyValue == '5' : false"
            :items="warehouseList"
            itemKey="label"
            :tableBoxHideColumns="['label']"
            :selectedObject.sync="tyreStoreInValue"
            style="margin-top: 4px"
            :errorMessage.sync="tyreStoreInValueError"
            :isDisabled="!tyreConfigCanEditTyreChange"
          />
          <RadioButtonSet
            title="Replace Tyre With:"
            :showBox="false"
            :buttons="tyreReplaceRadio"
            :selected.sync="tyreReplaceValue"
            labelKey="label"
            style="margin: 4px"
            :isRequired="true"
            @radioChanged="tyreConfigRadioChanged"
            :errorMessage.sync="tyreReplaceValueError"
            :isDisabled="!tyreConfigCanEditTyreChange || tyreConfigRotateOtherDisabled"
          />

          <InputQuery
            placeholder="Tyre From Warehouse"
            :isRequired="true"
            v-if="tyreReplaceValue ? tyreReplaceValue.keyValue == 'WHSE' : false"
            :items="tyreNewList"
            itemKey="Serial No"
            :selectedObject.sync="tyreExistingValue"
            @selected="tyreConfigWarehouseSelected"
            :errorMessage.sync="tyreExistingValueError"
            :isDisabled="!tyreConfigCanEditTyreChange"
            :tableBoxHideColumns="[
              'ID',
              'ProdTxnID',
              'label',
              'MadeIn',
              'Model',
              'ProdDate',
              'InvoiceNo',
              'PONo',
              'AllocGUID',
              'PickQtyBal',
              'BinLoc,',
            ]"
          />
          <InputQuery
            placeholder="Select Other Vehicle"
            :isRequired="true"
            v-if="tyreReplaceValue ? tyreReplaceValue.keyValue == 'OTHER' : false"
            :selectedObject.sync="tyreConfigOtherVehicleValue"
            :items="vehicleList.filter((item) => item['Inventory #'] != jobInventoryValue['Inventory #'])"
            itemKey="label"
            :tableBoxHideColumns="['label']"
            @selected="tyreConfigOtherVehicleSelected"
            :errorMessage.sync="tyreConfigOtherVehicleValueError"
            :isDisabled="!tyreConfigCanEditTyreChange || tyreConfigRotateOtherDisabled"
          />
        </BoxContainer>
        <div class="form-container flex-container w-100">
          <div class="w-100 h-100 d-flex" v-if="tyreConfigSelectedVehicleLayout">
            <BoxContainer :title="`Tyres From: ${tyreConfigSelectedVehicleNo}`" class="w-100"
              ><TyreGrid
                :tyreLayout="tyreConfigSelectedVehicleLayout"
                layoutType="LeftRight"
                :selectedTyre.sync="tyreConfigRotateSelectedTyre"
                @tyreClicked="tyreConfigRotateClicked"
              />
              <Dropdown
                placeholder="Selected Tyre Position"
                :items="tyreConfigSelectedVehicleDropdownList"
                :selectedObject.sync="tyreConfigSelectedVehicleDropdownValue"
                itemKey="label"
                @selected="tyreConfigSelectedVehicleDropdownSelected"
                :isDisabled="!tyreConfigCanEditTyreChange || tyreConfigRotateOtherDisabled"
              />
            </BoxContainer>
          </div>
          <div class="tyre-info-container w-100 flex-container flex-column">
            <BoxContainer title="Tyre Info" class="flex-1 w-100 pb-3">
              <InputQuery
                placeholder="Tyre Type"
                :isRequired="true"
                :isDisabled="
                  (tyreConfigTyreInfoDisabled && tyreConfigTyreTypeDisabled) ||
                  !tyreConfigCanEditTyreChange ||
                  tyreConfigRotateOtherDisabled
                "
                :selectedObject.sync="tyreConfigTyreTypeValue"
                :items="tyreTypeListActive"
                itemKey="label"
                :tableBoxHideColumns="[
                  'label',
                  'Expense Code',
                  'Unique Code',
                  'IsExpenses',
                  'isExpense',
                  'Unit Price',
                  'SuppCode',
                  'Spp_Name',
                  'ProdTxnID',
                  'Active',
                ]"
                :errorMessage.sync="tyreConfigTyreTypeValueError"
                @selected="tyreTypeSelected"
              />
              <div class="flex-container">
                <div class="w-100">
                  <Input
                    placeholder="Serial No"
                    :isRequired="true"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigSerialNoValue"
                    :errorMessage.sync="tyreConfigSerialNoValueError"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Part No"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigPartNoValue"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Size"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigSizeValue"
                    validator="text,25"
                  />
                  <InputQuery
                    placeholder="Taken From"
                    :isDisabled="
                      tyreConfigTyreInfoDisabled || (tyreReplaceValue ? tyreReplaceValue.keyValue == 'NEW' : false)
                    "
                    :selectedObject.sync="tyreConfigTakenFromValue"
                    :search.sync="tyreConfigTakenFromInputValue"
                    :items="warehouseList"
                    itemKey="label"
                    :allowFreeText="takenFromFreeText"
                    :tableBoxHideColumns="['label']"
                  />
                  <Input
                    :placeholder="`Unit Price (${localCurrency})`"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigUnitPriceValue"
                    @inputChanged="tyreConfigUnitPriceOnInput"
                    validator="money"
                  />
                  <DateTimePicker
                    placeholder="MFG Date"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :value.sync="tyreConfigMfgDateValue"
                    :showTime="false"
                  />
                </div>
                <div class="w-100">
                  <Input
                    placeholder="Model"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigModelValue"
                    validator="text,60"
                  />
                  <InputQuery
                    placeholder="Made In"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :selectedObject.sync="tyreConfigMadeInValue"
                    :items="countryList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                  />
                  <InputQuery
                    placeholder="Manufacturer"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :selectedObject.sync="tyreConfigManufacturerValue"
                    :items="manufacturerList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                  />
                  <InputQuery
                    placeholder="Supplier"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :selectedObject.sync="tyreConfigSupplierValue"
                    :items="supplierList"
                    itemKey="label"
                  />
                  <Input
                    placeholder="PO No."
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigPONoValue"
                    validator="text,25"
                  />
                  <DateTimePicker
                    placeholder="Date Purchase"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :value.sync="tyreConfigDatePurchaseValue"
                    :showTime="false"
                  />
                </div>
              </div>
            </BoxContainer>
            <BoxContainer title="Job Part Info" class="w-100 flex-1">
              <Input
                placeholder="Previous Tyre Serial No"
                :inputValue.sync="tyreConfigOldSerialNoValue"
                :isDisabled="true"
              />
              <Input
                placeholder="Price"
                :inputValue.sync="tyreConfigJobPriceValue"
                :isDisabled="viewMode || (tyreReplaceValue ? tyreReplaceValue.keyValue == 'NEW' : false)"
                validator="money"
              />
              <Input
                placeholder="Invoice No."
                :inputValue.sync="tyreConfigInvoiceNoValue"
                :isDisabled="viewMode"
                validator="text,25"
              />
              <Input
                placeholder="Insurance Claim No."
                :inputValue.sync="tyreConfigInsuranceNoValue"
                :isDisabled="viewMode"
                validator="text,25"
              />
              <Input
                placeholder="Remarks"
                :inputValue.sync="tyreConfigRemarksValue"
                :isDisabled="viewMode"
                validator="text,200"
              />
            </BoxContainer>
            <BoxContainer
              title="Link To Existing Job?"
              class="w-100"
              v-if="tyreReplaceValue ? tyreReplaceValue.keyValue == 'OTHER' : false"
            >
              <Dropdown
                placeholder="Workshop Job No."
                :items="tyreRotateOtherVehicleWorkshopJobList"
                :selectedObject.sync="tyreRotateOtherVehicleWorkshopJobValue"
                :search.sync="tyreRotateOtherVehicleWorkshopJobInputValue"
                :errorMessage.sync="tyreRotateOtherVehicleWorkshopJobValueError"
                itemKey="label"
                :isDisabled="!tyreConfigSelectedVehicleLayout || disableAttachJob"
                :isRequired="true"
              />
            </BoxContainer>
          </div>
        </div>
        <div class="cta-group">
          <div class="error-text" v-if="tyreConfigErrorText">{{ tyreConfigErrorText }}</div>
          <div class="error-text" v-if="tyreConfigEditWarningText">{{ tyreConfigEditWarningText }}</div>
          <div class="error-text" v-if="tyreConfigRotateOtherDisabled"
            >Edits to tyre changes are not allowed for saved changes with other vehicles. Only Job Part Info is allowed
            for editing.</div
          >
          <div class="error-text" v-if="tyreConfigRotateOtherWorkshopJobIsNotLatest"
            >Changes exists for non latest job. Pleae choose another vehicle.</div
          >
          <Button v-if="tyreConfigCanSavePopup" @click="tyreConfigPopupSave">Confirm</Button>
          <Button @click="tyreConfigPopupShow = false">{{ viewMode || !tyreConfigCanSavePopup ? 'Close' : 'Cancel' }}</Button>
        </div>
      </div>
    </div>
    <UpdateTyreStatus :popupOpen.sync="updateTyrePopupOpen" />
  </div>
</template>

<script>
import TyreGrid from '@/components/TyreGrid.vue';
import UpdateTyreStatus from './UpdateTyreStatus.vue';

const cleanText = (value) => (typeof value == 'string' ? value.trim() : value);

export default {
  components: {
    TyreGrid,
    UpdateTyreStatus,
  },
  data() {
    const activeEndDate = new Date();
    const activeStartDate = new Date(activeEndDate);
    activeStartDate.setDate(activeStartDate.getDate() - 14);

    return {
      filterOpen: false,
      dateFilterActive: true,
      jobNoFilterActive: false,
      filterActiveMode: 'Date',

      defaultWeeksToShow: 2,

      activeStartDate,
      activeEndDate,
      filterStartDate: new Date(activeStartDate),
      filterEndDate: new Date(activeEndDate),

      activeStartJobNo: '',
      activeEndJobNo: '',
      filterStartJobNo: null,
      filterEndJobNo: null,
      filterStartJobNoError: '',
      filterEndJobNoError: '',

      EMSJobNoList: [],

      //workshop table items
      workshopJobsTableData: [],
      workshopJobsTableSelectedRows: [],
      workshopJobsTableDataType: {
        '#': 'number',
        'Job #': 'string',
        'Job Date': 'datetime',
        'Inventory #': 'string',
        InventoryNo: 'string',
        'Repair Type': 'string',
        'Internal / External': 'string',
        Confirmed: 'string',
        'Tyre Change': 'string',
        'Requested By': 'string',
        Workshop: 'string',
        'Service Person': 'string',
        'Mileage (KM)': 'number',
        Remark: 'string',
        'Date / Time In': 'datetime',
        'Date / Time Out': 'datetime',
        'Trailer #': 'string',
        'Last Update Time': 'datetime',
        'Last Update UserID': 'string',
        'Create Time': 'datetime',
        'Create UserID': 'string',
        IsLatest: 'string',
        canDelete: 'number',
      },
      workshopJobsTableLoading: true,
      workshopJobsTableColumnOrderViews: [],
      workshopJobsTableColumnOrderID: null,
      docTableColumnOrderViews: [],
      docTableColumnOrderID: null,
      taskTableColumnOrderViews: [],
      taskTableColumnOrderID: null,
      partTableColumnOrderViews: [],
      partTableColumnOrderID: null,
      tyreConfigTableColumnOrderViews: [],
      tyreConfigTableColumnOrderID: null,

      //popupstuff
      showPopup: false,
      selectedTabIndex: 0,
      viewMode: false,
      menuMode: '',
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryErrorMessages: [],

      selectedVehicleInfo: [],

      jobFetched: null,

      //general details
      jobNoValue: '',
      jobNoValueError: '',
      jobDateValue: new Date(), //defaults to today
      jobDateValueError: '',
      jobInventoryValue: null,
      jobInventoryValueError: null,

      createdInfo: '',
      lastUpdatedInfo: '',

      //#region Job Details Tab
      trailerNoValue: null,
      requestedByValue: null,
      repairTypeValue: null,
      repairTypeValueError: '',
      internalExternalValue: null,
      internalExternalValueError: '',
      servicePersonValue: '',
      workshopValue: null,
      workshopValueError: '',
      mileageValue: '',
      mileageValueError: '',
      remark1Value: '',
      dateTimeInValue: null,
      dateTimeInValueError: '',
      dateTimeOutValue: null,
      confirmJobValue: false,
      inventoryAvailabilityValue: null,
      inventoryUnavailableRemarkValue: '',
      inventoryUnavailableRemarkValueError: '',

      isLatestJob: true,
      isLatestTyreJob: true,

      ////trailer list in computed
      vehicleList: [],
      employeeList: [],
      supplierList: [],

      intExtOptions: [
        { label: 'Internal', keyValue: '0' },
        { label: 'External', keyValue: '1' },
      ],
      availabilityOptions: [
        { label: 'Available', keyValue: '1' },
        { label: 'Unavailable', keyValue: '0' },
      ],

      repairTypeOptions: [
        { label: 'Preventitive', keyValue: 'P' },
        { label: 'Repair', keyValue: 'R' },
      ],

      //#endregion

      //#region Document Tab
      docTableData: [],
      docTableSelectedRows: [],
      docTableDataType: {
        '#': 'number',
        'Ref No': 'string',
        Type: 'string',
        Code: 'string',
        Date: 'datetime',
        'Date Received': 'datetime',
        'Pic/Doc': 'string',
        Remark: 'string',
      },
      docSelectedItem: null,

      docFields: {
        docReferenceNoValue: '',
        docDocTypeValue: null,
        docDocDateValue: null,
        docDateReceivedValue: null,
        docDocCodeValue: null,
        docPicDocValue: null,
        docPicDocType: '',
        docPicDocName: '',
        docRemarkValue: '',
      },
      docFieldsChangedCheck: {},

      docReferenceNoError: '',
      docDocTypeError: '',
      docDocDateError: '',
      docDateReceivedError: '',
      docDocCodeError: '',

      docTypeList: [],
      docCodeList: [],
      //#endregion

      //#region Task Tab
      taskTableData: [],
      taskTableSelectedRows: [],
      taskTableDataType: {
        '#': 'number',
        'Service Code': 'string',
        'Service Description': 'string',
        'Task Code': 'string',
        'Task Description': 'string',
        'Service Person': 'string',
        'Date Start': 'datetime',
        'Date End': 'datetime',
        Remark: 'string',
      },
      taskSelectedItem: null,

      taskFields: {
        taskServiceValue: null,
        taskServiceTaskValue: null,
        taskServicePersonValue: '',
        taskDateTimeStartValue: null,
        taskDateTimeEndValue: null,
        taskRemarkValue: '',
      },
      taskFieldsChangedCheck: {},

      taskServiceError: '',
      taskServiceTaskError: '',
      taskServicesList: [],
      taskServicesTaskList: [],

      //#endregion

      //#region Part Tab
      partTableData: [],
      partTableSelectedRows: [],
      partTableDataType: {
        '#': 'number',
        Type: 'string',
        'Serial No': 'string',
        Manufacturer: 'string',
        'Made In': 'string',
        Size: 'string',
        Quantity: 'number',
        'Unit Price': 'currency',
        'Cost (RM)': 'currency',
        Replace: 'string',
        Supplier: 'string',
        'Date Purchase': 'datetime',
        'Insurance Claim No': 'string',
        'Invoice No': 'string',
        'Taken From': 'string',
        Remark: 'string',
        PONo: 'string',
        UserID: 'string',
        'Date Updated': 'datetime',
      },
      partSelectedItem: null,

      partFields: {
        partTypeValue: null,
        partWarehouseItemValue: null,
        partWarehouseItemSavedValue: null,
        partWarehouseItemSearch: '',
        partSerialNoValue: null,
        partSerialNoInputValue: '',
        partPartNoValue: '',
        partSizeValue: '',
        partTakenFromValue: null,
        partTakenFromInputValue: '',
        partUnitPriceValue: '',
        partMFGDateValue: null,
        partDatePurchaseValue: null,
        partInvoiceNoValue: '',
        // partServiceCodeValue: null,
        partModelValue: '',
        partReplaceValue: '',
        partMadeInValue: null,
        partQuantityValue: '1',
        partManufacturerValue: null,
        partPurchaseFromValue: null,
        partPONoValue: '',
        partInsuranceNoValue: '',
        partRemarkValue: '',
      },
      partFieldsChangedCheck: {},

      partTypeError: '',
      partSerialNoError: '',
      partQuantityError: '',

      partTypeList: [],
      serialNoList: [],
      takenFromList: [],
      countryList: [],
      manufacturerList: [],
      ////supplier list declared in jobdetails

      serialNoListDataType: {
        'Serial No': 'string',
        Warehouse: 'string',
        'Qty Bal': 'number',
        Model: 'string',
        ID: 'string',
        'Part Desc': 'string',
        'Part Type': 'string',
        MadeIn: 'string',
        Manufacturer: 'string',
        ProdDate: 'datetime',
        PurcDate: 'datetime',
        NoOfRetread: 'number',
        PONo: 'string',
        'Part No': 'string',
        'Unit Price': 'currency',
        Spp_Code: 'string',
      },

      //#endregion

      //#region Tyre Tab
      tyreConfigLayoutSelectedItem: null,
      tyrePositionSelectedValue: null,

      tyreConfigTyresList: [],
      tyreConfigTableSelectedRow: [],
      tyreLayout: [],

      //#endregion

      //#region Tyre Config POPUP
      tyreConfigPopupShow: false,

      tyreConfigSelectedVehichleTyreConfig: [],
      tyreConfigSelectedVehichleTyreConfigFull: [],
      tyreConfigSelectedVehicleLayout: null,
      tyreConfigSelectedVehicleNo: '',
      tyreConfigRotateSelectedTyre: null,

      tyreConfigSelectedVehicleDropdownValue: null,

      tyreConfigUpdateList: [],

      tyreConfigUpdateListDataType: {
        'New Serial No': 'string',
        'Old Serial No': 'string',
        'Tyre Position': 'string',
        'Replace Type': 'string',
        'Rotated With': 'string',
        'Store In': 'string',
        'Prev Tyre Status': 'string',
        Remarks: 'string',
      },

      tyreStatusList: [
        { label: 'Unavailable', keyValue: '0' },
        { label: 'Rotate', keyValue: '0' },
        { label: 'Send to warehouse', keyValue: '5' },
        { label: 'Send for retread', keyValue: '3' },
        { label: 'Discard', keyValue: '4' },
      ],

      tyreStatusValue: null,
      tyreStatusValueError: '',
      tyreStoreInValue: null,
      tyreStoreInValueError: '',

      tyreReplaceValue: null,
      tyreReplaceValueError: '',

      tyreExistingValue: null,
      tyreExistingValueError: '',

      tyreConfigOtherVehicleValue: null,
      tyreConfigOtherVehicleValueError: '',

      tyreConfigTyreTypeDisabled: false,

      tyreConfigTyreTypeValue: null,
      tyreConfigSerialNoValue: '',
      tyreConfigPartNoValue: '',
      tyreConfigSizeValue: '',
      tyreConfigTakenFromValue: null,
      tyreConfigTakenFromInputValue: '',
      tyreConfigUnitPriceValue: '',
      tyreConfigMfgDateValue: null,
      tyreConfigModelValue: '',
      tyreConfigMadeInValue: null,
      tyreConfigManufacturerValue: null,
      tyreConfigSupplierValue: null,
      tyreConfigPONoValue: null,
      tyreConfigDatePurchaseValue: null,

      tyreConfigTyreTypeValueError: '',
      tyreConfigSerialNoValueError: '',

      tyreConfigOldSerialNoValue: '',
      tyreConfigJobPriceValue: '',
      tyreConfigInvoiceNoValue: null,
      tyreConfigInsuranceNoValue: null,
      tyreConfigRemarksValue: null,

      tyreUpdateMode: '',

      warehouseList: [],
      jobPartTyreList: [],
      jobPartTyreSaveList: [],

      tyreConfigErrorText: '',
      tyreConfigPositionEditAllowed: false,
      tyreConfigPositionEditChecked: false,
      tyreConfigPositionEditMessage: '',
      //#endregion

      tyreRotateOtherVehicleWorkshopJobValue: null,
      tyreRotateOtherVehicleWorkshopJobInputValue: '',
      tyreRotateOtherVehicleWorkshopJobValueError: '',
      tyreRotateOtherVehicleWorkshopJobs: null,
      tyreConfigUpdateOtherVehicle: [],

      partUpdateOtherVehicle: [],

      disableAttachJob: false,

      updateTyrePopupOpen: false,

      //other stuff
      tabFieldsAction: '',
      SASControl: [],
      localCurrency: 'MYR',
    };
  },

  computed: {
    menu() {
      const menuEnabled = !this.workshopJobsTableLoading;
      let menuItems = [
        {
          title: 'New Workshop Job',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'New',
          visible: true,
          enabled: menuEnabled,
          control: 'mnNew',
        },
        {
          title: 'Edit Workshop Job',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'Edit',
          visible: true,
          enabled: menuEnabled,
          control: 'mnEdit',
        },
        {
          title: 'Delete Workshop Job',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'Delete',
          visible: true,
          enabled: menuEnabled,
          control: 'mnDelete',
        },
        {
          title: 'View Workshop Job',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'View',
          visible: true,
          enabled: menuEnabled,
          control: 'mnQuery',
        },
        {
          title: 'Print Work Order',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          key: 'Print',
          visible: true,
          enabled: menuEnabled,
          control: 'printWorkOrder',
        },
        {
          title: 'Update Retread Tyres',
          icon: 'https://dsmlib.logistics.systems/Icons/Reverse.png',
          key: 'UpdateRetreadTyres',
          visible: true,
          enabled: menuEnabled,
          control: 'mnUpdateTyreStatus',
        },
      ];

      menuItems = menuItems.map((item) => {
        const { control } = item;
        const SAS = this.SASControl.find((sas) => sas.Control == control) || null;
        if (!SAS) return item;
        const { Properties, PropertiesValue } = SAS;
        return {
          ...item,
          [Properties.toLowerCase()]: PropertiesValue == 'True',
        };
      });

      return menuItems;
    },
    activeDateString() {
      if (this.filterActiveMode == 'Date')
        return `Date: ${new Date(this.activeStartDate).formatString('dd-MM-yyyy')} to ${new Date(this.activeEndDate).formatString('dd-MM-yyyy')}`;
      else if (this.filterActiveMode == 'JobNo') {
        return `Job No: ${this.activeStartJobNo} to ${this.activeEndJobNo}`;
      }
      return 'No Filter Active';
    },

    tabItems() {
      return [
        { title: 'Job Details' },
        { title: this.docTabTitle, disabled: !this.jobInventoryValue },
        { title: this.taskTabTitle, disabled: !this.jobInventoryValue },
        { title: this.partTabTitle, disabled: !this.jobInventoryValue },
        { title: this.tyreTabTitle, disabled: !this.jobInventoryValue },
      ];
    },
    docTabTitle() {
      return `Documents${this.docTableDataFiltered.length > 0 ? ` (${this.docTableDataFiltered.length})` : ''}`;
    },
    taskTabTitle() {
      return `Tasks${this.taskTableDataFiltered.length > 0 ? ` (${this.taskTableDataFiltered.length})` : ''}`;
    },
    partTabTitle() {
      return `Parts / Labour${this.partTableDataFiltered.length > 0 ? ` (${this.partTableDataFiltered.length})` : ''}`;
    },
    tyreTabTitle() {
      const length = this.tyreConfigUpdateListFiltered?.length ?? 0;
      return `Tyres${length > 0 ? ` (${length})` : ''}`;
    },
    popUpTitle() {
      return `${this.menuMode} Workshop Job ${this.menuMode != 'New' && this.jobNoValue ? `- ${this.jobNoValue}` : ''}`;
    },
    availabilityEnabled() {
      return this.isLatestJob;
    },
    trailerList() {
      return this.vehicleList.filter((item) => item.Category == 'TRAILER');
    },
    serialNoListFiltered() {
      const { partTypeValue } = this.partFields || {};
      if (!partTypeValue) return [];

      return this.serialNoList?.filter(
        (item) =>
          (item.PartType == partTypeValue['Part Type'] &&
            !this.partTableDataFiltered.some((part) => part['Serial No'] == item['Serial No'])) ||
          item['Serial No'] == this.partSelectedItem?.['Serial No'],
      );
    },
    serialNoRequired() {
      const { 'Unique Code': uniqueCode } = this.partFields.partTypeValue || {};
      return uniqueCode == 'Y';
    },
    takenFromFreeText() {
      const { partTakenFromInputValue: partTakenFromInputValueSaved } = this.partFieldsChangedCheck || {};
      const { partTakenFromInputValue, partSerialNoValue } = this.partFields || {};
      const { Warehouse } = partSerialNoValue || {};
      const fromPopulate = partTakenFromInputValue == partTakenFromInputValueSaved;
      const fromSerial = Warehouse && Warehouse == partTakenFromInputValue;
      return fromPopulate || fromSerial;
      //enable free text to prevent 'Taken From' from locking out other actions
      //enable free text if taken from is gottne from Serial No
    },
    takenFromListFiltered() {
      const { partTypeValue } = this.partFields || {};
      const { ProdTxnID: PartFieldsProdTxnID, 'Part Type': PartsFieldsPartType } = partTypeValue || {};
      if (!PartFieldsProdTxnID && !PartsFieldsPartType) return [];
      const selectedWarehouseItems = [
        this.partFields.partWarehouseItemValue,
        this.partFields.partWarehouseItemSavedValue,
      ].filter(Boolean);
      const list = this.takenFromList.filter((item) => {
        const validateTxn = Boolean(PartFieldsProdTxnID) && item.ProdTxnID == PartFieldsProdTxnID;
        const validatePartType = Boolean(PartsFieldsPartType) && item['Part Type'] == PartsFieldsPartType;
        const validateType = validateTxn || validatePartType;
        const isSelectedWarehouseItem = selectedWarehouseItems.some((part) =>
          this.warehouseItemMatchesPart(item, part),
        );
        return isSelectedWarehouseItem || (validateType && item['Quantity Balance'] > 0);
      });
      return list;
    },
    partWarehouseItemSelected() {
      return Boolean(this.partFields.partWarehouseItemValue);
    },
    partQuantityMaximum() {
      const selectedItem = this.partFields.partWarehouseItemValue;
      if (
        !selectedItem ||
        selectedItem['Quantity Balance'] === null ||
        selectedItem['Quantity Balance'] === undefined ||
        selectedItem['Quantity Balance'] === ''
      ) {
        return null;
      }
      return this.warehouseItemEffectiveQuantityBalance(selectedItem);
    },
    serialNoLookup() {
      const lookup = Object.create(null);
      this.serialNoList.forEach((item) => {
        const serialNo = String(item?.['Serial No'] ?? '');
        if (serialNo && !lookup[serialNo]) lookup[serialNo] = item;
      });
      return lookup;
    },
    warehouseItemListFiltered() {
      const list = this.mergeWarehouseItemRows(this.takenFromListFiltered.map((item) => {
        const serialItem = this.serialNoLookup[String(item['Serial No'] ?? '')] || {};
        const unitPrice = item['Unit Price'] ?? serialItem.UnitPrice;
        const purchaseDate = item['Date In'] || serialItem.PurcDate || '';
        return {
          ...item,
          warehouseItemRawQuantityBalance: item['Quantity Balance'],
          'Quantity Balance': item['Quantity Balance'],
          'Unit Price': unitPrice === null || unitPrice === undefined || unitPrice === '' ? '' : this.$currencyFormat(this.parsePartAmount(unitPrice)),
          Manufacturer: item.Manufacturer || serialItem.Manufacturer || '',
          Model: item['Model No'] || serialItem.Model || '',
          'Date Purchase': purchaseDate ? new Date(purchaseDate).formatString('yyyy-MM-dd') : '',
          'Manufacture Date': item.ProdDate || serialItem.ProdDate || '',
          label: this.warehouseItemPrimaryLabel(item),
        };
      })).map((item) => ({
        ...item,
        'Quantity Balance': this.warehouseItemEffectiveQuantityBalance(item),
      }));
      const savedPart = this.partFields.partWarehouseItemSavedValue;
      if (savedPart && !list.some((item) => this.warehouseItemMatchesPart(item, savedPart))) {
        const savedWarehouseItem = this.warehouseItemFromSavedPart(savedPart);
        savedWarehouseItem['Quantity Balance'] = this.warehouseItemEffectiveQuantityBalance(savedWarehouseItem);
        list.push(savedWarehouseItem);
      }
      if (this.partWarehouseItemSelected) return list;

      const warehouse = String(
        this.partFields.partTakenFromValue?.Warehouse ||
          this.partFields.partTakenFromValue?.['Warehouse Code'] ||
          this.partFields.partTakenFromInputValue ||
          '',
      ).trim();
      const serialNo = String(this.partFields.partSerialNoInputValue || '').trim();
      if (!warehouse && !serialNo) return list;

      return list.filter((item) => {
        const warehouseMatches = !warehouse || String(item.Warehouse ?? '').trim() == warehouse;
        const serialMatches = !serialNo || String(item['Serial No'] ?? '').trim() == serialNo;
        return warehouseMatches && serialMatches;
      });
    },
    warehouseItemSearchKeys() {
      return [
        'Warehouse',
        'Serial No',
        'Part No',
        'Model',
        'Model No',
        'Manufacturer',
        'MadeIn',
        'Supplier',
        'Unit Price',
        'Quantity Balance',
        'PONo',
        'InvoiceNo',
        'BinLoc',
        'Date In',
        'ProdDate',
        'Date Purchase',
        'Manufacture Date',
      ];
    },
    warehouseItemSecondaryFields() {
      return [
        { key: 'Date Purchase', label: 'Date Purchase' },
        { key: 'Part No', label: 'Part No' },
        { key: 'Manufacture Date', label: 'Manufacture Date' },
        { key: 'InvoiceNo', label: 'Invoice No' },
        { key: 'Model', label: 'Model' },
        { key: 'MadeIn', label: 'Made In' },
        { key: 'Manufacturer', label: 'Manufacturer' },
        { key: 'Supplier', label: 'Supplier' },
        { key: 'PONo', label: 'PO No.' },
        { key: 'BinLoc', label: 'Bin Location' },
      ];
    },
    warehouseItemInlineSecondaryFields() {
      return [
        { key: 'Unit Price', label: 'Unit Price' },
        { key: 'Quantity Balance', label: 'Quantity' },
      ];
    },
    tableAdditionalButton() {
      if (this.viewMode) return [{ text: 'View', index: 0, width: 60 }];
      return [{ text: 'Edit', index: 0, width: 60 }];
    },
    partTypeListNoTyres() {
      const filterTyres = this.partTypeList.filter((item) => {
        return item.Active == '1' && !item?.['Part Group']?.toLowerCase().includes('tyre');
      });

      const selectedType = this.partFields.partTypeValue;
      if (
        selectedType?.['Part Group']?.toLowerCase().includes('tyre') &&
        !filterTyres.some((item) => item['Part Type'] == selectedType['Part Type'])
      ) {
        return [selectedType, ...filterTyres];
      }

      return filterTyres;
    },
    tyreTypeList() {
      const filterTyres = this.partTypeList.filter((item) => {
        return item?.['Part Group']?.toLowerCase().includes('tyre');
      });

      return filterTyres;
    },
    tyreTypeListActive() {
      return this.tyreTypeList.filter((item) => item.Active == '1');
    },

    partTyreMode() {
      return this.partFields.partTypeValue?.['Part Group']?.toLowerCase().includes('tyre');
    },
    partTotalCostValue() {
      const quantity = this.parsePartAmount(this.partFields.partQuantityValue) || 1;
      const unitPrice = this.parsePartAmount(this.partFields.partUnitPriceValue);
      return this.$currencyFormat(unitPrice * quantity);
    },
    docTableDataFiltered() {
      const table = this.reorderTableNo(this.docTableData.filter((doc) => doc.action != 'D'));
      return table;
    },
    taskTableDataFiltered() {
      const table = this.reorderTableNo(this.taskTableData.filter((task) => task.action != 'D'));
      return table;
    },
    partTableDataFiltered() {
      const tableLimitedColumns = this.partTableData
        .filter((part) => part.action != 'D')
        .map((part) => {
          const {
            Type,
            'Serial No': SerialNo,
            'Part No': PartNo,
            Manufacturer,
            'Made In': MadeIn,
            Size,
            Quantity,
            'Cost (RM)': Cost,
            Replace,
            'Purchase From': PurchaseFrom,
            'Insurance Claim No': InsNo,
            'Invoice No': InvNo,
            'Taken From': TakenFrom,
            Remark,
            PONo,
            UserID,
            'Date Updated': DateUpdated,
            seqNo,
          } = part || {};

          const update = this.tyreConfigUpdateList.find(
            (tyre) =>
              cleanText(tyre.serialno) == cleanText(part['Serial No']) &&
              cleanText(tyre.tyreindex) == cleanText(part.TyreIndex),
          );
          const partType = this.partTypeList.find((item) => item['Part Type'] == Type) || {};
          const { Description: PartDesc } = partType;
          const quantityValue = this.parsePartAmount(Quantity) || 1;
          const totalCostValue = this.parsePartAmount(Cost);
          const unitPriceValue = totalCostValue / quantityValue;

          return {
            Type: `${Type}${PartDesc ? ` - ${PartDesc}` : ''}`,
            'Serial No': SerialNo,
            'Part No': PartNo,
            Manufacturer,
            'Made In': MadeIn,
            Size,
            Quantity: quantityValue,
            'Unit Price': unitPriceValue,
            'Cost (RM)': totalCostValue,
            Replace,
            Supplier: PurchaseFrom,
            'Insurance Claim No': InsNo,
            'Invoice No': InvNo,
            'Taken From': TakenFrom,
            Remark,
            PONo,
            UserID,
            'Date Updated': DateUpdated,
            seqNo,
            tyreSeqNo: update ? update.seqno : null,
          };
        })
        .filter(Boolean);
      return this.reorderTableNo(tableLimitedColumns);
    },

    fieldsChanged() {
      if (this.viewMode) return false;
      let current = {};
      let saved = {};
      if (this.docSelectedItem) {
        current = this.docFields;
        saved = this.docFieldsChangedCheck;
      }
      if (this.taskSelectedItem) {
        current = this.taskFields;
        saved = this.taskFieldsChangedCheck;
      }
      if (this.partSelectedItem) {
        current = this.partFields;
        saved = this.partFieldsChangedCheck;
      }
      //the order doesnt really matter
      current = JSON.stringify(current);
      saved = JSON.stringify(saved);

      return current != saved;
    },

    tyreConfigButtonText() {
      if (this.tyreConfigTableSelectedRow.length > 0)
        return this.tyreChangeAllowed ? 'Edit Tyre Change' : 'View Tyre Change';
      if (this.viewMode || !this.tyreChangeAllowed) return 'View Tyre Change';
      return 'New Tyre Change';
    },

    tyreChangeAllowed() {
      return this.isLatestTyreJob;
    },

    tyreConfigCanEditJobPartInfo() {
      return !this.viewMode && this.tyreConfigTableSelectedRow.length > 0;
    },

    tyreConfigSelectedSavedTyreChange() {
      const selected = this.tyreConfigTableSelectedRow[0];
      return selected && selected.action != 'N';
    },

    tyreConfigCanEditTyreChange() {
      if (this.viewMode) return false;
      if (this.tyreConfigSelectedSavedTyreChange)
        return this.tyreChangeAllowed && this.tyreConfigPositionEditAllowed;
      return this.tyreChangeAllowed;
    },

    tyreConfigCanSavePopup() {
      return this.tyreConfigCanEditTyreChange || this.tyreConfigCanEditJobPartInfo;
    },

    tyreConfigPartInfoOnlyEdit() {
      return this.tyreConfigCanEditJobPartInfo && !this.tyreConfigCanEditTyreChange;
    },

    tyreConfigEditWarningText() {
      if (!this.tyreConfigPartInfoOnlyEdit) return '';
      if (this.tyreConfigPositionEditMessage) return this.tyreConfigPositionEditMessage;
      return 'Tyre change cannot be edited because future changes may exist for this position. Only Job Part Info is allowed for editing.';
    },

    tyreStatusOptions() {
      if (this.tyreConfigOldSerialNoValue) return this.tyreStatusList;
      return [{ label: 'None', keyValue: '0' }];
    },

    tyrePopupSelectedPosition() {
      if (this.tyreConfigTableSelectedRow[0]) {
        return `${this.tyreConfigTableSelectedRow[0]['Tyre Position']}${this.tyreConfigTableSelectedRow[0]['Old Serial No'] ? ` - ${this.tyreConfigTableSelectedRow[0]['Old Serial No']}` : ''}`;
      }
      if (this.tyrePositionSelectedValue) return this.tyrePositionSelectedValue.label;
      return '';
    },

    tyreNewList() {
      const { 'New Serial No': serialno } = this.tyreConfigTableSelectedRow?.[0] || {};
      return this.jobPartTyreList.filter((item) => {
        return (
          (!this.tyreConfigUpdateListFiltered.some((tyre) => tyre['New Serial No'] == item['Serial No']) &&
            item.tyreindex != this.tyreConfigLayoutSelectedItem?.index &&
            item['Qty Bal'] > 0) ||
          (item['Serial No'] == serialno && this.tyreReplaceValue?.keyValue == 'WHSE')
        );
      });
    },

    tyreConfigLayout() {
      let returnLayout = [...this.tyreLayout];
      const canViewCurrentTyres = this.tyreChangeAllowed;

      // 1) Check latest job usage
      returnLayout = returnLayout.map((layout) => {
        const found =
          canViewCurrentTyres ?
            this.tyreConfigTyresList.findLast(
              (item) =>
                layout.index == item.tyreindex && (item.inuse == '1' || item.inuse == '2') && item.action != 'D',
            )
          : null;

        return {
          ...layout,
          state: found ? 'saved' : 'normal',
          tyreInfo: found,
        };
      });

      // Helper to avoid repeating findLast logic
      const findUpdateForIndex = (index, checkFn) =>
        this.tyreConfigUpdateList?.findLast((u) => u.tyreindex == index && checkFn(u));

      // 2) Check reverted previous update
      returnLayout = returnLayout.map((tyre) => {
        const found = findUpdateForIndex(tyre.index, (u) => u.prevupdate);
        return found ? { ...tyre, state: 'saved', tyreInfo: found } : tyre;
      });

      // 3) Check revert flag
      returnLayout = returnLayout.map((tyre) => {
        const found = findUpdateForIndex(tyre.index, (u) => u.revert);
        return found ? { ...tyre, state: 'saved', tyreInfo: found } : tyre;
      });

      // 4) Check normal update (no revert, no prevupdate)
      returnLayout = returnLayout.map((tyre) => {
        const found = findUpdateForIndex(tyre.index, (u) => !u.revert && !u.prevupdate && u.action != 'D');

        if (!found) return tyre;

        const prevTyre = this.tyreConfigTyresList.findLast((item) => item.serialno == found.oldserialno);

        return {
          ...tyre,
          state: 'changes',
          tyreInfo: prevTyre ? { ...prevTyre, inuse: '1' } : tyre.tyreInfo,
        };
      });

      // 5) Add label
      return returnLayout.map((item) => ({
        ...item,
        label: `${item.description}${item.tyreInfo?.serialno ? ` - ${item.tyreInfo.serialno}` : ''}`,
      }));
    },

    tyreConfigSelectedVehicleDropdownList() {
      const { seqno } = this.tyreConfigTableSelectedRow?.[0] || {};
      return this.tyreConfigSelectedVehicleLayout?.filter(
        (item) => item.state == 'saved' && item.tyreInfo?.seqno != seqno,
      );
    },

    tyreConfigUpdateListFiltered() {
      const newItem = this.tyreConfigUpdateList.filter(
        (tyreConfig) =>
          (!this.tyreConfigTyresList.some((tyre) => tyre.seqno == tyreConfig.seqno) ||
            cleanText(tyreConfig.jobno) == cleanText(this.jobNoValue)) &&
          tyreConfig.action != 'D' &&
          !tyreConfig.revert,
      );

      const isRotate = (item) => cleanText(item?.type) == 'ROTATE';

      // Check for mirrored ROTATE
      let filteredList = newItem.map((item) => {
        if (isRotate(item)) {
          const isSecondOfPair = newItem.some(
            (other) =>
              other != item &&
              isRotate(other) &&
              cleanText(other.serialno) == cleanText(item.oldserialno) &&
              cleanText(other.oldserialno) == cleanText(item.serialno) &&
              Number(other.seqno) < Number(item.seqno),
          );
          // skip if this is the second item (i.e., found a mirrored ROTATE)
          if (isSecondOfPair) {
            return {
              ...item,
              isSecondPair: true,
            };
          }
        }
        return item;
      });
      // Add rotatedWithPosition
      filteredList = filteredList.map((item) => {
        if (isRotate(item)) {
          const findPair = filteredList.find(
            (pair) =>
              isRotate(pair) &&
              pair.isSecondPair &&
              cleanText(pair.serialno) == cleanText(item.oldserialno) &&
              cleanText(pair.oldserialno) == cleanText(item.serialno) &&
              Number(pair.seqno) > Number(item.seqno),
          );
          if (findPair) {
            return {
              ...item,
              rotatedWithPosition: findPair.tyreposition,
            };
          }
        }
        return item;
      });
      // Remove mirrored ROTATE
      filteredList = filteredList.filter((item) => !item.isSecondPair);
      // Add storein
      filteredList = filteredList.map((item) => {
        if (item.storein) return item;
        const findPrev = this.tyreConfigTyresList?.findLast(
          (tyre) =>
            cleanText(tyre.serialno) == cleanText(item.oldserialno) &&
            cleanText(tyre.tyreindex) == cleanText(item.tyreindex) &&
            tyre.seqno <= item.seqno,
        );
        const { whsecode, inuse } = findPrev || {};
        if (inuse == '5') {
          return {
            ...item,
            storein: whsecode,
          };
        }
        return item;
      });

      filteredList = filteredList.map((item) => {
        let prevStatus = '';
        const type = cleanText(item.type);
        if (!item.oldserialno) prevStatus = 'None';
        else if (type == 'ROTATE') prevStatus = 'Rotated';
        else if (type == 'OTHER') prevStatus = 'Rotated (Other Vehicle)';
        else {
          const findPrev = this.tyreConfigTyresList?.findLast(
            (tyre) =>
              cleanText(tyre.serialno) == cleanText(item.oldserialno) &&
              cleanText(tyre.tyreindex) == cleanText(item.tyreindex) &&
              parseInt(tyre.seqno) < parseInt(item.seqno),
          );

          const { inuse } = findPrev || {};
          const { label: statusName } =
            this.tyreStatusList.find((status) => status.keyValue == String(inuse ?? '')) || {};
          prevStatus = statusName;
        }
        return {
          ...item,
          prevStatus: prevStatus,
        };
      });

      const list = filteredList.map((item) => {
        const findPart = this.partTableData.find((part) => part.tyreSeqNo == item.seqno) ?? {};
        const type = cleanText(item.type);
        const otherInventoryNo = cleanText(item.otherinventoryno);
        const otherJobNo = cleanText(item.otherjobno);
        const hasOtherVehicle = type == 'OTHER' && otherInventoryNo;
        const rotatedWith = hasOtherVehicle ?
            `${otherInventoryNo} (${otherJobNo || 'New Job'})`
          : isRotate(item) ? (item.rotatedWithPosition ?? '') : '';
        return {
          'New Serial No': item.serialno ?? '',
          'Old Serial No': item.oldserialno ?? '',
          'Tyre Position': item.tyreposition ?? '',
          'Replace Type': type ?? '',
          'Rotated With': rotatedWith,
          'Store In': item.storein ?? '',
          'Prev Tyre Status': item.prevStatus ?? '',
          'Date Changed': item.datechange ?? '',
          Remarks: findPart.Remark ?? '',
          seqno: item.seqno,
          tyreindex: item.tyreindex,
          otherjobno: otherJobNo,
          rotatedwith: hasOtherVehicle ? otherInventoryNo : isRotate(item) ? (item.rotatedWithPosition ?? '') : '',
          action: item.action,
        };
      });
      return this.reorderTableNo(list.sort((a, b) => (a.seqno ?? 0) - (b.seqno ?? 0)));
    },

    tyreReplaceRadio() {
      return [
        {
          label: 'New Tyre',
          keyValue: 'NEW',
          isDisabled: this.tyreStatusValue?.label == 'Rotate' || !this.tyreStatusValue,
        },
        {
          label: 'Tyre From Warehouse',
          keyValue: 'WHSE',
          isDisabled: this.tyreStatusValue?.label == 'Rotate' || !this.tyreStatusValue,
        },
        {
          label: 'Tyre From Current Vehicle',
          keyValue: 'ROTATE',
          isDisabled: this.tyreStatusValue?.label != 'Rotate' || !this.tyreStatusValue,
        },
        {
          label: 'Tyre From Other Vehicle',
          keyValue: 'OTHER',
          isDisabled: this.tyreStatusValue?.label != 'Rotate' || !this.tyreStatusValue,
        },
      ];
    },

    tyreRotateOtherVehicleWorkshopJobList() {
      const workshopJobs =
        this.tyreRotateOtherVehicleWorkshopJobs === null ?
          this.workshopJobsTableData
        : this.tyreRotateOtherVehicleWorkshopJobs;

      return [
        ...workshopJobs
          .filter(
            (item) =>
              cleanText(item['Job #']) != cleanText(this.jobNoValue) &&
              cleanText(item['Inventory #']) == cleanText(this.tyreConfigOtherVehicleValue?.['Inventory #']) &&
              String(item.IsLatest).toLowerCase() == 'true',
          )
          .map((item) => ({ ...item, label: `Link To Latest ${item['Job #']} (${item['Job Date']})` })),
        { label: 'Create New Workshop Job', 'Job #': '' },
      ];
    },

    tyreConfigTyreInfoDisabled() {
      if (!this.tyreConfigCanEditTyreChange) return true;
      if (this.tyreReplaceValue?.keyValue != 'NEW') return true;
      return false;
    },

    tyreConfigRotateOtherDisabled() {
      const selected = this.tyreConfigTableSelectedRow?.[0];
      const { action, 'Replace Type': replaceType } = selected || {};
      return selected && this.tyreReplaceValue?.keyValue == 'OTHER' && action != 'N' && replaceType == 'OTHER';
    },

    tyreConfigRotateOtherWorkshopJobIsNotLatest() {
      return this.disableAttachJob && !this.tyreRotateOtherVehicleWorkshopJobValue;
    },

    jobDateDate() {
      return this.jobDateValue?.formatString('yyyy-MM-dd') ?? '';
    },
    jobDateTime() {
      return this.jobDateValue?.formatString('HHmm') ?? '';
    },
  },

  methods: {
    async menuClicked(e) {
      this.viewMode = false;
      this.menuMode = e.key;
      this.selectedTabIndex = 0;
      switch (e.key) {
        case 'New': {
          this.showPopup = true;
          this.viewMode = false;
          this.clearPopupFields();
          this.jobDateValue = new Date();
          break;
        }

        case 'Edit': {
          if (this.workshopJobsTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Workshop Job is selected', 'Please select a Workshop Job to Edit');
            break;
          }
          this.showPopup = true;
          this.populatePopupEntryFields(this.workshopJobsTableSelectedRows[0]['Job #']);
          break;
        }
        case 'View': {
          if (this.workshopJobsTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Workshop Job is selected', 'Please select a Workshop Job to View');
            break;
          }
          this.showPopup = true;
          this.viewMode = true;
          this.populatePopupEntryFields(this.workshopJobsTableSelectedRows[0]['Job #']);
          break;
        }
        case 'Delete': {
          if (this.workshopJobsTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Workshop Job is selected', 'Please select a Workshop Job to Delete');
            break;
          }

          const selectedJob = this.workshopJobsTableSelectedRows[0] || {};
          const { 'Job #': jobNo, canDelete } = selectedJob;

          if (canDelete != 1) {
            this.$showAlert(
              'error',
              'Workshop Job cannot be deleted',
              'Expenses are already posted for some of the parts.',
            );
            break;
          }

          const hasIsLatest = Object.prototype.hasOwnProperty.call(selectedJob, 'IsLatest');
          const isLatest =
            selectedJob.IsLatest == '1' ||
            selectedJob.IsLatest == 1 ||
            selectedJob.IsLatest === true ||
            String(selectedJob.IsLatest).toLowerCase() == 'true';

          if (hasIsLatest && !isLatest) {
            this.$showAlert(
              'error',
              'Workshop Job cannot be deleted',
              'Only the latest Workshop Job for this vehicle can be deleted.',
            );
            break;
          }

          const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Workshop Job?', '', [
            { text: 'Delete', color: 'Red', textColor: 'white' },
            { text: 'Cancel' },
          ]);
          if (discard == 'Cancel') return false;
          this.$fullscreenLoading('Deleting Workshop Job');

          const result = (
            await this.$apiCallV2('/WorkshopJob/SaveEMSJob', {
              jobno: jobNo,
              action: 'D',
            })
          )[0];
          const { Results, ErrMsg } = result || {};

          if (Results == '0') {
            this.$showAlert('success', 'Workshop Job Deleted', ErrMsg, [{ text: 'OK' }]);
          } else {
            this.$showAlert('error', 'Workshop Job Could Not Be Deleted', ErrMsg, [{ text: 'OK' }]);
          }
          await this.getWorkshopJobs();
          this.$fullscreenLoading();
          break;
        }
        case 'Print': {
          const { 'Job #': jobno } = this.workshopJobsTableSelectedRows[0] || {};

          const printAckRoute = this.$router.resolve(
            `/Home/${this.$route.params.user}/Workshop/PrintWorkOrder${jobno ? `?job=${jobno.trim()}` : ''}`,
          );

          window.open(printAckRoute.href, '_blank');
          break;
        }

        case 'UpdateRetreadTyres': {
          this.updateTyrePopupOpen = true;
          break;
        }
      }
    },

    async filterRefreshClick() {
      this.filterStartJobNoError = '';
      this.filterEndJobNoError = '';

      if (this.dateFilterActive) {
        if (!this.filterEndDate) {
          this.filterEndDate = new Date();
        }
        if (!this.filterStartDate || this.filterStartDate >= this.filterEndDate) {
          this.filterStartDate = this.getFilterStartDate(this.filterEndDate);
        }
        this.filterActiveMode = 'Date';
        this.activeStartDate = this.filterStartDate;
        this.activeEndDate = this.filterEndDate;
      } else if (this.jobNoFilterActive) {
        if (!this.filterStartJobNo && !this.filterEndJobNo) {
          this.filterStartJobNoError = 'Start Job # is required';
          this.filterEndJobNoError = 'End Job # is required';
          return;
        }

        if (!this.filterStartJobNo) {
          this.filterStartJobNo = this.filterEndJobNo;
        }
        if (!this.filterEndJobNo) {
          this.filterEndJobNo = this.filterStartJobNo;
        }

        this.filterActiveMode = 'JobNo';
        this.activeStartJobNo = this.filterStartJobNo.JobNo;
        this.activeEndJobNo = this.filterEndJobNo.JobNo;
      }

      this.filterOpen = false;
      await this.getWorkshopJobs();
    },

    dateFilterCheckBoxClicked(e) {
      this.jobNoFilterActive = !e;
    },

    jobNoFilterCheckBoxClicked(e) {
      this.dateFilterActive = !e;
    },

    startJobNoSelected() {
      const findStartIndex = this.EMSJobNoList.findIndex((jobno) => jobno.JobNo == this.filterStartJobNo.JobNo);
      const findEndIndex = this.EMSJobNoList.findIndex((jobno) => jobno.JobNo == this.filterEndJobNo?.JobNo);

      if (findEndIndex > findStartIndex) this.filterEndJobNo = null;
    },

    endJobNoSelected() {
      const findStartIndex = this.EMSJobNoList.findIndex((jobno) => jobno.JobNo == this.filterStartJobNo?.JobNo);
      const findEndIndex = this.EMSJobNoList.findIndex((jobno) => jobno.JobNo == this.filterEndJobNo.JobNo);

      if (findEndIndex > findStartIndex) this.filterStartJobNo = null;
    },

    // #region API fetching data
    async getEMSJobNoList() {
      this.EMSJobNoList = await this.$apiCallV2('/WorkshopJob/GetEMSJobNoList', {});
    },

    async getWorkshopJobColumnOrders() {
      const tableConfigs = [
        {
          viewsKey: 'workshopJobsTableColumnOrderViews',
          idKey: 'workshopJobsTableColumnOrderID',
          formctrlname: 'Workshop Job',
        },
        {
          viewsKey: 'docTableColumnOrderViews',
          idKey: 'docTableColumnOrderID',
          formctrlname: 'Workshop Job Documents',
        },
        {
          viewsKey: 'taskTableColumnOrderViews',
          idKey: 'taskTableColumnOrderID',
          formctrlname: 'Workshop Job Tasks',
        },
        {
          viewsKey: 'partTableColumnOrderViews',
          idKey: 'partTableColumnOrderID',
          formctrlname: 'Workshop Job Parts',
        },
        {
          viewsKey: 'tyreConfigTableColumnOrderViews',
          idKey: 'tyreConfigTableColumnOrderID',
          formctrlname: 'Workshop Job Tyre Changes',
        },
      ];

      await Promise.all(
        tableConfigs.map(async ({ viewsKey, idKey, formctrlname }) => {
          if (this[idKey] !== null) return;

          const columnOrderID = {
            formname: 'EMS_WorkshopJob',
            formctrlname,
          };
          this[viewsKey] = await this.$apiCallV2('/TableBox/GetColumnOrder', columnOrderID);
          this[idKey] = columnOrderID;
        }),
      );
    },

    async getTyreRotateOtherVehicleWorkshopJobs(inventoryNo) {
      if (!inventoryNo) {
        this.tyreRotateOtherVehicleWorkshopJobs = [];
        return;
      }

      const result = await this.$apiCallV2('/WorkshopJob/GetEMSJobList', {
        startdate: '',
        enddate: '',
        inventoryno: inventoryNo,
      });
      this.tyreRotateOtherVehicleWorkshopJobs = Array.isArray(result) ? result : [];
    },

    async getWorkshopJobs() {
      this.workshopJobsTableLoading = true;
      this.workshopJobsTableData = [];
      this.workshopJobsTableSelectedRows = [];

      this.getEMSJobNoList();

      const joinDateTime = (date, time) => {
        if (!time) return date;
        const timeAdd = `${time.slice(0, 2)}:${time.slice(2, 5)}`;
        return `${date.slice(0, -5)}${timeAdd}`;
      };

      const filterBody = () => {
        const filterMode = this.filterActiveMode;

        if (filterMode == 'Date') {
          return {
            startdate: new Date(this.activeStartDate).formatString('yyyy-MM-dd'),
            enddate: new Date(this.activeEndDate).formatString('yyyy-MM-dd'),
          };
        } else if (filterMode == 'JobNo') {
          return {
            startjobno: this.activeStartJobNo,
            endjobno: this.activeEndJobNo,
          };
        }
      };

      this.workshopJobsTableData = (await this.$apiCallV2('/WorkshopJob/GetEMSJobList', filterBody())).map((item) => {
        const {
          'Date / Time In': dateTimeIn,
          TimeIn,
          'Date / Time Out': dateTimeOut,
          TimeOut,
          'Create Time': CreateTime,
          'Create UserID': CreateUserID,
          'Last Update Time': UpdateTime,
          'Last Update UserID': UpdateUserID,
        } = item;
        const dateTimeInFormat = dateTimeIn ? joinDateTime(dateTimeIn, TimeIn) : null;
        const dateTimeOutFormat = dateTimeOut ? joinDateTime(dateTimeOut, TimeOut) : null;
        return {
          ...item,
          'Date / Time In': dateTimeInFormat,
          'Date / Time Out': dateTimeOutFormat,
          'Create Time': CreateTime || UpdateTime,
          'Create UserID': CreateUserID || UpdateUserID,
        };
      });

      await this.getWorkshopJobColumnOrders();

      this.workshopJobsTableLoading = false;
    },

    async getWorkshopJobDetails(jobNo) {
      const result = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'EMSJOB',
        module: 'MASTER',
        recordid: jobNo,
      });
      return result.EMSJob;
    },

    async getVehicleList() {
      this.vehicleList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VEHICLE' })).map((item) => ({
        ...item,
        label: `${item['Inventory #']} - ${item['Inventory Code']} `,
      }));
    },

    async getEmployeeList() {
      this.employeeList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'EMPLOYEE' })).map((item) => ({
        '#': item['#'],
        EmployeeID: item.EmployeeID,
        'Employee Name': item['Employee Name'],
        ...item,
        label: `${item['EmployeeID']} - ${item['Employee Name']}`,
      }));
    },
    async getSupplierList() {
      this.supplierList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SUPPLIER' }))
        .filter((item) => item.Active == 'YES' || item.Active == '1')
        .map((item) => ({
          ...item,
          label: `${item['Supplier Code']} - ${item['Supplier Name']}`,
        }));
    },

    async getDocType() {
      this.docTypeList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'doctype' })).map((item) => ({
        ...item,
        label: `${item['Doc Type']} - ${item.Description}`,
      }));
    },
    async getDocCode() {
      this.docCodeList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'doc' })).map((item) => ({
        ...item,
        label: `${item['Doc Code']} - ${item.Description}`,
      }));
    },

    async getServices() {
      this.taskServicesList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'services' })).map(
        (item) => ({
          ...item,
          label: `${item.ServiceCode} - ${item.Description}`,
        }),
      );
    },

    async getServicesTask() {
      this.taskServicesTaskList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'servicestask' })).map(
        (item) => ({
          ...item,
          label: `${item.ServiceCode} - ${item.Description}`,
        }),
      );
    },

    async getPartType() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTTYPEWITHPRODUCTINFO' });
      this.partTypeList = result.map((item) => {
        const { 'Part Type': PartType, Description, 'Part Group': PartGroup, ProdName, Size, Shade } = item;

        const partTypeLabel = `${PartType}${PartGroup ? ` [${PartGroup}]` : ''}`;
        const infoArray = [];
        if (ProdName && ProdName != Description) infoArray.push(ProdName);
        if (Size && Size != '-') infoArray.push(Size);
        if (Shade && Shade != '-') infoArray.push(Shade);
        const infoLabel = infoArray.length > 0 ? `[${infoArray.join('|')}]` : '';
        const descriptionLabel = `${Description} ${infoLabel}`;

        return {
          ...item,
          isExpense: item.IsExpenses,
          'Is Expense': ['Y', 'YES', '1', 'TRUE'].includes(String(item.IsExpenses ?? '').trim().toUpperCase()) ? 'Yes' : 'No',
          label: `${partTypeLabel} - ${descriptionLabel}`,
        };
      });
    },

    async getSerialNo() {
      this.serialNoList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBPARTSERIALNO' });
    },

    async getTakenFrom() {
      this.takenFromList = (
        await this.$apiCallV2('/WorkshopJob/GetWorkshopJobWarehouseItems', {
          jobno: this.jobNoValue || '',
        })
      ).map((item) => ({
        ...item,
        label: `${item.Warehouse}${item['Serial No'] ? `- ${item['Serial No']}` : ''}`,
      }));
    },

    async getCountry() {
      this.countryList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'country' })).map((item) => ({
        ...item,
        label: `${item['Country Code']} - ${item.Country}`,
      }));
    },
    async getManufacturer() {
      this.manufacturerList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'manufacturer' })).map(
        (item) => ({ ...item, label: `${item.MFGCode} - ${item.MFGName}` }),
      );
    },
    async getVehicleInfo(vehicleNo) {
      const result = await this.$apiCallV2('/Master/GetDetailsMaster', { recordid: vehicleNo, masterfile: 'VEHICLE' });
      return result.Vehicle;
    },
    async getVehicleTyreLayout(vehicleCategory, vehicleType) {
      const result = (
        await this.$apiCallV2(`/Vehicle/GetVehicleTyreConfiguration?category=${vehicleCategory}&type=${vehicleType}`)
      ).map((tyre) => {
        return {
          index: tyre['tyreindex'],
          description: tyre['tyrenaming'],
        };
      });
      return result;
    },
    async getVehicleDriver(invNo) {
      const result = await this.$apiCallV2('/WorkshopJob/GetDriverDetails', { vehicleno: invNo });
      return result;
    },
    async getWarehouse() {
      this.warehouseList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'WAREHOUSE' })).map(
        (warehouse) => {
          return {
            ...warehouse,
            label: `${warehouse['Warehouse Code']} - ${warehouse.Name}`,
          };
        },
      );
    },
    async getJobPartTyreList() {
      this.jobPartTyreList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBPARTTYRELIST' })).map(
        (item) => ({ ...item, label: `${item['Serial No']} - ${item['Part Type']}` }),
      );
    },
    async getJobPartTyreSaveList() {
      this.jobPartTyreSaveList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBPARTTYRESAVE' });
    },

    async getSAS() {
      this.SASControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=EMS`);
    },

    async getLocalCurrency() {
      const companyDetails = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'COMPANY',
        module: 'MASTER',
        recordid: this.$store.state.loginDetails.CoCode,
      });
      if (companyDetails.CompanyLocalCur) this.localCurrency = companyDetails.CompanyLocalCur;
    },

    //#endregion

    // #region clears
    clearPopupFields() {
      this.jobNoValue = '';
      this.jobDateValue = null;
      this.clearPopupFieldsWithoutJobNo();
    },

    clearPopupFieldsWithoutJobNo() {
      this.jobInventoryValue = null;

      this.clearErrorsGeneral();

      this.createdInfo = '';
      this.lastUpdatedInfo = '';

      this.trailerNoValue = null;
      this.requestedByValue = null;
      this.repairTypeValue = null;
      this.repairTypeValueError = '';
      this.internalExternalValue = null;
      this.internalExternalValueError = '';
      this.servicePersonValue = '';
      this.workshopValue = null;
      this.workshopValueError = '';
      this.mileageValue = '';
      this.mileageValueError = '';
      this.remark1Value = '';
      this.dateTimeInValue = null;
      this.dateTimeOutValue = null;
      this.confirmJobValue = false;
      this.inventoryAvailabilityValue = null;
      this.inventoryUnavailableRemarkValue = '';
      this.inventoryUnavailableRemarkValueError = '';

      this.docTableData = [];
      this.taskTableData = [];
      this.partTableData = [];
      this.tyreConfigUpdateList = [];
      this.tyreRotateOtherVehicleWorkshopJobs = null;
      this.tyreConfigUpdateOtherVehicle = [];
      this.partUpdateOtherVehicle = [];

      this.clearTabsFields();

      this.clearEntryTexts();

      this.isLatestJob = true;
      this.isLatestTyreJob = true;
    },

    clearEntryTexts() {
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryErrorMessages = [];
    },
    clearTabsFields() {
      this.docFields.docReferenceNoValue = '';
      this.docFields.docDocTypeValue = null;
      this.docFields.docDocDateValue = null;
      this.docFields.docDateReceivedValue = null;
      this.docFields.docDocCodeValue = null;
      this.docFields.docPicDocValue = null;
      this.docFields.docRemarkValue = '';
      this.docFields.docPicDocType = '';
      this.docFields.docPicDocName = '';

      this.clearErrorsDoc();
      this.docFieldsChangedCheck = structuredClone(this.docFields);

      this.taskFields.taskServiceValue = null;
      this.taskFields.taskServiceTaskValue = null;
      this.taskFields.taskServicePersonValue = '';
      this.taskFields.taskDateTimeStartValue = null;
      this.taskFields.taskDateTimeEndValue = null;
      this.taskFields.taskRemarkValue = '';

      this.clearErrorsTask();
      this.taskFieldsChangedCheck = structuredClone(this.taskFields);

      this.partFields.partTypeValue = null;
      this.clearPartFieldsWithoutType();

      this.clearErrorsPart();
      this.partFieldsChangedCheck = structuredClone(this.partFields);

      this.clearTyresFields();

      this.docTableSelectedRows = [];
      this.taskTableSelectedRows = [];
      this.partTableSelectedRows = [];

      this.docSelectedItem = null;
      this.taskSelectedItem = null;
      this.partSelectedItem = null;
    },

    clearPartFieldsWithoutType() {
      this.partFields.partWarehouseItemValue = null;
      this.partFields.partWarehouseItemSavedValue = null;
      this.partFields.partWarehouseItemSearch = '';
      this.partFields.partSerialNoValue = null;
      this.partFields.partSerialNoInputValue = '';
      this.partFields.partPartNoValue = '';
      this.partFields.partSizeValue = '';
      this.partFields.partTakenFromValue = null;
      this.partFields.partTakenFromInputValue = '';
      this.partFields.partUnitPriceValue = '';
      this.partFields.partMFGDateValue = null;
      this.partFields.partDatePurchaseValue = null;
      this.partFields.partInvoiceNoValue = '';
      this.partFields.partModelValue = '';
      this.partFields.partReplaceValue = '';
      this.partFields.partMadeInValue = null;
      this.partFields.partQuantityValue = '1';
      this.partFields.partManufacturerValue = null;
      this.partFields.partPurchaseFromValue = null;
      this.partFields.partPONoValue = '';
      this.partFields.partInsuranceNoValue = '';
      this.partFields.partRemarkValue = '';
    },

    clearTyresFields() {
      this.tyreConfigLayoutSelectedItem = null;
      this.tyrePositionSelectedValue = null;
      this.tyreConfigTableSelectedRow = [];
    },

    clearErrorsGeneral() {
      this.jobNoValueError = '';
      this.jobDateValueError = '';
      this.jobInventoryValueError = null;
      this.dateTimeInValueError = '';
    },

    clearErrorsDoc() {
      this.docReferenceNoError = '';
      this.docDocTypeError = '';
      this.docDocDateError = '';
      this.docDateReceivedError = '';
      this.docDocCodeError = '';
    },

    clearErrorsTask() {
      this.taskServiceError = '';
      this.taskServiceTaskError = '';
    },

    clearErrorsPart() {
      this.partTypeError = '';
      this.partSerialNoError = '';
      this.partQuantityError = '';
    },

    clearErrorsTyreConfig() {
      this.tyreExistingValueError = '';
      this.tyreConfigOtherVehicleValueError = '';
      this.tyreConfigTyreTypeValueError = '';
      this.tyreConfigSerialNoValueError = '';
      this.tyreConfigErrorText = '';
    },
    //#endregion

    // #region populate
    async populatePopupEntryFields(jobNo) {
      this.clearPopupFields();
      this.$fullscreenLoading('Getting Workshop Job Details...');
      const job = await this.getWorkshopJobDetails(jobNo);
      const {
        JobNo,
        JobDate,
        Inventory,
        LastUpdateTime,
        LastUpdateUserID,
        CreateTime,
        CreateUserID,
        Trailer,
        RequestedBy,
        RepairType,
        InternalExternal,
        Workshop,
        ServicePerson,
        Mileage,
        Remark,
        DateTimeIn,
        TimeIn,
        DateTimeOut,
        TimeOut,
        Status,
        InvAvailability,
        Documents,
        Tasks,
        Parts,
        InvAvailabilityRemark,
        IsLatest,
        IsLatestTyreJob,
      } = job || {};

      const DateIn = DateTimeIn ? new Date(DateTimeIn) : null;
      if (DateIn) {
        const TimeInHour = TimeIn ? parseInt(TimeIn?.slice(0, 2)) : '00';
        const TimeInMinute = TimeIn ? parseInt(TimeIn?.slice(2, 5)) : '00';
        DateIn.setHours(TimeInHour, TimeInMinute);
      }

      const DateOut = DateTimeOut ? new Date(DateTimeOut) : null;
      if (DateOut) {
        const TimeOutHour = TimeOut ? parseInt(TimeOut?.slice(0, 2)) : '00';
        const TimeOutMinute = TimeOut ? parseInt(TimeOut?.slice(2, 5)) : '00';
        DateOut.setHours(TimeOutHour, TimeOutMinute);
      }

      this.jobFetched = job;

      this.jobNoValue = cleanText(JobNo);
      this.jobDateValue = new Date(JobDate);
      this.jobInventoryValue =
        this.vehicleList.find((item) => cleanText(item['Inventory #']) == cleanText(Inventory)) ?? null;
      this.lastUpdatedInfo = `${LastUpdateUserID} ${new Date(LastUpdateTime).formatString('dd/MM/yyyy HH:mm')}`;
      this.createdInfo =
        CreateUserID && CreateTime ?
          `${CreateUserID} ${new Date(CreateTime).formatString('dd/MM/yyyy HH:mm')}`
        : this.lastUpdatedInfo;

      this.trailerNoValue =
        this.trailerList.find((item) => cleanText(item['Inventory #']) == cleanText(Trailer)) ?? null;
      this.requestedByValue = this.employeeList.find((item) => item['EmployeeID'] == RequestedBy);
      this.repairTypeValue = this.repairTypeOptions.find((item) => item.keyValue == RepairType) ?? null;
      this.internalExternalValue = this.intExtOptions.find((item) => item.keyValue == InternalExternal) ?? null;
      this.servicePersonValue = ServicePerson;
      this.workshopValue = this.supplierList.find((item) => item['Supplier Code'] == Workshop) ?? null;
      this.mileageValue = parseFloat(Mileage).toString();
      this.remark1Value = Remark;
      this.dateTimeInValue = DateIn || DateIn == 'Invalid Date' ? DateIn : null;
      this.dateTimeOutValue = DateOut || DateOut == 'Invalid Date' ? DateOut : null;
      this.confirmJobValue = Boolean(Status);
      this.inventoryAvailabilityValue =
        this.availabilityOptions.find((item) => item.keyValue == InvAvailability) ?? this.availabilityOptions[0];
      this.inventoryUnavailableRemarkValue = InvAvailabilityRemark || '';

      if (Documents)
        this.docTableData = this.convertToArray(Documents).map((doc, index) => ({
          ...doc,
          'Date Received': this.combineDateTime(doc['Date Received'], doc['Time Received']),
          seqNo: index + 1, //seqNo used for table actions new/edit/delete
        }));
      if (Tasks)
        this.taskTableData = this.convertToArray(Tasks).map((task) => ({
          ...task,
          'Date Start': this.combineDateTime(task['Date Start'], task['Time Start']),
          'Date End': this.combineDateTime(task['Date End'], task['Time End']),
        }));

      await this.populateVehicleInfo(Inventory);
      const parts = this.convertToArray(Parts);
      this.tyreConfigUpdateList = this.tyreConfigUpdateList.map((tyre) => {
        if (cleanText(tyre.oldserialno)) return tyre;

        const part = parts.find(
          (item) =>
            cleanText(item['Serial No']) &&
            cleanText(item.TyreIndex) != null &&
            cleanText(item['Serial No']) == cleanText(tyre.serialno) &&
            cleanText(item.TyreIndex) == cleanText(tyre.tyreindex),
        );
        const oldSerialNo = cleanText(part?.Replace);
        return oldSerialNo ? { ...tyre, oldserialno: oldSerialNo } : tyre;
      });

      if (Parts)
        this.partTableData = parts.map((part, index) => {
          const { Type, MfgDate, 'Date Purchase': DatePurchase } = part;
          const {
            ProdTxnID,
            Description,
            'Expense Code': expenseCode,
            IsExpenses: isExpense,
          } = this.partTypeList.find((item) => item['Part Type'] == Type) || {};
          const update = this.tyreConfigUpdateList.find(
            (tyre) =>
              cleanText(tyre.serialno) == cleanText(part['Serial No']) &&
              cleanText(tyre.tyreindex) == cleanText(part.TyreIndex),
          );
          const isRotation = update?.type == 'ROTATE' || update?.type == 'OTHER';

          return {
            ...part,
            seqNo: index + 1,
            prodTxnID: ProdTxnID,
            AllocGUID: part.AllocGUID || '',
            BinLoc: part.BinLoc || '',
            expenseCode: expenseCode,
            isExpense: isRotation ? 'N' : isExpense,
            partType: Type,
            partTypeDesc: Description,
            MfgDate: MfgDate?.split('T')[0] || null,
            DatePurchase: DatePurchase?.split('T')[0] || null,
            tyreSeqNo: update ? update.seqno : null,
          };
        });

      this.isLatestJob = IsLatest == '1' || IsLatest == 1 || IsLatest === true;
      this.isLatestTyreJob = IsLatestTyreJob == '1' || IsLatestTyreJob == 1 || IsLatestTyreJob === true;

      await Promise.all([
        this.getSerialNo(),
        this.getTakenFrom(),
        this.getJobPartTyreList(),
        this.getJobPartTyreSaveList(),
      ]);

      this.$fullscreenLoading();
    },

    async populateVehicleInfo(inventoryNo) {
      this.$fullscreenLoading('Getting Vehicle info...');
      const vehicle = await this.getVehicleInfo(inventoryNo);
      const { TyreJob, VehicleCategory, Type, MeterReading, Availability } = vehicle || {};
      this.selectedVehicleInfo = vehicle;
      if (this.menuMode == 'New') {
        this.inventoryAvailabilityValue =
          this.availabilityOptions.find((item) => item.keyValue == Availability) ?? this.availabilityOptions[0];
        this.inventoryUnavailableRemarkValue = '';
      }

      const tyreLayout = await this.getVehicleTyreLayout(VehicleCategory, Type);

      const tyreConfigList = this.tyreConfigToStructure(TyreJob, tyreLayout);

      this.tyreConfigTyresList = tyreConfigList.filter((item) => cleanText(item.jobno) != cleanText(this.jobNoValue));
      this.tyreConfigUpdateList = tyreConfigList.filter((item) => cleanText(item.jobno) == cleanText(this.jobNoValue));
      this.tyreConfigTableSelectedRow = [];

      this.tyreLayout = [
        ...tyreLayout,
        { index: '100', description: 'Spare1' },
        { index: '101', description: 'Spare2' },
        { index: '102', description: 'Spare3' },
        { index: '103', description: 'Spare4' },
      ];

      if (this.menuMode == 'New') {
        this.mileageValue = MeterReading ?? '0';
      }
      this.$fullscreenLoading();
    },

    populateDocumentFields(doc) {
      this.clearTabsFields();
      const {
        Code,
        Date: DocDate,
        'Date Received': DateReceived,
        'Pic/Doc': PicDoc,
        PicDocFile,
        PicDocType,
        'Ref No': RefNo,
        Remark,
        Type,
      } = doc || {};
      this.docFields.docReferenceNoValue = RefNo;
      this.docFields.docDocTypeValue = this.docTypeList.find((item) => item['Doc Type'] == Type);
      this.docFields.docDocDateValue = DocDate ? new Date(DocDate) : null;
      this.docFields.docDateReceivedValue = DateReceived ? new Date(DateReceived) : null;
      this.docFields.docDocCodeValue = this.docCodeList.find((item) => item['Doc Code'] == Code);
      this.docFields.docPicDocName = PicDoc;
      this.docFields.docPicDocType = PicDocType;
      this.docFields.docPicDocValue = PicDocFile;

      //att here
      this.docFields.docRemarkValue = Remark;

      if (!this.viewMode) this.docFieldsChangedCheck = structuredClone(this.docFields);
    },

    populateTaskFields(task) {
      this.clearTabsFields();
      const {
        'Date Start': DateStart,
        'Date End': DateEnd,
        Remark,
        'Service Code': ServiceCode,
        'Service Person': ServicePerson,
        'Task Code': TaskCode,
      } = task || {};

      this.taskFields.taskServiceValue = this.taskServicesList.find((item) => item.ServiceCode == ServiceCode) ?? null;
      this.taskFields.taskServiceTaskValue =
        this.taskServicesTaskList.find((item) => item.ServiceCode == TaskCode) ?? null;
      this.taskFields.taskServicePersonValue = ServicePerson;
      this.taskFields.taskDateTimeStartValue = DateStart ? new Date(DateStart) : null;
      this.taskFields.taskDateTimeEndValue = DateEnd ? new Date(DateEnd) : null;
      this.taskFields.taskRemarkValue = Remark;

      if (!this.viewMode) this.taskFieldsChangedCheck = structuredClone(this.taskFields);
    },

    populatePartFields(part) {
      this.clearTabsFields();
      const {
        Type,
        'Serial No': SerialNo,
        'Part No': PartNo,
        Manufacturer,
        'Made In': MadeIn,
        Size,
        'Cost (RM)': Cost,
        Replace,
        'Purchase From': PurchaseFrom,
        'Date Purchase': DatePurchase,
        'Insurance Claim No': InsuranceNo,
        'Invoice No': InvoiceNo,
        'Taken From': Warehouse,
        Remark,
        PONo,
        MfgDate,
        Model,
        Quantity,
      } = part || {};

      const quantityValue = this.parsePartAmount(Quantity) || 1;
      const unitPrice = this.parsePartAmount(Cost) / quantityValue;
      const isWarehouseLinked = this.partHasWarehouseLink(part);

      this.partFields.partWarehouseItemSavedValue = isWarehouseLinked ? part : null;
      this.partFields.partTypeValue = this.partTypeList.find((item) => item['Part Type'] == Type) ?? null;
      this.partFields.partSerialNoValue =
        isWarehouseLinked ? this.serialNoList.find((item) => item['Serial No'] == SerialNo) ?? null : null;
      this.partFields.partSerialNoInputValue = SerialNo || '';
      this.partFields.partPartNoValue = PartNo;
      this.partFields.partSizeValue = Size;
      const warehouseItem = isWarehouseLinked ?
        this.warehouseItemListFiltered.find((item) => this.warehouseItemMatchesPart(item, part))
        : null;
      const selectedWarehouse = this.partWarehouseValue(warehouseItem || { Warehouse });
      this.partFields.partTakenFromValue = selectedWarehouse;
      this.partFields.partTakenFromInputValue = selectedWarehouse?.label ?? Warehouse ?? '';
      if (warehouseItem) {
        this.partFields.partWarehouseItemValue = warehouseItem;
        this.partFields.partWarehouseItemSearch = warehouseItem.label;
      }
      this.partFields.partUnitPriceValue = this.$currencyFormat(unitPrice);
      this.partFields.partMFGDateValue = MfgDate ? new Date(MfgDate) : null;
      this.partFields.partDatePurchaseValue = DatePurchase ? new Date(DatePurchase) : null;
      this.partFields.partInvoiceNoValue = InvoiceNo;
      this.partFields.partReplaceValue = Replace;
      this.partFields.partModelValue = Model;
      this.partFields.partMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) ?? null;
      this.partFields.partQuantityValue = this.normalizePartQuantityValue(Quantity || '1') || '1';
      this.partFields.partManufacturerValue =
        this.manufacturerList.find((item) => item.MFGCode == Manufacturer) ?? null;
      this.partFields.partPurchaseFromValue =
        this.supplierList.find((item) => item['Supplier Code'] == PurchaseFrom) ?? null;
      this.partFields.partPONoValue = PONo;
      this.partFields.partInsuranceNoValue = InsuranceNo;
      this.partFields.partRemarkValue = Remark;

      if (!this.viewMode) this.partFieldsChangedCheck = structuredClone(this.partFields);
    },
    //#endregion

    //#region tyreConfig
    tyreGridClicked() {
      this.clearTyreConfigFieldsOnly();
      this.tyreUpdateMode = 'N';
      const { index: selectedIndex } = this.tyreConfigLayoutSelectedItem || {};
      if (!selectedIndex) {
        this.tyreConfigTableSelectedRow = [];
        this.tyrePositionSelectedValue = null;
        return;
      }
      this.tyrePositionSelectedValue = this.tyreConfigLayout.findLast((item) => item.index == selectedIndex) ?? null;

      this.tyreConfigTableSelectedRow = (() => {
        let returnItem = null;
        returnItem = this.tyreConfigUpdateListFiltered.find((item) => item.tyreindex == selectedIndex);
        if (!returnItem) {
          const findUpdate = this.tyreConfigUpdateList.find((item) => item.tyreindex == selectedIndex);
          if (findUpdate?.type == 'ROTATE') {
            const findPair = this.tyreConfigUpdateListFiltered.find((item) => item.seqno == findUpdate.seqno - 1);
            returnItem = findPair;
          }
        }
        return returnItem ? [{ ...returnItem, _rowid: `row${returnItem?.['#'] - 1}` }] : [];
      })();
    },
    tyreDropdownSelected(e) {
      const { index } = e || {};
      if (!index) {
        this.tyreConfigLayoutSelectedItem = null;
        this.tyreConfigTableSelectedRow = [];
        return;
      }
      this.tyreConfigLayoutSelectedItem = this.tyreConfigLayout?.find((item) => item.index == index) ?? null;

      this.tyreGridClicked();
    },
    tyreTableRowSelected(row, selected) {
      if (!selected) {
        this.tyreConfigLayoutSelectedItem = null;
        this.tyrePositionSelectedValue = null;
        return;
      }
      const { tyreindex } = row;
      const selectGrid = this.tyreConfigLayout?.find((item) => item.index == tyreindex);
      this.tyreConfigLayoutSelectedItem = selectGrid ?? null;
      this.tyrePositionSelectedValue = this.tyreConfigLayout.find((item) => item.index == tyreindex) ?? null;
    },

    async tyreConfigPopupOpen() {
      this.clearTyreConfigPopup();
      this.clearErrorsTyreConfig();
      const {
        'Old Serial No': oldserialno,
        action,
        otherjobno,
        'Replace Type': replace,
      } = this.tyreConfigTableSelectedRow[0] || {};
      const { serialno: currentOnTyre } = this.tyrePositionSelectedValue?.tyreInfo || {};
      this.tyreConfigOldSerialNoValue = oldserialno ?? currentOnTyre ?? '';
      if (!this.tyreConfigOldSerialNoValue) {
        this.tyreStatusValue = this.tyreStatusOptions[0];
      }

      if (this.tyreConfigTableSelectedRow.length > 0) {
        const { seqno } = this.tyreConfigTableSelectedRow[0];
        const tyreInfo = this.tyreConfigUpdateList.find((item) => item.seqno == seqno) || {};
        const partInfo = this.partTableData.find((item) => item.tyreSeqNo == seqno) || {};
        await this.populateTyreConfigFields(tyreInfo, partInfo);
        await this.validateTyreConfigPositionEdit(tyreInfo);
      }

      if (replace == 'OTHER') {
        await this.getTyreRotateOtherVehicleWorkshopJobs(this.tyreConfigOtherVehicleValue?.['Inventory #']);

        const findOtherSame = this.tyreConfigUpdateList.find(
          (item) =>
            cleanText(item.otherinventoryno) == cleanText(this.tyreConfigOtherVehicleValue?.['Inventory #']) &&
            item.action != 'D',
        );
        if (action != 'N' && findOtherSame) this.disableAttachJob = true;
        else this.disableAttachJob = false;

        const linkedJobNo = otherjobno || findOtherSame?.otherjobno;
        const findWorkshop = this.tyreRotateOtherVehicleWorkshopJobList.find(
          (item) => cleanText(item['Job #']) == cleanText(linkedJobNo),
        );
        if (findWorkshop) this.tyreRotateOtherVehicleWorkshopJobValue = findWorkshop;
        else if (linkedJobNo) {
          this.tyreRotateOtherVehicleWorkshopJobValue = {
            'Job #': linkedJobNo,
            label: linkedJobNo,
          };
        }
      }

      this.tyreConfigPopupShow = true;
    },

    async validateTyreConfigPositionEdit(tyreInfo) {
      const selected = this.tyreConfigTableSelectedRow[0] || {};
      this.tyreConfigPositionEditAllowed = false;
      this.tyreConfigPositionEditChecked = false;
      this.tyreConfigPositionEditMessage = '';

      if (!selected.seqno || selected.action == 'N' || this.viewMode) return;

      this.$fullscreenLoading('Checking Tyre Change...');
      const result = await this.$apiCallV2('/WorkshopJob/ValidateTyreChangeEdit', {
        jobno: this.jobNoValue,
        seqno: selected.seqno,
        inventoryno: this.jobInventoryValue?.['Inventory #'] ?? tyreInfo.inventoryno ?? '',
        tyreindex: selected.tyreindex ?? tyreInfo.tyreindex ?? '',
        oldserialno: selected['Old Serial No'] ?? tyreInfo.oldserialno ?? '',
        newserialno: selected['New Serial No'] ?? tyreInfo.serialno ?? '',
        datechanged: selected['Date Changed'] ?? tyreInfo.datechange ?? '',
      });
      this.$fullscreenLoading();

      const response = Array.isArray(result) ? result[0] || {} : result || {};
      const allowEdit = response.allowEdit ?? response.AllowEdit;
      this.tyreConfigPositionEditChecked = true;
      this.tyreConfigPositionEditAllowed = allowEdit === true || allowEdit == '1';
      if (!this.tyreConfigPositionEditAllowed)
        this.tyreConfigPositionEditMessage =
          response.reason ||
          response.Reason ||
          response.ErrMsg ||
          'Tyre change cannot be edited because future changes may exist for this position. Only Job Part Info is allowed for editing.';
    },

    tyreConfigRadioChanged() {
      this.tyreConfigOtherVehicleValue = null;
      this.tyreRotateOtherVehicleWorkshopJobs = null;
      this.tyreRotateOtherVehicleWorkshopJobValue = null;
      this.tyreRotateOtherVehicleWorkshopJobInputValue = '';
      const replaceType = this.tyreReplaceValue?.keyValue;
      this.tyreConfigJobPriceValue = '';

      this.clearTyreConfigFieldsOnly();
      this.tyreConfigRotateLayoutChange(replaceType);

      this.clearErrorsTyreConfig();
    },

    tyreConfigRotateLayoutChange(replaceType) {
      if (replaceType == 'ROTATE') {
        const { 'New Serial No': newserialnoSelected, tyreindex: tyreindexTableSelected } =
          this.tyreConfigTableSelectedRow[0] || {};
        const tyreindexselected = tyreindexTableSelected ?? this.tyrePositionSelectedValue?.index;
        this.tyreConfigSelectedVehicleLayout = this.tyreConfigLayout.map((tyre) => {
          const { tyreInfo, index } = tyre;
          const { serialno } = tyreInfo || {};
          let returnState = tyre.state;
          if (index == tyreindexselected) {
            returnState = 'changes';
          }
          if (serialno && serialno == newserialnoSelected) {
            returnState = 'saved';
          }

          const findRevert = this.tyreConfigUpdateList.findLast(
            (item) => item.tyreindex == tyreindexselected && item.revert,
          );

          let returnTyreInfo = tyre.tyreInfo;
          if (findRevert) {
            returnTyreInfo = findRevert;
          }

          return { ...tyre, state: returnState, label: returnTyreInfo?.label, tyreInfo: returnTyreInfo };
        });

        this.tyreConfigSelectedVehicleLayout = this.tyreConfigSelectedVehicleLayout.map((item) => {
          if (item.state != 'saved' || this.viewMode || !this.tyreChangeAllowed) return { ...item, disabled: true };
          return item;
        });

        this.tyreConfigSelectedVehicleNo = this.jobInventoryValue?.['Inventory #'] ?? '';
      } else {
        this.tyreConfigSelectedVehicleLayout = null;
        this.tyreConfigSelectedVehicleNo = '';
      }

      if (replaceType == 'WHSE') {
        this.tyreConfigWarehouseSelected();
      }
    },

    async tyreConfigOtherVehicleSelected() {
      this.tyreConfigSelectedVehichleTyreConfig = [];
      this.tyreConfigSelectedVehichleTyreConfigFull = [];
      if (!this.tyreConfigOtherVehicleValue) {
        this.tyreConfigSelectedVehicleLayout = null;
        return;
      }

      this.tyreRotateOtherVehicleWorkshopJobValue = null;
      this.tyreRotateOtherVehicleWorkshopJobInputValue = '';

      const { 'Inventory #': InventoryNo } = this.tyreConfigOtherVehicleValue || {};

      await this.getTyreRotateOtherVehicleWorkshopJobs(InventoryNo);

      const findUpdate = this.tyreConfigUpdateList.find(
        (item) => cleanText(item.otherinventoryno) == cleanText(InventoryNo),
      );

      if (findUpdate) {
        const findJob = this.tyreRotateOtherVehicleWorkshopJobList.find(
          (item) => cleanText(item['Job #']) == cleanText(findUpdate.otherjobno),
        );
        if (findJob) this.tyreRotateOtherVehicleWorkshopJobValue = findJob;
        else if (findUpdate.otherjobno) {
          this.tyreRotateOtherVehicleWorkshopJobValue = {
            'Job #': findUpdate.otherjobno,
            label: findUpdate.otherjobno,
          };
        }
      }
      const findUpdateIfNew = this.tyreConfigUpdateList.find(
        (item) => cleanText(item.otherinventoryno) == cleanText(InventoryNo) && item.action != 'D',
      );
      if (findUpdateIfNew) {
        this.disableAttachJob = true;
      } else {
        this.disableAttachJob = false;
      }

      await this.getTyreConfigOtherVehicle();
    },

    async getTyreConfigOtherVehicle() {
      const { 'Inventory #': InventoryNo } = this.tyreConfigOtherVehicleValue || {};

      this.$fullscreenLoading('Getting Other Vehicle Tyre Layout...');
      const { TyreJob, VehicleCategory, Type } = await this.getVehicleInfo(InventoryNo);
      const tyreLayout = await this.getVehicleTyreLayout(VehicleCategory, Type);

      let tyreConfig = this.tyreConfigToStructure(TyreJob, tyreLayout);

      this.tyreConfigSelectedVehichleTyreConfigFull = tyreConfig;

      tyreConfig.map((tyre) => {
        const findUpdate = this.tyreConfigUpdateList.find(
          (item) =>
            cleanText(item.serialno) == cleanText(tyre.oldserialno) &&
            cleanText(item.oldserialno) == cleanText(tyre.serialno) &&
            cleanText(item.type) == 'OTHER',
        );
        if (findUpdate) {
          tyreConfig = tyreConfig.filter((item) => item.seqno != tyre.seqno);

          const findPrev = tyreConfig.findLast(
            (item) =>
              cleanText(item.serialno) == cleanText(findUpdate.serialno) &&
              cleanText(item.tyreindex) == cleanText(tyre.tyreindex) &&
              item.seqno != tyre.seqno,
          );
          tyreConfig = tyreConfig.map((item) => {
            if (findPrev && item.seqno == findPrev.seqno) {
              return {
                ...item,
                inuse: '1',
              };
            }
            return item;
          });
        }
      });

      this.tyreConfigUpdateOtherVehicle.map((item) => {
        if (item.revert && cleanText(item.inventoryno) == cleanText(InventoryNo)) {
          tyreConfig = tyreConfig.map((tyre) => {
            if (tyre.seqno == item.seqno) {
              return {
                ...tyre,
                inuse: item.inuse,
              };
            }
            return tyre;
          });
        }
      });

      const { 'New Serial No': newserialnoSelected } = this.tyreConfigTableSelectedRow?.[0] || {};

      this.tyreConfigSelectedVehichleTyreConfig = tyreConfig;

      let returnLayout = [
        ...tyreLayout,
        { index: '100', description: 'Spare1' },
        { index: '101', description: 'Spare2' },
        { index: '102', description: 'Spare3' },
        { index: '103', description: 'Spare4' },
      ].map((layout) => {
        const found = tyreConfig.find(
          (item) => layout.index == item.tyreindex && (item.inuse == '1' || item.inuse == '2'),
        );
        const editsFound = this.tyreConfigUpdateList.find(
          (item) =>
            cleanText(item.serialno) == cleanText(found?.serialno) &&
            cleanText(item.serialno) != cleanText(this.tyreConfigTableSelectedRow?.[0]?.['New Serial No']) &&
            item.inuse == '1' &&
            item.action != 'D',
        );

        const changesInLatest =
          found &&
          this.tyreRotateOtherVehicleWorkshopJobValue &&
          cleanText(this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #']) == cleanText(found.jobno);

        const state = (() => {
          if (editsFound || changesInLatest) return 'changes';
          if (found) return 'saved';
          return 'normal';
        })();

        return {
          ...layout,
          state: state,
          tyreInfo: found,
          label: found ? `${layout['description']} - ${found.serialno}` : '',
        };
      });

      returnLayout = returnLayout.map((item) => {
        const findTaken = this.tyreConfigUpdateList?.find(
          (update) =>
            cleanText(update.serialno) == cleanText(item.tyreInfo?.serialno) &&
            cleanText(update.serialno) != cleanText(newserialnoSelected) &&
            update.action != 'D',
        );
        if (findTaken) {
          return {
            ...item,
            state: 'changes',
          };
        }
        return item;
      });

      if (this.tyreConfigRotateOtherDisabled || this.tyreConfigRotateOtherWorkshopJobIsNotLatest) {
        returnLayout = returnLayout.map((item) => {
          if (newserialnoSelected && cleanText(item.tyreInfo?.serialno) == cleanText(newserialnoSelected))
            return {
              ...item,
              state: 'saved',
            };
          return {
            ...item,
            state: 'normal',
          };
        });
      }

      this.tyreConfigSelectedVehicleLayout = returnLayout.map((item) => {
        const { state, tyreInfo } = item || {};
        return { ...item, disabled: state == 'changes' || !tyreInfo || this.tyreConfigRotateOtherDisabled };
      });
      this.tyreConfigSelectedVehicleNo = InventoryNo ?? '';

      this.$fullscreenLoading();
    },

    tyreConfigRotateClicked() {
      if (!this.tyreConfigRotateSelectedTyre) {
        this.clearTyreConfigFields();
        this.tyreConfigSelectedVehicleDropdownValue = null;
        return;
      }
      const { index } = this.tyreConfigRotateSelectedTyre;
      this.tyreConfigSelectedVehicleDropdownValue =
        this.tyreConfigSelectedVehicleDropdownList.find((item) => item.index == index) || null;
      const tyreInfo = this.tyreConfigSelectedVehicleDropdownValue?.tyreInfo || {};
      const { seqno } = tyreInfo;
      const partInfo = this.partTableData.find((item) => item.tyreSeqNo == seqno) || {};
      this.populateTyreConfigFields(tyreInfo, partInfo, false);
    },

    tyreConfigSelectedVehicleDropdownSelected(e) {
      this.tyreConfigRotateSelectedTyre = e ?? null;
      this.tyreConfigRotateClicked();
    },

    tyreConfigWarehouseSelected() {
      const {
        'Serial No': SerialNo,
        Warehouse,
        Model,
        'Part Type': PartType,
        ID: ProdTxnID,
        MadeIn,
        Manufacturer,
        ProdDate,
        PurcDate,
        UnitPrice,
        Supplier: SppCode,
        PONo,
        'Part No': PartNo,
      } = this.tyreExistingValue || {};

      let findTyreType = this.tyreTypeList.find((item) => item.ProdTxnID == ProdTxnID) || {};
      if (!findTyreType) findTyreType = this.tyreTypeList.find((item) => item['Part Type'] == PartType) || {};
      const { Size } = findTyreType;

      this.tyreConfigTyreTypeValue = findTyreType;
      this.tyreConfigSerialNoValue = SerialNo ?? '';
      this.tyreConfigPartNoValue = PartNo ?? '';
      this.tyreConfigSizeValue = Size?.toString() ?? '';
      const findWarehouse = this.warehouseList.find((item) => item['Warehouse Code'] == Warehouse) ?? null;
      if (findWarehouse) this.tyreConfigTakenFromValue = findWarehouse;
      else this.tyreConfigTakenFromInputValue = Warehouse ?? '';
      this.tyreConfigUnitPriceValue = UnitPrice?.toString() ?? '';
      this.tyreConfigModelValue = Model ?? '';
      this.tyreConfigMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) ?? null;
      this.tyreConfigManufacturerValue = this.manufacturerList.find((item) => item.MFGCode == Manufacturer) ?? null;
      this.tyreConfigSupplierValue = this.supplierList.find((item) => item['Supplier Code'] == SppCode) ?? null;
      this.tyreConfigPONoValue = PONo ?? '';
      this.tyreConfigMfgDateValue = ProdDate ? new Date(ProdDate) : null;
      this.tyreConfigDatePurchaseValue = PurcDate ? new Date(PurcDate) : null;

      this.tyreConfigJobPriceValue = UnitPrice?.toString() ?? '';
    },

    clearTyreConfigPopup() {
      this.tyreStatusValue = null;
      this.tyreStoreInValue = null;
      this.tyreReplaceValue = null;
      this.tyreExistingValue = null;
      this.tyreConfigOtherVehicleValue = null;
      this.tyreRotateOtherVehicleWorkshopJobs = null;

      this.tyreConfigSelectedVehicleLayout = null;
      this.tyreConfigSelectedVehicleNo = '';

      this.tyreRotateOtherVehicleWorkshopJobInputValue = '';
      this.tyreConfigPositionEditAllowed = false;
      this.tyreConfigPositionEditChecked = false;
      this.tyreConfigPositionEditMessage = '';

      this.clearTyreConfigFields();
      this.clearTyreConfigPartFields();
    },

    clearTyreConfigFields() {
      this.tyreConfigTyreTypeDisabled = true;

      this.tyreRotateOtherVehicleWorkshopJobValue = null;
      this.tyreRotateOtherVehicleWorkshopJobInputValue = '';
      this.disableAttachJob = false;

      this.clearTyreConfigFieldsOnly();
    },

    clearTyreConfigFieldsOnly() {
      this.tyreConfigRotateSelectedTyre = null;
      this.tyreConfigSelectedVehicleDropdownValue = null;

      this.tyreConfigTyreTypeValue = null;
      this.tyreConfigSerialNoValue = '';
      this.tyreConfigPartNoValue = '';
      this.tyreConfigSizeValue = '';
      this.tyreConfigModelValue = '';
      this.tyreConfigTakenFromValue = null;
      this.tyreConfigTakenFromInputValue = '';
      this.tyreConfigUnitPriceValue = '';
      this.tyreConfigModelValue = '';
      this.tyreConfigMadeInValue = null;
      this.tyreConfigManufacturerValue = null;
      this.tyreConfigSupplierValue = null;
      this.tyreConfigPONoValue = '';
      this.tyreConfigMfgDateValue = null;
      this.tyreConfigDatePurchaseValue = null;
    },

    clearTyreConfigPartFields() {
      this.tyreConfigOldSerialNoValue = '';
      this.tyreConfigJobPriceValue = '';
      this.tyreConfigInvoiceNoValue = null;
      this.tyreConfigInsuranceNoValue = null;
      this.tyreConfigRemarksValue = null;
    },

    async tyreConfigDeleteClicked() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Tyre Change?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;

      let updateTyreUpdateList = this.tyreConfigUpdateList;
      let updateTyreConfigList = this.tyreConfigTyresList;
      const {
        'Replace Type': type,
        seqno: selectedSeqNo,
        action,
        'Old Serial No': oldserialno,
        'New Serial No': newserialno,
        rotatedwith,
        otherjobno,
      } = this.tyreConfigTableSelectedRow?.[0] || {};
      const seqno = parseInt(selectedSeqNo);
      if (action == 'N') {
        updateTyreUpdateList = updateTyreUpdateList.filter((item) => item.seqno != seqno);
        if (oldserialno) {
          updateTyreUpdateList = updateTyreUpdateList.filter(
            (item) => !(item.serialno == oldserialno && item.prevupdate == true),
          );
        }
        //ROTATE to empty position - disabled
        // else if (type == 'ROTATE' && !oldserialno) {
        //   updateTyreUpdateList = updateTyreUpdateList.filter(
        //     (item) => !(item.serialno == newserialno && item.prevupdate == true),
        //   );
        // }
        this.partTableData = this.partTableData.filter((item) => item.tyreSeqNo != seqno);
      } else {
        updateTyreUpdateList = updateTyreUpdateList.map((item) => {
          if (item.seqno == seqno)
            return {
              ...item,
              action: 'D',
            };
          return item;
        });

        const findPrevious = this.tyreConfigTyresList.findLast((item) => item.serialno == oldserialno);

        if (findPrevious) {
          const findFromUpdateList = this.tyreConfigTyresList?.findLast(
            (u) => u.tyreindex == findPrevious.tyreindex && u.inuse == '5',
          );

          updateTyreUpdateList.push({
            ...findPrevious,
            inuse: '1',
            action: 'E',
            revert: true,
            revertgrn: Boolean(findFromUpdateList),
          });
          updateTyreConfigList = updateTyreConfigList.map((item) => {
            if (item.seqno == findPrevious.seqno)
              return {
                ...item,
                inuse: '1',
                action: 'E',
              };
            return item;
          });
        }

        this.partTableData = this.partTableData.map((item) => {
          if (item.tyreSeqNo == seqno)
            return {
              ...item,
              action: 'D',
            };
          return item;
        });
      }

      if (type == 'ROTATE' && oldserialno) {
        if (action == 'N') {
          updateTyreUpdateList = updateTyreUpdateList.filter((item) => item.seqno != seqno + 1);
          this.partTableData = this.partTableData.filter((item) => item.tyreSeqNo != seqno + 1);
          updateTyreUpdateList = updateTyreUpdateList.filter(
            (item) => !(item.serialno == newserialno && item.prevupdate == true),
          );
        } else {
          updateTyreUpdateList = updateTyreUpdateList.map((item) => {
            if (item.seqno == seqno + 1)
              return {
                ...item,
                action: 'D',
              };
            return item;
          });

          const findPrevious = this.tyreConfigTyresList.findLast((item) => item.serialno == newserialno);
          if (findPrevious) {
            updateTyreUpdateList = [
              ...updateTyreUpdateList,
              { ...findPrevious, inuse: '1', action: 'E', revert: true },
            ];
            updateTyreConfigList = updateTyreConfigList.map((item) => {
              if (item.seqno == findPrevious.seqno)
                return {
                  ...item,
                  inuse: '1',
                  action: 'E',
                  revert: true,
                };
              return item;
            });
          }
          this.partTableData = this.partTableData.map((item) => {
            if (item.tyreSeqNo == seqno + 1)
              return {
                ...item,
                action: 'D',
              };
            return item;
          });
        }
      }

      if (type == 'OTHER') {
        if (action == 'N') {
          this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.filter(
            (tyre) => !(tyre.serialno == oldserialno && tyre.oldserialno == newserialno),
          );
          this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.filter(
            (tyre) => !(tyre.serialno == newserialno && tyre.action == 'E'),
          );
        } else {
          this.$fullscreenLoading('Deleting...');
          const vehicle = await this.getVehicleInfo(rotatedwith);
          const { TyreJob, VehicleCategory, Type } = vehicle || {};
          const tyreLayout = await this.getVehicleTyreLayout(VehicleCategory, Type);
          const tyreConfigList = this.tyreConfigToStructure(TyreJob, tyreLayout);

          const findUpdate = tyreConfigList.findLast(
            (item) => item.serialno == oldserialno && item.oldserialno == newserialno && type == 'OTHER',
          );

          this.tyreConfigUpdateOtherVehicle.push({ ...findUpdate, action: 'D' });

          const findPrev = tyreConfigList.findLast(
            (item) => item.serialno == findUpdate.oldserialno && item.tyreindex == findUpdate.tyreindex,
          );
          this.tyreConfigUpdateOtherVehicle.push({ ...findPrev, action: 'E', inuse: '1', revert: true });

          const otherJob = await this.getWorkshopJobDetails(otherjobno);
          const { Parts } = otherJob || {};
          const parts = this.convertToArray(Parts);
          const findPart = parts.find(
            (part) => part['Serial No'] == findUpdate.serialno && part.Replace == findUpdate.oldserialno,
          );
          if (findPart) {
            this.partUpdateOtherVehicle.push({
              ...findPart,
              partinventoryno: rotatedwith,
              jobno: findPart.JobNo,
              action: 'D',
            });
          }

          this.$fullscreenLoading();
        }
      }

      this.tyreConfigUpdateList = [];
      this.tyreConfigTyresList = [];
      this.$nextTick(() => {
        this.tyreConfigUpdateList = updateTyreUpdateList;
        this.tyreConfigTyresList = updateTyreConfigList;
      });

      this.tyreConfigLayoutSelectedItem = null;
      this.tyreConfigTableSelectedRow = [];
      this.tyrePositionSelectedValue = null;
    },

    async tyreConfigPopupSave() {
      if (this.tyreConfigPartInfoOnlyEdit) {
        this.saveTyreConfigJobPartInfoOnly();
        return;
      }

      let error = false;
      if (!this.tyreStatusValue) {
        this.tyreStatusValueError = 'Current Tyre Status is required!';
        error = true;
      }
      if (this.tyreStatusValue.keyValue == '5' && !this.tyreStoreInValue) {
        this.tyreStoreInValueError = 'Warehouse is required!';
        error = true;
      }

      if (!this.tyreReplaceValue) {
        this.tyreReplaceValueError = 'Replace With is requried!';
        error = true;
      } else {
        const { keyValue: replace } = this.tyreReplaceValue || {};

        if (!this.tyreExistingValue && replace == 'WHSE') {
          this.tyreExistingValueError = 'Tyre is required!';
          error = true;
        }
        if (!this.tyreConfigSelectedVehicleDropdownValue && replace == 'ROTATE') {
          this.tyreConfigErrorText = 'Selected Tyre is required!';
          error = true;
        }

        if (replace == 'OTHER') {
          if (!this.tyreConfigOtherVehicleValue) {
            this.tyreConfigOtherVehicleValueError = 'Other Vehicle is required!';
            error = true;
          }

          if (!this.tyreConfigSelectedVehicleDropdownValue) {
            this.tyreConfigErrorText = 'Selected Tyre is required!';
            error = true;
          }

          if (!this.tyreRotateOtherVehicleWorkshopJobInputValue || !this.tyreRotateOtherVehicleWorkshopJobValue) {
            this.tyreRotateOtherVehicleWorkshopJobValueError = 'Workshop Job No. is required!';
            error = true;
          }
        }

        if (replace == 'NEW') {
          if (!this.tyreConfigTyreTypeValue) {
            this.tyreConfigTyreTypeValueError = 'Tyre Type is requried!';
            error = true;
          }

          if (!this.tyreConfigSerialNoValue) {
            this.tyreConfigSerialNoValueError = 'Serial No is required!';
            error = true;
          } else if (this.tyreConfigSerialNoValue) {
            const newserialno = this.tyreConfigTableSelectedRow?.[0]?.['New Serial No'] ?? '';
            const noChange = newserialno == this.tyreConfigSerialNoValue;

            error = await (async () => {
              if (newserialno && noChange) {
                return false;
              }

              const findExist = this.tyreConfigUpdateList.find(
                (item) => item.serialno == this.tyreConfigSerialNoValue && item.serialno != newserialno,
              );
              if (findExist) return true;

              this.$fullscreenLoading('Checking Serial No...');
              const checkDB = await this.$apiCallV2('/WorkshopJob/CheckTyreSerialNoValid', {
                serialno: this.tyreConfigSerialNoValue,
              });
              this.$fullscreenLoading();
              if (checkDB[0].Results != 0) return true;

              return false;
            })();

            if (error) {
              this.tyreConfigSerialNoValueError = 'New Tyre Serial No already exists!';
            }
          }
        }

        if (!error) {
          if (!this.tyreConfigTyreTypeValue) {
            this.tyreConfigTyreTypeValueError = 'Tyre Type is requried!';
            error = true;
          }

          if (!this.tyreConfigSerialNoValue) {
            this.tyreConfigSerialNoValueError = 'Serial No is required!';
            error = true;
          }
        }
      }

      if (error) return;

      let updateTyres = this.tyreConfigUpdateList || [];
      let updateParts = this.partTableData || [];

      const {
        seqno: existSeqno,
        'Old Serial No': oldserialnoTableSelected,
        'New Serial No': newserialnoTableSelected,
        'Date Changed': datechangedSelected,
        tyreindex: tyreIndexTableSelected,
      } = this.tyreConfigTableSelectedRow[0] || {};

      //clean up for previous entry
      if (existSeqno) {
        const findUpdateItem = this.tyreConfigUpdateList.find((item) => item.seqno == existSeqno);
        const { type, serialno, oldserialno } = findUpdateItem || {};
        if (this.tyreReplaceValue.keyValue != type) {
          if (type == 'ROTATE') {
            const findPair =
              updateTyres.find(
                (item) => item.serialno == oldserialno && item.oldserialno == serialno && item.type == 'ROTATE',
              ) || {};
            if (findPair.tyreguid) {
              updateTyres = updateTyres.map((item) => {
                if (item.seqno == findPair.seqno) {
                  return {
                    ...item,
                    action: 'D',
                  };
                }
              });

              const findLast = this.tyreConfigTyresList.findLast(
                (item) => item.serialno == findPair.oldserialno && item.inuse != '1',
              );
              updateTyres.push({ ...findLast, action: 'E', inuse: '1' });
            } else {
              updateTyres = updateTyres.filter(
                (item) => item.seqno != findPair.seqno && !(item.serialno == serialno && item.prevupdate),
              );
            }

            const findPart = updateParts.find((item) => findPair.seqno == item.tyreSeqNo);

            if (findPart) {
              if (findPart.action == 'N') {
                updateParts = updateParts.filter((item) => item.tyreSeqNo != findPart.tyreSeqNo);
              } else
                updateParts = updateParts.map((item) => {
                  if (item.tyreSeqNo == findPart.tyreSeqNo) {
                    return {
                      ...item,
                      action: 'D',
                    };
                  }
                  return item;
                });
            }
          }
        }
      }

      // end cleanup

      let selectedTyrePosition = this.tyrePositionSelectedValue || {};
      if (tyreIndexTableSelected) {
        selectedTyrePosition = this.tyreConfigLayout.find((item) => item.index == tyreIndexTableSelected);
      }
      const { tyreInfo, index: tyreIndex, description: positionDescription } = selectedTyrePosition || {};
      const { action: selectedTyreAction, tyretype: selectedPartType } = tyreInfo || {};
      const action = this.tyreConfigTableSelectedRow[0] && this.tyreConfigTableSelectedRow[0].action != 'N' ? 'E' : 'N';

      const { keyValue: inUse } = this.tyreStatusValue;

      //Tyre to add
      const { keyValue: replaceTypeKey } = this.tyreReplaceValue;
      const { align, tyrePosition, rowNo: rowno, columnNo: columnno } = this.tyrePositionInfo(tyreIndex);
      const { 'Warehouse Code': storeInWarehouse } = this.tyreStoreInValue || {};
      const { 'Inventory #': inventoryNo } = this.jobInventoryValue;
      const tyreVehicleMaxSeqNo = Math.max(0, ...this.tyreConfigTyresList.map((item) => Number(item.seqno) || 0)) + 1;
      const tyreUpdatemaxSeqno = Math.max(0, ...this.tyreConfigUpdateList.map((item) => Number(item.seqno) || 0)) + 1;
      const tyreMaxSeqNo = Math.max(tyreUpdatemaxSeqno, tyreVehicleMaxSeqNo);

      const { 'Part Type': partType, ProdTxnID } = this.tyreConfigTyreTypeValue || {};

      const tyreConfigToAdd = {
        type: replaceTypeKey,
        serialno: this.tyreConfigSerialNoValue ?? '',
        oldserialno: this.tyreConfigOldSerialNoValue ?? '',
        tyreposition: positionDescription || tyrePosition,
        storein: inUse == '5' ? storeInWarehouse : '',
        inventoryno: inventoryNo,
        tyresize: this.tyreConfigSizeValue ?? '',
        tyreindex: tyreIndex,
        repair: 'Y',
        inuse: '1',
        datechange: datechangedSelected ?? this.jobDateDate,
        align,
        rowno,
        columnno,
        tyretype: partType ?? '',
        prodtxnid: ProdTxnID ?? '',
        action: selectedTyreAction == 'N' ? 'N' : action,

        partno: this.tyreConfigPartNoValue ?? '',
        mfgdate: this.tyreConfigMfgDateValue?.formatString('yyyy-MM-dd') ?? null,
        datepurchase: this.tyreConfigDatePurchaseValue?.formatString('yyyy-MM-ddTHH:mm:ss') ?? null,
        noofretread: this.tyreExistingValue?.NoOfRetread || '0',
        whsecode: this.tyreConfigTakenFromValue?.['Warehouse Code'] ?? this.tyreConfigTakenFromInputValue ?? '',
        model: this.tyreConfigModelValue ?? '',
        madein: this.tyreConfigMadeInValue?.['Country Code'] ?? '',
        manufacturer: this.tyreConfigManufacturerValue?.MFGCode ?? '',
        unitprice: this.tyreConfigJobPriceValue ?? '0',
        sppcode: this.tyreConfigSupplierValue?.['Supplier Code'] ?? '',

        otherjobno:
          replaceTypeKey == 'OTHER' ?
            this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #'] || this.tyreRotateOtherVehicleWorkshopJobInputValue
          : '',
        otherinventoryno: replaceTypeKey == 'OTHER' ? this.tyreConfigOtherVehicleValue['Inventory #'] : '',

        jobno: this.jobNoValue,
      };

      if (!this.tyreConfigRotateOtherDisabled && !this.tyreConfigRotateOtherWorkshopJobIsNotLatest) {
        if (existSeqno) {
          updateTyres = updateTyres?.map((item) => {
            if (item?.seqno == existSeqno) {
              return {
                ...item,
                ...tyreConfigToAdd,
                action: action == 'N' ? item.action : 'E',
              };
            }
            return item;
          });
        } else updateTyres.push({ ...tyreConfigToAdd, seqno: tyreMaxSeqNo });

        //Update previous tyre
        if (tyreInfo) {
          updateTyres = updateTyres.filter((item) => !(item.serialno == oldserialnoTableSelected && item.prevupdate));
          const {
            align: alignPrevTyre,
            tyrePosition: tyrePositionPrevTyre,
            rowNo: rownoPrevTyre,
            columnNo: columnnoPrevTyre,
          } = this.tyrePositionInfo(tyreInfo.tyreindex);

          const findFromUpdateList = this.tyreConfigTyresList?.findLast(
            (u) => u.tyreindex == tyreInfo.tyreindex && u.inuse == '5',
          );

          const { ProdTxnID: TyreTypeProdTxnID, tyreposition } = tyreInfo || {};

          let tyreTypeFind = null;
          if (TyreTypeProdTxnID) {
            tyreTypeFind = this.tyreTypeList.find((item) => item.ProdTxnID == TyreTypeProdTxnID);
          } else {
            tyreTypeFind = this.tyreTypeList.find((item) => item['Product Code'] == selectedPartType);
          }

          updateTyres.push({
            ...tyreInfo,
            align: alignPrevTyre,
            tyreposition: positionDescription || tyreposition || tyrePositionPrevTyre,
            rowno: rownoPrevTyre,
            columnno: columnnoPrevTyre,
            inuse: inUse,
            action: 'E',
            tyretype: tyreTypeFind?.['Product Code'] ?? partType,
            prodtxnid: tyreTypeFind?.ProdTxnID ?? ProdTxnID,
            prevupdate: true,
            revertgrn: Boolean(findFromUpdateList),
            storein: inUse == '5' ? storeInWarehouse : '',
          });
        }
      }

      // DISABLED - this was to enable taking tyres from the same vehicle to empty slots
      // A bit too buggy to enable for. Will explore when needed
      // The bugs are related to 'Old Serial No' not being present when you try to edit.
      // Some of the code that relies on old serial no will break, so need special cases for it

      // else if (replaceTypeKey == 'ROTATE' && !tyreInfo) {
      //   if (action == 'N') {
      //     updateTyres = updateTyres.filter(
      //       (item) => item.serialno != this.tyreConfigTableSelectedRow?.[0]?.['New Serial No'],
      //     );
      //   } else if (action == 'E') {
      //     if (
      //       this.tyreConfigSelectedVehicleDropdownValue.tyreInfo?.serialno !=
      //       this.tyreConfigTableSelectedRow?.[0]?.['New Serial No']
      //     ) {
      //       const findPrev = this.tyreConfigTyresList.findLast(
      //         (item) => item.serialno == this.tyreConfigTableSelectedRow?.[0]?.['New Serial No'],
      //       );
      //       updateTyres.push({
      //         ...findPrev,
      //         inuse: '1',
      //         action: 'E',
      //       });
      //     }
      //   }

      //   updateTyres.push({
      //     ...this.tyreConfigSelectedVehicleDropdownValue?.tyreInfo,
      //     inuse: inUse,
      //     action: 'E',
      //     prevupdate: true,
      //   });
      // }

      const partMaxSeqNo = Math.max(0, ...updateParts.map((item) => Number(item.seqNo) || 0)) + 1;

      const partToAdd = {
        Type: this.tyreConfigTyreTypeValue?.['Part Type'] ?? '',
        'Serial No': this.tyreConfigSerialNoValue ?? '',
        'Part No': this.tyreConfigPartNoValue ?? '',
        Manufacturer: this.tyreConfigManufacturerValue?.MFGCode ?? '',
        'Made In': this.tyreConfigMadeInValue?.['Country Code'] ?? '',
        Size: this.tyreConfigSizeValue ?? '',
        'Cost (RM)': this.tyreConfigJobPriceValue ?? '0',
        Replace: this.tyreConfigOldSerialNoValue ?? '',
        'Purchase From': this.tyreConfigSupplierValue?.['Supplier Code'] ?? '',
        'Date Purchase':
          this.tyreConfigDatePurchaseValue ?
            new Date(this.tyreConfigDatePurchaseValue.formatString('yyyy-MM-ddTHH:mm:ss'))
          : null,
        'Insurance Claim No': this.tyreConfigInsuranceNoValue ?? '',
        'Invoice No': this.tyreConfigInvoiceNoValue ?? '',
        'Taken From': this.tyreConfigTakenFromValue?.['Warehouse Code'] ?? this.tyreConfigTakenFromInputValue ?? '',
        Remark: this.tyreConfigRemarksValue,
        PONo: this.tyreConfigPONoValue ?? '',
        UserID: '',
        'Date Updated': '',
        MfgDate: this.tyreConfigMfgDateValue ? new Date(this.tyreConfigMfgDateValue).formatString('yyyy-MM-dd') : null,
        Model: this.tyreConfigModelValue ?? '',
        Quantity: '1',

        partTypeDesc: this.tyreConfigTyreTypeValue?.Description ?? '',

        action: action,
        expenseCode: this.tyreConfigTyreTypeValue?.['Expense Code'] || 'EMS',
        isExpense:
          replaceTypeKey == 'ROTATE' || replaceTypeKey == 'OTHER' ?
            'N'
          : this.tyreConfigTyreTypeValue?.isExpense ?? this.tyreConfigTyreTypeValue?.IsExpenses,

        prodTxnID: this.tyreConfigTyreTypeValue?.ProdTxnID,
        AllocGUID: this.tyreExistingValue?.AllocGUID ?? '',
        BinLoc: this.tyreExistingValue?.BinLoc ?? '',

        TyreIndex: tyreIndex,
      };

      if (existSeqno) {
        updateParts = updateParts.map((item) => {
          if (item.tyreSeqNo == existSeqno) {
            return {
              ...item,
              ...partToAdd,
            };
          }
          return item;
        });
      } else updateParts.push({ ...partToAdd, tyreSeqNo: tyreMaxSeqNo, seqNo: partMaxSeqNo });

      //tyreRotate
      if (replaceTypeKey == 'ROTATE' && tyreInfo) {
        const { tyreInfo: tyreInfoRotatedPair, description: tyreRotatePositionDescription } =
          this.tyreConfigSelectedVehicleDropdownValue || {};
        const {
          tyreposition,
          tyreindex,
          tyresize,
          prodtxnid,
          partno,
          mfgdate,
          datepurchase,
          noofretread,
          whsecode,
          tyretype,
          model,
          madein,
          manufacturer,
          unitprice,
          supplier,
        } = tyreInfoRotatedPair || {};
        const {
          align: alignRotate,
          tyrePosition: tyrePositionRotate,
          rowNo: rowNoRotate,
          columnNo: columnNoRotate,
        } = this.tyrePositionInfo(tyreindex);
        const tyreTypeFind = this.tyreTypeList.find((item) => item['Product Code'] == tyretype);

        const tyreConfigRotated = {
          type: 'ROTATE',
          serialno: this.tyreConfigOldSerialNoValue ?? '',
          oldserialno: this.tyreConfigSerialNoValue ?? '',
          tyreposition: tyreRotatePositionDescription || tyrePositionRotate || tyreposition,
          storein: '',
          inventoryno: inventoryNo,
          tyresize: tyresize ?? this.tyreConfigSizeValue ?? '',
          tyreindex: tyreindex,
          repair: 'Y',
          inuse: '1',
          datechange: datechangedSelected ?? this.jobDateDate,
          align: alignRotate,
          rowno: rowNoRotate,
          columnno: columnNoRotate,
          tyretype: tyreTypeFind ? tyreTypeFind['Product Code'] : partType,
          prodtxnid: prodtxnid || (tyreTypeFind ? tyreTypeFind.ProdTxnID : ProdTxnID),
          action: selectedTyreAction == 'N' ? 'N' : action,

          partno: partno ?? '',
          mfgdate: mfgdate ? new Date(mfgdate) : null,
          datepurchase: datepurchase ? new Date(datepurchase) : null,
          noofretread: noofretread || '0',
          whsecode: whsecode ?? '',
          model: model ?? '',
          madein: madein ?? '',
          manufacturer: manufacturer ?? '',
          unitprice: unitprice ?? '0',
          sppcode: supplier ?? '',

          jobno: this.jobNoValue,
        };

        const seqNoRotate = parseInt(existSeqno ?? tyreMaxSeqNo) + 1;

        if (!this.tyreConfigRotateOtherDisabled && !this.tyreConfigRotateOtherWorkshopJobIsNotLatest) {
          if (this.tyreConfigUpdateList.find((item) => item.seqno == seqNoRotate)) {
            updateTyres = updateTyres.map((item) => {
              if (item.seqno == seqNoRotate) {
                return { ...item, ...tyreConfigRotated, seqno: seqNoRotate };
              }
              return item;
            });
          } else {
            updateTyres.push({ ...tyreConfigRotated, seqno: seqNoRotate });
          }

          if (action == 'N') {
            //prev tyre clear
            updateTyres = updateTyres.filter((item) => !(item.serialno == newserialnoTableSelected && item.prevupdate));

            const {
              align: alignPrevTyre,
              tyrePosition: tyrePositionPrevTyre,
              rowNo: rownoPrevTyre,
              columnNo: columnnoPrevTyre,
            } = this.tyrePositionInfo(tyreInfoRotatedPair.tyreindex);

            //prev Tyre add
            updateTyres.push({
              ...tyreInfoRotatedPair,
              align: alignPrevTyre,
              tyreposition: tyreRotatePositionDescription || tyrePositionPrevTyre,
              rowno: rownoPrevTyre,
              columnno: columnnoPrevTyre,
              action: 'E',
              inuse: '0',
              prevupdate: true,
            });
          } else if (action == 'E') {
            if (newserialnoTableSelected != this.tyreConfigSerialNoValue) {
              const findPrev = this.tyreConfigTyresList.findLast((item) => item.serialno == newserialnoTableSelected);
              if (findPrev) {
                const {
                  align: alignPrevTyre,
                  tyrePosition: tyrePositionPrevTyre,
                  rowNo: rownoPrevTyre,
                  columnNo: columnnoPrevTyre,
                } = this.tyrePositionInfo(findPrev.tyreindex);

                updateTyres.push({
                  ...findPrev,
                  align: alignPrevTyre,
                  tyreposition: tyreRotatePositionDescription || tyrePositionPrevTyre,
                  rowno: rownoPrevTyre,
                  columnno: columnnoPrevTyre,
                  action: 'E',
                  inuse: '1',
                  prevupdate: true,
                  revert: true,
                });
              }

              const {
                align: alignPrevTyre,
                tyrePosition: tyrePositionPrevTyre,
                rowNo: rownoPrevTyre,
                columnNo: columnnoPrevTyre,
              } = this.tyrePositionInfo(tyreInfoRotatedPair.tyreindex);

              //prev Tyre add
              updateTyres.push({
                ...tyreInfoRotatedPair,
                align: alignPrevTyre,
                tyreposition: tyrePositionPrevTyre,
                rowno: rownoPrevTyre,
                columnno: columnnoPrevTyre,
                action: 'E',
                inuse: '0',
                prevupdate: true,
              });
            }
          }
        }

        const partRotateToAdd = {
          Type: this.tyreConfigTyreTypeValue?.['Part Type'] ?? '',
          'Serial No': this.tyreConfigOldSerialNoValue ?? '',
          'Part No': partno ?? '',
          Manufacturer: manufacturer ?? '',
          'Made In': madein ?? '',
          Size: tyresize ?? this.tyreConfigSizeValue ?? '',
          'Cost (RM)': unitprice ?? '0',
          Replace: this.tyreConfigSerialNoValue ?? '',
          'Purchase From': supplier ?? '',
          'Date Purchase': datepurchase ? new Date(datepurchase).formatString('yyyy-MM-ddTHH:mm:ss') : null,
          'Insurance Claim No': this.tyreConfigInsuranceNoValue ?? '',
          'Invoice No': this.tyreConfigInvoiceNoValue ?? '',
          'Taken From': whsecode ?? '',
          Remark: this.tyreConfigRemarksValue,
          PONo: this.tyreConfigPONoValue ?? '',
          UserID: '',
          'Date Updated': '',
          MfgDate: mfgdate ? new Date(mfgdate).formatString('yyyy-MM-dd') : null,
          Model: model ?? '',
          Quantity: '1',

          partTypeDesc: this.tyreConfigTyreTypeValue?.Description ?? '',

          action: action,
          expenseCode: this.tyreConfigTyreTypeValue?.['Expense Code'] || 'EMS',
          isExpense: 'N',

          prodTxnID: this.tyreConfigTyreTypeValue?.ProdTxnID,
          TyreIndex: tyreindex,
        };

        if (existSeqno) {
          updateParts = updateParts.map((item) => {
            if (item.tyreSeqNo == seqNoRotate) {
              return {
                ...item,
                ...partRotateToAdd,
              };
            }
            return item;
          });
        } else updateParts.push({ ...partRotateToAdd, tyreSeqNo: seqNoRotate, seqNo: partMaxSeqNo + 1 });
      }

      if (replaceTypeKey == 'OTHER') {
        const { tyreInfo: tyreInfoRotatedPair } = this.tyreConfigSelectedVehicleDropdownValue || {};
        const {
          tyreposition,
          tyreindex,
          tyresize,
          prodtxnid,
          partno,
          mfgdate,
          datepurchase,
          noofretread,
          whsecode,
          tyretype,
          model,
          madein,
          manufacturer,
          unitprice,
          supplier,
          inventoryno,
        } = tyreInfoRotatedPair || {};
        const {
          align: alignRotate,
          tyrePosition: tyrePositionRotate,
          rowNo: rowNoRotate,
          columnNo: columnNoRotate,
        } = this.tyrePositionInfo(tyreindex);
        const tyreConfigToAdd = {
          type: 'OTHER',
          serialno: this.tyreConfigOldSerialNoValue ?? '',
          oldserialno: this.tyreConfigSerialNoValue ?? '',
          tyreposition: tyrePositionRotate || tyreposition,
          storein: '',
          inventoryno: inventoryno,
          tyresize: tyresize ?? this.tyreConfigSizeValue ?? '',
          tyreindex: tyreindex,
          repair: 'Y',
          inuse: '1',
          datechange: datechangedSelected ?? this.jobDateDate,
          align: alignRotate,
          rowno: rowNoRotate,
          columnno: columnNoRotate,
          tyretype: tyretype ?? partType ?? '',
          prodtxnid: prodtxnid ?? ProdTxnID ?? '',
          action: selectedTyreAction == 'N' ? 'N' : action,

          partno: partno ?? '',
          mfgdate: mfgdate ? new Date(mfgdate).formatString('yyyy-MM-dd') : null,
          datepurchase: datepurchase ? new Date(datepurchase).formatString('yyyy-MM-ddTHH:mm:ss') : null,
          noofretread: noofretread || '0',
          whsecode: whsecode ?? '',
          model: model ?? '',
          madein: madein ?? '',
          manufacturer: manufacturer ?? '',
          unitprice: unitprice ?? '0',
          sppcode: supplier ?? '',

          jobno: this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #'] ?? '',
        };

        const targetJobNo = this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #'] ?? '';
        const findMaxSeqNoOther = Math.max(
          ...this.tyreConfigUpdateOtherVehicle
            .filter((item) => item.jobno == targetJobNo)
            .map((item) => item.seqno ?? 0),
        );
        const maxSeqNoFromExisting = Math.max(
          ...this.tyreConfigSelectedVehichleTyreConfigFull.map((x) => x.seqno ?? 0),
        );

        const tyreMaxSeqNoOther = Math.max(findMaxSeqNoOther + 1, maxSeqNoFromExisting + 1);

        if (action == 'N') {
          const findRotated = this.tyreConfigUpdateOtherVehicle.find(
            (item) => item.serialno == this.tyreConfigOldSerialNoValue && item.type == 'OTHER',
          );
          if (findRotated) {
            this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.map((item) => {
              if (item.seqno == findRotated.seqno && item.inventoryno == findRotated.inventoryno) {
                return {
                  ...item,
                  ...tyreConfigToAdd,
                };
              }
              return item;
            });
            this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.filter(
              (item) =>
                !(
                  item.serialno == findRotated.oldserialno &&
                  item.inventoryno == findRotated.inventoryno &&
                  item.prevupdate
                ),
            );
          } else {
            this.tyreConfigUpdateOtherVehicle.push({ ...tyreConfigToAdd, seqno: tyreMaxSeqNoOther });
          }

          this.tyreConfigUpdateOtherVehicle.push({
            ...tyreInfoRotatedPair,
            action: 'E',
            inuse: '0',
            seqno: tyreMaxSeqNoOther,
            prevupdate: true,
          });
        }

        //DISABLED edit existing configs
        // else {
        //   const tyreConfigList = this.tyreConfigSelectedVehichleTyreConfigFull;

        //   const findExistUpdateOther = this.tyreConfigUpdateOtherVehicle.findLast(
        //     (item) =>
        //       item.serialno == oldserialnoTableSelected &&
        //       item.oldserialno == newserialnoTableSelected &&
        //       item.type == 'OTHER' &&
        //       item.action == 'E',
        //   );

        //   if (findExistUpdateOther) {
        //     this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.map((item) => {
        //       if (item.tyreguid == findExistUpdateOther.tyreguid) {
        //         return {
        //           ...item,
        //           ...tyreConfigRotated,
        //         };
        //       }
        //       return item;
        //     });

        //     this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.filter(
        //       (item) => !(item.serialno == newserialnoTableSelected && item.prevupdate),
        //     );

        //     const findPrev = tyreConfigList.findLast(
        //       (item) => item.serialno == this.tyreConfigSerialNoValue && item.tyreindex == tyreConfigRotated.tyreindex,
        //     );
        //     this.tyreConfigUpdateOtherVehicle.push({
        //       ...findPrev,
        //       inuse: '1',
        //       action: 'E',
        //       prevupdate: true,
        //     });
        //   } else {
        //     const findUpdateOther = tyreConfigList.findLast(
        //       (item) =>
        //         item.serialno == oldserialnoTableSelected &&
        //         item.oldserialno == newserialnoTableSelected &&
        //         item.type == 'OTHER',
        //     );

        //     this.tyreConfigUpdateOtherVehicle.push({
        //       ...findUpdateOther,
        //       ...tyreConfigRotated,
        //       action: 'E',
        //     });

        //     if (newserialnoTableSelected != this.tyreConfigSerialNoValue) {
        //       const findPrev = tyreConfigList.findLast(
        //         (item) => item.serialno == newserialnoTableSelected && item.tyreindex == findUpdateOther.tyreindex,
        //       );

        //       this.tyreConfigUpdateOtherVehicle.push({
        //         ...findPrev,
        //         inuse: '1',
        //         action: 'E',
        //         revert: true,
        //       });

        //       const revertSelected = tyreConfigList.findLast(
        //         (item) =>
        //           item.serialno == this.tyreConfigSerialNoValue && item.tyreindex == tyreConfigRotated.tyreindex,
        //       );

        //       this.tyreConfigUpdateOtherVehicle.push({
        //         ...revertSelected,
        //         inuse: '0',
        //         action: 'E',
        //         prevupdate: true,
        //       });
        //     }
        //   }
        // }

        const partRotateToAdd = {
          Type: this.tyreConfigTyreTypeValue?.['Part Type'] ?? '',
          'Serial No': this.tyreConfigOldSerialNoValue ?? '',
          'Part No': partno ?? '',
          Manufacturer: manufacturer ?? '',
          'Made In': madein ?? '',
          Size: tyresize ?? this.tyreConfigSizeValue ?? '',
          'Cost (RM)': unitprice ?? '0',
          Replace: this.tyreConfigSerialNoValue ?? '',
          'Purchase From': supplier ?? '',
          'Date Purchase': datepurchase ? new Date(datepurchase).formatString('yyyy-MM-ddTHH:mm:ss') : null,
          'Insurance Claim No': this.tyreConfigInsuranceNoValue ?? '',
          'Invoice No': this.tyreConfigInvoiceNoValue ?? '',
          'Taken From': whsecode ?? '',
          Remark: this.tyreConfigRemarksValue,
          PONo: this.tyreConfigPONoValue ?? '',
          UserID: '',
          'Date Updated': '',
          MfgDate: mfgdate ? new Date(mfgdate).formatString('yyyy-MM-dd') : null,
          Model: model ?? '',
          Quantity: '1',

          partTypeDesc: this.tyreConfigTyreTypeValue?.Description ?? '',

          action: action,
          expenseCode: this.tyreConfigTyreTypeValue?.['Expense Code'] || 'EMS',
          isExpense: 'N',

          prodTxnID: this.tyreConfigTyreTypeValue?.ProdTxnID,
          TyreIndex: tyreindex,

          partinventoryno: inventoryno,
          jobno:
            this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #'] || this.tyreRotateOtherVehicleWorkshopJobInputValue,
        };

        if (action == 'N') {
          const findExist = this.partUpdateOtherVehicle.find(
            (item) => item['Serial No'] == oldserialnoTableSelected && item.Replace == newserialnoTableSelected,
          );
          if (findExist) {
            this.partUpdateOtherVehicle = this.partUpdateOtherVehicle.map((item) => {
              if (item.tyreSeqNo == findExist.tyreSeqNo) {
                return {
                  ...item,
                  ...partRotateToAdd,
                };
              }
              return item;
            });
          } else
            this.partUpdateOtherVehicle.push({
              ...partRotateToAdd,
              action: 'N',
              tyreSeqNo: tyreMaxSeqNoOther,
            });
        }

        const otherJobNo =
          this.tyreRotateOtherVehicleWorkshopJobValue ?
            this.tyreRotateOtherVehicleWorkshopJobValue['Job #']
          : this.tyreRotateOtherVehicleWorkshopJobInputValue;

        if (!this.tyreConfigRotateOtherDisabled && !this.tyreConfigRotateOtherWorkshopJobIsNotLatest) {
          if (action == 'N') {
            updateTyres = updateTyres.map((item) => {
              if (item.otherinventoryno == tyreConfigToAdd.inventoryno && item.type == 'OTHER') {
                return {
                  ...item,
                  otherjobno: otherJobNo,
                };
              }
              return item;
            });
            this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.map((item) => {
              if (item.inventoryno == tyreConfigToAdd.inventoryno && item.type == 'OTHER' && item.action == 'N') {
                return {
                  ...item,
                  jobno: otherJobNo,
                };
              }
              return item;
            });

            this.partUpdateOtherVehicle = this.partUpdateOtherVehicle.map((item) => {
              if (item.partinventoryno == tyreConfigToAdd.inventoryno && item.action == 'N') {
                return {
                  ...item,
                  jobno: otherJobNo,
                };
              }
              return item;
            });
          }
        }
      }

      this.tyreConfigUpdateList = [];
      this.updateParts = [];
      this.$nextTick(() => {
        this.tyreConfigUpdateList = this.reorderTableNo(updateTyres);
        this.partTableData = this.reorderTableNo(updateParts);
      });

      this.tyreConfigLayoutSelectedItem = null;
      this.tyrePositionSelectedValue = null;
      this.tyreConfigTableSelectedRow = [];

      this.tyreConfigPopupShow = false;
    },

    saveTyreConfigJobPartInfoOnly() {
      const { seqno } = this.tyreConfigTableSelectedRow[0] || {};
      if (!seqno) return;
      const rotationTyre = this.tyreConfigUpdateList.find((item) => item.seqno == seqno);
      const isRotation = rotationTyre?.type == 'ROTATE' || rotationTyre?.type == 'OTHER';

      const updateParts = this.partTableData.map((part) => {
        if (part.tyreSeqNo != seqno) return part;

        const quantity = this.parsePartAmount(part.Quantity) || 1;
        const unitPrice = this.parsePartAmount(this.tyreConfigJobPriceValue);
        return {
          ...part,
          'Cost (RM)': unitPrice * quantity,
          'Insurance Claim No': this.tyreConfigInsuranceNoValue ?? '',
          'Invoice No': this.tyreConfigInvoiceNoValue ?? '',
          Remark: this.tyreConfigRemarksValue,
          isExpense: isRotation ? 'N' : part.isExpense,
          action: part.action ?? 'E',
        };
      });

      const updateTyres = this.tyreConfigUpdateList.map((tyre) => {
        if (tyre.seqno != seqno) return tyre;

        return {
          ...tyre,
          unitprice: this.tyreConfigJobPriceValue ?? '0',
        };
      });

      this.tyreConfigUpdateList = [];
      this.partTableData = [];
      this.$nextTick(() => {
        this.tyreConfigUpdateList = this.reorderTableNo(updateTyres);
        this.partTableData = this.reorderTableNo(updateParts);
      });

      this.tyreConfigLayoutSelectedItem = null;
      this.tyrePositionSelectedValue = null;
      this.tyreConfigTableSelectedRow = [];
      this.tyreConfigPopupShow = false;
    },
    //#endregion

    //#region menu actions
    newDocClick() {
      this.clearTabsFields();
      this.docFieldsChangedCheck = structuredClone(this.docFields);
      this.docSelectedItem = {};
      this.tabFieldsAction = 'N';
    },

    newTaskClick() {
      this.clearTabsFields();
      this.taskFieldsChangedCheck = structuredClone(this.taskFields);
      this.taskSelectedItem = {};
      this.tabFieldsAction = 'N';
    },
    newPartClick() {
      this.clearTabsFields();
      this.partFieldsChangedCheck = structuredClone(this.partFields);
      this.partSelectedItem = {};
      this.tabFieldsAction = 'N';
    },

    docFileDownload() {
      // if it already has prefix like "data:...;base64,", use it directly
      const base64Data = this.docFields.docPicDocValue;
      let url = base64Data;
      if (!base64Data.startsWith('data:')) {
        // fallback generic MIME type if unknown
        url = 'data:application/octet-stream;base64,' + base64Data;
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = this.docFields.docPicDocName; // no extension if you don’t know it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async docFileChanged(file) {
      if (file.length == 0) {
        this.docFields.docPicDocName = '';
        this.docFields.docPicDocValue = null;
        this.docFields.docPicDocType = '';
      } else {
        this.$fullscreenLoading('Adding File');
        this.docFields.docPicDocName = file[0].name;
        this.docFields.docPicDocType = file[0].name.substring(file[0].name.indexOf('.'));
        const tempAtt = await file[0].toBase64();
        this.docFields.docPicDocValue = tempAtt.substring(tempAtt.indexOf(',') + 1);
        this.$fullscreenLoading();
      }
    },

    async docSaveClick() {
      this.clearErrorsDoc();
      const { '#': tableNo, seqNo } = this.docSelectedItem || {};
      const {
        docDocCodeValue,
        docDocDateValue,
        docDateReceivedValue,
        docReferenceNoValue,
        docRemarkValue,
        docDocTypeValue,
        docPicDocName,
        docPicDocValue,
        docPicDocType,
      } = this.docFields;

      const { 'Ref No': selectedRefNo } = this.docSelectedItem || {};

      //#region  error checking
      let error = false;

      if (!docReferenceNoValue) {
        this.docReferenceNoError = 'Reference No is required!';
        error = true;
      } else if (docReferenceNoValue != this.docFieldsChangedCheck.docReferenceNoValue) {
        this.$fullscreenLoading('Checking Reference No...');
        const check = await this.$apiCallV2('/WorkshopJob/CheckRefNo', { refno: docReferenceNoValue });
        const { Results } = check[0] || {};
        if (Results != 0) {
          this.docReferenceNoError = 'Reference No already exists!';
          error = true;
        }
        this.$fullscreenLoading();
      } else if (
        selectedRefNo != docReferenceNoValue &&
        this.docTableData.find((doc) => doc['Ref No'] == docReferenceNoValue)
      ) {
        this.docReferenceNoError = 'Reference No is duplicated from list!';
        error = true;
      }

      if (!docDocTypeValue) {
        this.docDocTypeError = 'Doc Type is required!';
        error = true;
      }
      if (!docDocDateValue) {
        this.docDocDateError = 'Doc Date is required!';
        error = true;
      }
      if (!docDateReceivedValue) {
        this.docDateReceivedError = 'Date Received is required!';
        error = true;
      }
      if (!docDocCodeValue) {
        this.docDocCodeError = 'Doc Code is required!';
        error = true;
      }

      if (error) return;
      //#endregion

      const docItem = {
        ...this.docSelectedItem,
        '#': tableNo ?? '',
        'Ref No': docReferenceNoValue,
        Type: docDocTypeValue['Doc Type'],
        Code: docDocCodeValue['Doc Code'],
        Date: docDocDateValue,
        'Date Received': docDateReceivedValue,
        'Pic/Doc': docPicDocName,
        PicDocFile: docPicDocValue,
        PicDocType: docPicDocType,
        Remark: docRemarkValue,
        JobNo: '',
        action: this.docSelectedItem.action ?? this.tabFieldsAction,
        seqNo: seqNo,
      };

      let setTableData = this.docTableData;
      if (this.tabFieldsAction == 'N') {
        setTableData = [...this.docTableData, { ...docItem, seqNo: this.docTableData.length + 1 }];
      } else if (this.tabFieldsAction == 'E') {
        setTableData = this.docTableData.map((doc) => {
          if (doc.seqNo == seqNo) return docItem;
          return doc;
        });
      }
      //use filtered data for operations but set to the actual table data
      this.docTableData = [];
      this.$nextTick(() => {
        this.docTableData = setTableData;
      });
      this.clearTabsFields();
    },

    taskSaveClick() {
      const { '#': tableNo, seqNo } = this.taskSelectedItem;

      const {
        taskServiceValue,
        taskServiceTaskValue,
        taskServicePersonValue,
        taskDateTimeStartValue,
        taskDateTimeEndValue,
        taskRemarkValue,
      } = this.taskFields;

      //#region error checking
      let error = false;
      if (!taskServiceValue) {
        this.taskServiceError = 'Service Code is required!';
        error = true;
      }
      if (!taskServiceTaskValue) {
        this.taskServiceTaskError = 'Task is required!';
        error = true;
      }
      if (error) return;
      //#endregion

      const taskItem = {
        ...this.taskSelectedItem,
        '#': tableNo ?? '',
        'Service Code': taskServiceValue.ServiceCode,
        'Service Description': taskServiceValue.Description,
        'Task Code': taskServiceTaskValue.ServiceCode,
        'Task Description': taskServiceTaskValue.Description,
        'Service Person': taskServicePersonValue,
        'Date Start': taskDateTimeStartValue,
        'Date End': taskDateTimeEndValue,
        Remark: taskRemarkValue,
        action: this.taskSelectedItem.action ?? this.tabFieldsAction,
        seqNo: seqNo,
      };

      let setTableData = this.taskTableData;
      if (this.tabFieldsAction == 'N') {
        setTableData = [...this.taskTableData, { ...taskItem, seqNo: this.taskTableData.length + 1 }];
      } else if (this.tabFieldsAction == 'E') {
        setTableData = this.taskTableData.map((task) => {
          if (task.seqNo == seqNo) return taskItem;
          return task;
        });
      }
      //use filtered data for operations but set to the actual table data
      this.taskTableData = [];
      this.$nextTick(() => {
        this.taskTableData = setTableData;
      });
      this.clearTabsFields();
    },

    partQuantityFocusChanged(focus) {
      if (focus) return;
      const quantityValue = this.normalizePartQuantityValue(this.partFields.partQuantityValue);
      if (quantityValue) this.partFields.partQuantityValue = quantityValue;
      this.validatePartQuantityMaximum();
    },

    validatePartQuantityMaximum(quantityValue = null) {
      const selectedWarehouseItem = this.partFields.partWarehouseItemValue;
      const quantityBalance = this.partQuantityMaximum;
      if (!selectedWarehouseItem || quantityBalance === null) return true;

      const value =
        quantityValue === null || quantityValue === undefined ?
          this.parsePartAmount(String(this.partFields.partQuantityValue || '1').trim() || '1')
        : quantityValue;
      if (value > quantityBalance) {
        this.partQuantityError = `Quantity cannot exceed available stock (${quantityBalance})!`;
        return false;
      }
      return true;
    },

    partSaveClick() {
      const { '#': tableNo, seqNo, tyreSeqNo } = this.partSelectedItem || {};
      const rotationTyre = this.tyreConfigUpdateList.find((item) => item.seqno == tyreSeqNo);
      const isRotation = rotationTyre?.type == 'ROTATE' || rotationTyre?.type == 'OTHER';
      const {
        partTypeValue,
        partSerialNoValue,
        partSerialNoInputValue,
        partPartNoValue,
        partSizeValue,
        partWarehouseItemValue,
        partWarehouseItemSavedValue,
        partTakenFromValue,
        partTakenFromInputValue,
        partUnitPriceValue,
        partMFGDateValue,
        partDatePurchaseValue,
        partInvoiceNoValue,
        partReplaceValue,
        partModelValue,
        partMadeInValue,
        partQuantityValue,
        partManufacturerValue,
        partPurchaseFromValue,
        partPONoValue,
        partInsuranceNoValue,
        partRemarkValue,
      } = this.partFields;

      //#region error checking
      this.partTypeError = '';
      this.partSerialNoError = '';
      this.partQuantityError = '';
      let error = false;
      if (!partTypeValue) {
        this.partTypeError = 'Type is required!';
        error = true;
      }
      if (!partSerialNoValue && this.serialNoRequired && !partSerialNoInputValue) {
        this.partSerialNoError = 'Serial No is required!';
        error = true;
      }
      const rawQuantity = partQuantityValue == null ? '' : partQuantityValue.toString().trim();
      const quantityValue = this.parsePartAmount(rawQuantity || '1');
      if (quantityValue <= 0) {
        this.partQuantityError = 'Quantity must be greater than 0!';
        error = true;
      }
      if (!this.validatePartQuantityMaximum(quantityValue)) error = true;
      if (error) return;
      //#endregion

      const quantity = quantityValue.toString();
      const unitPriceValue = this.parsePartAmount(partUnitPriceValue);
      const selectedWarehouseItem = partWarehouseItemValue;
      const savedWarehouseItem = partWarehouseItemSavedValue;
      const existingPart = this.partSelectedItem || {};
      const warehouseGitNo = selectedWarehouseItem ? selectedWarehouseItem.GitNo || '' : existingPart.GitNo || '';
      const warehousePLGuid = selectedWarehouseItem ? selectedWarehouseItem.PLGuid || '' : existingPart.PLGuid || '';
      const warehousePLNo = selectedWarehouseItem ? selectedWarehouseItem.PLNo || '' : existingPart.PLNo || '';
      const warehouseGRNGuid = selectedWarehouseItem ? selectedWarehouseItem.GRNGuid || savedWarehouseItem?.GRNGuid || '' : existingPart.GRNGuid || '';

      const partItem = {
        ...this.partSelectedItem,
        '#': tableNo ?? '',
        Type: partTypeValue['Part Type'],
        'Serial No': partSerialNoValue ? partSerialNoValue['Serial No'] : partSerialNoInputValue,
        'Part No': partPartNoValue,
        Manufacturer: partManufacturerValue?.MFGCode ?? '',
        'Made In': partMadeInValue?.['Country Code'] ?? '',
        Size: partSizeValue,
        'Cost (RM)': unitPriceValue * quantityValue,
        Replace: partReplaceValue,
        'Purchase From': partPurchaseFromValue?.['Supplier Code'] ?? '',
        'Date Purchase': partDatePurchaseValue,
        'Insurance Claim No': partInsuranceNoValue,
        'Invoice No': partInvoiceNoValue,
        'Taken From': partTakenFromValue?.Warehouse ?? partTakenFromValue?.['Warehouse Code'] ?? partTakenFromInputValue,
        Remark: partRemarkValue,
        PONo: partPONoValue,
        UserID: '',
        'Date Updated': '',
        MfgDate: partMFGDateValue,
        Model: partModelValue,
        Quantity: quantity,

        partTypeDesc: partTypeValue.Description,

        action: this.partSelectedItem.action ?? this.tabFieldsAction,
        seqNo: seqNo,

        expenseCode: partTypeValue['Expense Code'] || 'EMS',
        isExpense: isRotation ? 'N' : (partTypeValue?.isExpense ?? partTypeValue?.IsExpenses),

        prodTxnID: partTypeValue.ProdTxnID || partWarehouseItemValue?.ProdTxnID || '',
        AllocGUID: partWarehouseItemValue?.AllocGUID || partTakenFromValue?.AllocGUID || partSerialNoValue?.AllocGUID || '',
        BinLoc: partWarehouseItemValue?.BinLoc || partTakenFromValue?.BinLoc || '',
        GitNo: warehouseGitNo,
        PLGuid: warehousePLGuid,
        PLNo: warehousePLNo,
        GRNGuid: warehouseGRNGuid,
      };

      let setTableData = this.partTableData;
      if (this.tabFieldsAction == 'N') {
        setTableData = [...this.partTableData, { ...partItem, seqNo: this.partTableData.length + 1 }];
      } else if (this.tabFieldsAction == 'E') {
        setTableData = this.partTableData.map((part) => {
          if (part.seqNo == seqNo) return partItem;
          return part;
        });
      }

      this.partTableData = [];
      this.$nextTick(() => {
        this.partTableData = setTableData;
      });
      this.clearTabsFields();

      if (tyreSeqNo) {
        const updateTyreConfig = this.tyreConfigUpdateList.map((item) => {
          if (tyreSeqNo == item.seqno)
            return {
              ...item,
              manufacturer: partManufacturerValue?.MFGCode ?? '',
              madein: partMadeInValue?.['Country Code'] ?? '',
              model: partModelValue,
              tyresize: partSizeValue,
              sppcode: partPurchaseFromValue?.['Supplier Code'] ?? '',
              partno: partPartNoValue,
              unitprice: unitPriceValue,
            };
          return item;
        });

        this.tyreConfigUpdateList = [];
        this.$nextTick(() => {
          this.tyreConfigUpdateList = this.reorderTableNo(updateTyreConfig);
        });
      }
    },

    async docDeleteClick() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Document?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;
      const { seqNo, action } = this.docTableSelectedRows[0] || {};
      const update =
        action == 'N' ?
          this.docTableData.filter((item) => item.seqNo != seqNo)
        : this.docTableData.map((item) => {
            if (item.seqNo == seqNo) {
              return { ...item, action: 'D' };
            }
            return item;
          });
      this.docTableData = [];
      this.$nextTick(() => {
        this.docTableData = this.reorderTableNo(update);
      });
    },
    async taskDeleteClick() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Task?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;
      const { seqNo, action } = this.taskTableSelectedRows[0] || {};
      const update =
        action == 'N' ?
          this.taskTableData.filter((item) => item.seqNo != seqNo)
        : this.taskTableData.map((item) => {
            if (item.seqNo == seqNo) {
              return { ...item, action: 'D' };
            }
            return item;
          });
      this.taskTableData = [];
      this.$nextTick(() => {
        this.taskTableData = this.reorderTableNo(update);
      });
    },
    async partDeleteClick() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Part?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;
      const { seqNo, action, TyreIndex: tyreindex, 'Serial No': serialno } = this.partTableSelectedRows[0] || {};
      const update =
        action == 'N' ?
          this.partTableData.filter((item) => item.seqNo != seqNo)
        : this.partTableData.map((item) => {
            if (item.seqNo == seqNo) {
              return { ...item, action: 'D' };
            }
            return item;
          });
      this.partTableData = [];
      this.$nextTick(() => {
        this.partTableData = this.reorderTableNo(update);
        if (tyreindex) {
          this.tyreConfigDeleteClicked(null, tyreindex, serialno);
          this.$nextTick(() => {
            this.tyreConfigPopupSave();
          });
        }
      });
    },

    popupEntryRequiredClicked: function (item) {
      this.$refs[item].setFocus();
    },

    tabFieldsCancel() {
      this.docSelectedItem = null;
      this.taskSelectedItem = null;
      this.partSelectedItem = null;
      this.clearTabsFields();
    },

    async popupEntrySaveClicked() {
      if (this.fieldsChanged) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }

      this.clearEntryTexts();
      const error = [];
      if (!this.jobInventoryValue) {
        this.jobInventoryValueError = 'Inventory is required!';
        error.push({ key: 'jobInventory', value: 'Inventory is required!' });
      }
      if (!this.jobDateValue) {
        this.jobDateValueError = 'Job Date is requried!';
        error.push({ key: 'jobDate', value: 'Job Date is required!' });
      }
      if (!this.repairTypeValue) {
        this.repairTypeValueError = 'Replaced With is required!';
        error.push({ key: 'repairType', value: 'Replaced With is required!' });
      }
      if (!this.internalExternalValue) {
        this.internalExternalValueError = 'Internal/External is required!';
        error.push({ key: 'intext', value: 'Internal/External is required!' });
      }
      if (!this.mileageValue) {
        this.mileageValueError = 'Mileage is required!';
        error.push({ key: 'mileage', value: 'Mileage is required!' });
      }
      if (!this.dateTimeInValue && this.confirmJobValue) {
        this.dateTimeInValueError = 'Date/Time In is required!';
        error.push({ key: 'dateTimeIn', value: 'Date/Time In is required!' });
      }
      if (
        this.availabilityEnabled &&
        (this.inventoryAvailabilityValue ? this.inventoryAvailabilityValue.keyValue == '0' : false) &&
        !this.inventoryUnavailableRemarkValue
      ) {
        this.inventoryUnavailableRemarkValueError = 'Inventory Unavailable Remark is required!';
        error.push({ key: 'inventoryUnavailableRemark', value: 'Inventory Unavailable Remark is required!' });
      }
      this.popupEntryRequiredError = error;
      if (error.length > 0) return;

      this.$fullscreenLoading('Saving Workshop Job...');

      const successMessages = [];

      const requestBody = {
        action: this.menuMode == 'New' ? 'N' : 'E',

        //general info
        jobno: this.jobNoValue,
        inventoryno: this.jobInventoryValue['Inventory #'],
        jobdate: this.jobDateValue.formatString('yyyy-MM-ddTHH:mm:ss'),

        //job details
        trailer: this.trailerNoValue?.['Inventory #'] ?? '',
        requestby: this.requestedByValue?.['EmployeeID'] ?? '',
        servicetype: this.repairTypeValue?.keyValue ?? '',
        intext: this.internalExternalValue?.keyValue ?? '',
        serviceperson: this.servicePersonValue,
        workshop: this.workshopValue?.['Supplier Code'] ?? '',
        meterreading:
          this.confirmJobValue && this.isLatestJob && this.mileageValue > this.selectedVehicleInfo.MeterReading ?
            this.mileageValue
          : this.selectedVehicleInfo.MeterReading,
        remark1: this.remark1Value,
        datein: this.dateTimeInValue?.formatString('yyyy-MM-dd') ?? null,
        timein: this.dateTimeInValue?.formatString('HHmm') ?? '',
        dateout: this.dateTimeOutValue?.formatString('yyyy-MM-dd') ?? null,
        timeout: this.dateTimeOutValue?.formatString('HHmm') ?? '',
        status: this.confirmJobValue ? '12001' : '', //12001 is the hard coded value in 4G, not sure why
        availability: this.inventoryAvailabilityValue?.keyValue ?? '',
        invavailabilityremark:
          (this.inventoryAvailabilityValue ? this.inventoryAvailabilityValue.keyValue == '0' : false) ?
            this.inventoryUnavailableRemarkValue
          : '',

        //things not in form
        indicator: 'N', //must be N if saving or editing, if C it's a Canceled(Deleted) entry

        jobdatedate: this.jobDateDate,
        jobdatetime: this.jobDateTime,

        documents: this.docTableData
          .filter((task) => task.action != 'D')
          .map((item) => {
            const {
              Code,
              Date: DocDate,
              'Date Received': DateReceived,
              'Pic/Doc': PicDoc,
              PicDocFile,
              PicDocType,
              'Ref No': RefNo,
              Remark,
              Type,
              action,
            } = item;
            return {
              code: Code,
              date: DocDate,
              datereceived: DateReceived?.formatString('yyyy-MM-dd') ?? null,
              timereceived: DateReceived?.formatString('HHmm') ?? null,
              picdoc: PicDoc,
              picdocfile: PicDocFile ?? null,
              picdoctype: PicDocType ?? '',
              refno: RefNo,
              remark: Remark,
              type: Type,
              action: this.menuMode == 'New' ? 'N' : action,
            };
          }),
        //documents are deleted and inserted again because there are previous items where seqNo is not used
        //so making identifying unique items for Edit and Delete not possible

        tasks: this.taskTableData
          .filter((task) => task.action != 'D')
          .map((item, index) => {
            const {
              'Service Code': ServiceCode,
              'Task Code': TaskCode,
              'Service Person': ServicePerson,
              'Date Start': DateStart,
              'Date End': DateEnd,
              Remark,
              action,
              InvSeq,
            } = item;
            return {
              servicecode: ServiceCode,
              seqno: index + 1, //reset this seqNo, because deleted item might affect the order,
              task: TaskCode,
              serviceperson: ServicePerson,
              datestart: DateStart?.formatString('yyyy-MM-dd') ?? null,
              timestart: DateStart?.formatString('HHmm') ?? null,
              dateend: DateEnd?.formatString('yyyy-MM-dd') ?? null,
              timeend: DateEnd?.formatString('HHmm') ?? null,
              remark: Remark,
              action: this.menuMode == 'New' ? 'N' : action,

              invseq: InvSeq ?? null, //For ServiceTask not EMSTask
            };
          }),
        parts: this.partTableData
          .filter((part) => part.action)
          .map((item) => {
            const {
              'Cost (RM)': Cost,
              'Date Purchase': DatePurchase,
              'Insurance Claim No': InsuranceNo,
              'Invoice No': InvoiceNo,
              'Made In': MadeIn,
              Manufacturer,
              MfgDate,
              Model,
              PONo,
              'Part No': PartNo,
              PartID,
              'Purchase From': PurchaseFrom,
              Quantity,
              Remark,
              Replace,
              'Serial No': SerialNo,
              Size,
              'Taken From': TakenFrom,
              Type,

              partTypeDesc,

              action,
              seqNo,

              expenseCode,
              isExpense,

              prodTxnID,
              AllocGUID,
              GitNo,
              GRNGuid,
              BinLoc,
              PLNo,
              PLGuid,

              TyreIndex,
              noOfRetread,
              partinventoryno,
            } = item;

            return {
              partid: PartID ?? '',
              docrefno: InvoiceNo ?? '',
              serialno: SerialNo ?? '',
              partno: PartNo ?? '',
              parttype: Type ?? '',
              model: Model ?? '',
              madein: MadeIn ?? '',
              manufacturer: Manufacturer ?? '',
              purchasefrom: PurchaseFrom ?? '',
              datepurchase: this.toUTCDateTime(DatePurchase) ?? null,
              mfgdate: this.toUTCDateTime(MfgDate) ?? null,
              size: Size ?? '',
              quantity: Quantity ?? '',
              cost: Cost ?? '0',
              insclaimno: InsuranceNo ?? '',
              reusefrom: Replace ?? '',
              remark: Remark ?? '',
              pono: PONo ?? '',

              action: this.menuMode == 'New' ? 'N' : action,
              seqno: seqNo ?? '',

              takenfrom: TakenFrom ?? '',

              tyreindex: TyreIndex ?? '',
              noofretread: noOfRetread ?? '',

              parttypedesc: partTypeDesc ?? '',
              expensecode: expenseCode ?? '',
              isexpense: isExpense ?? '',

              prodtxnid: prodTxnID ?? '',
              allocguid: AllocGUID ?? '',
              gitno: GitNo ?? '',
              grnguid: GRNGuid ?? '',
              binloc: BinLoc,
              plno: PLNo ?? '',
              plguid: PLGuid ?? '',

              partinventoryno: partinventoryno ?? this.jobInventoryValue['Inventory #'],
            };
          }),

        tyres: [
          ...this.tyreConfigUpdateList,
          ...this.tyreConfigUpdateOtherVehicle.filter((item) => item.prevupdate || item.revert),
        ]
          .filter((item) => item.action && item.action != 'O')
          .map((item) => {
            const {
              serialno,
              oldserialno,
              actualSerial,
              tyreposition,
              storein,
              inventoryno,
              seqno,
              tyresize,
              tyreindex,
              repair,
              inuse,
              datechange,
              align,
              rowno,
              columnno,
              whsecode,
              prodtxnid,
              action,
              actualtyreguid,
              tyreguid,

              type,
              mfgdate,
              datepurchase,
              noofretread,
              model,
              madein,
              manufacturer,
              unitprice,
              sppcode,
              allocguid,

              jobno,

              tyretype,
              partno,

              startmileage,

              revertgrn,
            } = item;

            return {
              newserial: serialno ?? '',
              currentserial: oldserialno ?? '',
              actualserial: actualSerial ?? '',
              tyreposition: tyreposition ?? '',
              storein: storein ?? '',
              inventoryno: inventoryno ?? '',
              seqno: seqno ?? '',
              tyresize: tyresize ?? '',
              tyreindex: tyreindex ?? '',
              repair: repair ?? '',
              inuse: inuse ?? '',
              datechange: datechange ? (this.toUTCDateTime(datechange) ?? null) : this.toUTCDateTime(this.jobDateDate),
              align: align ?? '',
              rowno: rowno ?? '',
              columnno: columnno ?? '',
              whsecode: whsecode ?? '',
              prodtxnid: prodtxnid ?? '',
              action: action ?? '',
              actualtyreguid: actualtyreguid ?? '',
              currenttyreguid: tyreguid ?? '',
              type: type ?? '',
              mfgdate: this.toUTCDateTime(mfgdate) ?? null,
              datepurchase: this.toUTCDateTime(datepurchase) ?? null,
              noofretread: noofretread ?? '',
              model: model ?? '',
              madein: madein ?? '',
              manufacturer: manufacturer ?? '',
              unitprice: unitprice ?? '',
              sppcode: sppcode ?? '',
              allocguid: allocguid ?? '',
              tyretype: tyretype ?? '',
              partno: partno ?? '',

              jobno: jobno ?? '',

              startmileage: startmileage ?? this.mileageValue ?? '0',
              endmileage: this.mileageValue ?? '0',

              revertgrn: revertgrn ?? false,
            };
          }),

        //extra needed
        inventorycode: this.jobInventoryValue['Inventory Code'],
        islatestjob: this.isLatestJob ? 'Y' : 'N',
      };

      const result = await this.$apiCallV2('/WorkshopJob/SaveEMSJob', requestBody);

      const { Results, ErrMsg, EMSJOB } = result?.[0] || {};
      if (Results == '1') {
        this.popupEntryErrorMessages.push(ErrMsg);
        this.$fullscreenLoading();
        return;
      }
      successMessages.push(ErrMsg);

      const otherNewInv = [
        ...new Set(
          this.tyreConfigUpdateOtherVehicle
            .filter((item) => item.action == 'N' && !item.jobno && !(item.revert || item.prevupdate))
            .map((item) => item.inventoryno)
            .filter(Boolean), // removes null/undefined/empty
        ),
      ].map((invno) => {
        const findParts = this.partUpdateOtherVehicle.filter(
          (item) => item.partinventoryno == invno && item.action == 'N' && !item.jobno,
        );

        const findTyres = this.tyreConfigUpdateOtherVehicle.filter(
          (item) => item.inventoryno == invno && item.action == 'N' && !item.jobno,
        );

        return {
          action: 'N',

          //general info
          jobno: '',
          inventoryno: invno,
          jobdate: this.jobDateValue.formatString('yyyy-MM-ddTHH:mm:ss'),

          //job details
          trailer: '',
          requestby: this.requestedByValue?.['EmployeeID'] ?? '',
          servicetype: this.repairTypeValue?.keyValue ?? '',
          intext: this.internalExternalValue?.keyValue ?? '',
          serviceperson: this.servicePersonValue,
          workshop: this.workshopValue?.['Supplier Code'] ?? '',
          meterreading: '',
          remark1: '',
          datein: this.dateTimeInValue?.formatString('yyyy-MM-dd') ?? null,
          timein: this.dateTimeInValue?.formatString('HHmm') ?? '',
          dateout: this.dateTimeOutValue?.formatString('yyyy-MM-dd') ?? null,
          timeout: this.dateTimeOutValue?.formatString('HHmm') ?? '',
          status: this.confirmJobValue ? '12001' : '', //12001 is the hard coded value in 4G, not sure why

          availability: '1',
          invavailabilityremark: '',

          //things not in form
          indicator: 'N', //must be N if saving or editing, if C it's a Canceled(Deleted) entry

          jobdatedate: this.jobDateDate,
          jobdatetime: this.jobDateTime,

          islatsetjob: 'Y',

          parts: findParts.map((item) => {
            const {
              'Cost (RM)': Cost,
              'Date Purchase': DatePurchase,
              'Insurance Claim No': InsuranceNo,
              'Invoice No': InvoiceNo,
              'Made In': MadeIn,
              Manufacturer,
              MfgDate,
              Model,
              PONo,
              'Part No': PartNo,
              PartID,
              'Purchase From': PurchaseFrom,
              Quantity,
              Remark,
              Replace,
              'Serial No': SerialNo,
              Size,
              'Taken From': TakenFrom,
              Type,

              partTypeDesc,

              action,
              seqNo,

              expenseCode,
              isExpense,

              prodTxnID,
              AllocGUID,
              GitNo,
              GRNGuid,
              BinLoc,
              PLNo,
              PLGuid,

              TyreIndex,
              noOfRetread,
              partinventoryno,
            } = item;

            return {
              partid: PartID ?? '',
              docrefno: InvoiceNo ?? '',
              serialno: SerialNo ?? '',
              partno: PartNo ?? '',
              parttype: Type ?? '',
              model: Model ?? '',
              madein: MadeIn ?? '',
              manufacturer: Manufacturer ?? '',
              purchasefrom: PurchaseFrom ?? '',
              datepurchase: this.toUTCDateTime(DatePurchase) ?? null,
              mfgdate: this.toUTCDateTime(MfgDate) ?? null,
              size: Size ?? '',
              quantity: Quantity ?? '',
              cost: Cost ?? '0',
              insclaimno: InsuranceNo ?? '',
              reusefrom: Replace ?? '',
              remark: Remark ?? '',
              pono: PONo ?? '',

              action: action,
              seqno: seqNo ?? '',

              takenfrom: TakenFrom ?? '',

              tyreindex: TyreIndex ?? '',
              noofretread: noOfRetread ?? '',

              parttypedesc: partTypeDesc ?? '',
              expensecode: expenseCode ?? '',
              isexpense: isExpense ?? '',

              prodtxnid: prodTxnID ?? '',
              allocguid: AllocGUID ?? '',
              gitno: GitNo ?? '',
              grnguid: GRNGuid ?? '',
              binloc: BinLoc,
              plno: PLNo ?? '',
              plguid: PLGuid ?? '',

              partinventoryno: partinventoryno ?? this.jobInventoryValue['Inventory #'],
            };
          }),

          tyres: findTyres.map((item) => {
            const {
              serialno,
              oldserialno,
              actualSerial,
              tyreposition,
              storein,
              inventoryno,
              seqno,
              tyresize,
              tyreindex,
              repair,
              inuse,
              datechange,
              align,
              rowno,
              columnno,
              whsecode,
              prodtxnid,
              action,
              actualtyreguid,
              tyreguid,

              type,
              mfgdate,
              datepurchase,
              noofretread,
              model,
              madein,
              manufacturer,
              unitprice,
              sppcode,
              allocguid,

              jobno,

              tyretype,
              partno,

              startmileage,

              revertgrn,
            } = item;
            return {
              newserial: serialno ?? '',
              currentserial: oldserialno ?? '',
              actualserial: actualSerial ?? '',
              tyreposition: tyreposition ?? '',
              storein: storein ?? '',
              inventoryno: inventoryno ?? '',
              seqno: seqno ?? '',
              tyresize: tyresize ?? '',
              tyreindex: tyreindex ?? '',
              repair: repair ?? '',
              inuse: inuse ?? '',
              datechange: datechange ? (this.toUTCDateTime(datechange) ?? null) : this.toUTCDateTime(this.jobDateDate),
              align: align ?? '',
              rowno: rowno ?? '',
              columnno: columnno ?? '',
              whsecode: whsecode ?? '',
              prodtxnid: prodtxnid ?? '',
              action: action ?? '',
              actualtyreguid: actualtyreguid ?? '',
              currenttyreguid: tyreguid ?? '',
              type: type ?? '',
              mfgdate: this.toUTCDateTime(mfgdate) ?? null,
              datepurchase: this.toUTCDateTime(datepurchase) ?? null,
              noofretread: noofretread ?? '',
              model: model ?? '',
              madein: madein ?? '',
              manufacturer: manufacturer ?? '',
              unitprice: unitprice ?? '',
              sppcode: sppcode ?? '',
              allocguid: allocguid ?? '',
              tyretype: tyretype ?? '',
              partno: partno ?? '',

              jobno: jobno ?? '',

              startmileage: startmileage ?? '0',

              revertgrn: revertgrn ?? false,
            };
          }),
        };
      });

      const attachToJobNo = await Promise.all(
        [
          ...new Set(
            this.tyreConfigUpdateOtherVehicle
              .filter(
                (item) =>
                  item.jobno && item.jobno != this.jobNoValue && item.action != 'O' && !item.prevupdate && !item.revert,
              )
              .map((item) => item.jobno)
              .filter(Boolean),
          ),
        ].map(async (otherjobno) => {
          const getDetails = await this.getWorkshopJobDetails(otherjobno);
          const {
            JobNo,
            JobDate,
            Inventory,
            Trailer,
            RequestedBy,
            RepairType,
            InternalExternal,
            Workshop,
            ServicePerson,
            Mileage,
            Remark,
            DateTimeIn,
            TimeIn,
            DateTimeOut,
            TimeOut,
            Status,
            InvAvailability,
            InvAvailabilityRemark,
            Indicator,
          } = getDetails || {};

          const findParts = this.partUpdateOtherVehicle.filter(
            (item) => item.partinventoryno == Inventory && item.jobno == JobNo,
          );

          const findTyres = this.tyreConfigUpdateOtherVehicle.filter(
            (item) => item.inventoryno == Inventory && item.jobno == JobNo,
          );

          return {
            action: 'E',

            //general info
            jobno: JobNo,
            inventoryno: Inventory,
            jobdate: JobDate,

            //job details
            trailer: Trailer,
            requestby: RequestedBy,
            servicetype: RepairType,
            intext: InternalExternal,
            serviceperson: ServicePerson,
            workshop: Workshop,
            meterreading: Mileage,
            remark1: Remark,
            datein: DateTimeIn ?? null,
            timein: TimeIn ?? '',
            dateout: DateTimeOut ?? null,
            timeout: TimeOut ?? '',
            status: Status, //12001 is the hard coded value in 4G, not sure why

            availability: InvAvailability,
            invavailabilityremark: InvAvailabilityRemark || '',

            //things not in form
            indicator: Indicator, //must be N if saving or editing, if C it's a Canceled(Deleted) entry

            jobdatedate: new Date(JobDate).formatString('yyyy-MM-dd'),
            jobdatetime: new Date(JobDate).formatString('HHmm'),

            parts: findParts.map((item) => {
              const {
                'Cost (RM)': Cost,
                'Date Purchase': DatePurchase,
                'Insurance Claim No': InsuranceNo,
                'Invoice No': InvoiceNo,
                'Made In': MadeIn,
                Manufacturer,
                MfgDate,
                Model,
                PONo,
                'Part No': PartNo,
                PartID,
                'Purchase From': PurchaseFrom,
                Quantity,
                Remark,
                Replace,
                'Serial No': SerialNo,
                Size,
                'Taken From': TakenFrom,
                Type,

                partTypeDesc,

                action,
                seqNo,

                expenseCode,
                isExpense,

                prodTxnID,
                AllocGUID,
                GitNo,
                GRNGuid,
                BinLoc,
                PLNo,
                PLGuid,

                TyreIndex,
                noOfRetread,
                partinventoryno,
              } = item;

              return {
                partid: PartID ?? '',
                docrefno: InvoiceNo ?? '',
                serialno: SerialNo ?? '',
                partno: PartNo ?? '',
                parttype: Type ?? '',
                model: Model ?? '',
                madein: MadeIn ?? '',
                manufacturer: Manufacturer ?? '',
                purchasefrom: PurchaseFrom ?? '',
                datepurchase: this.toUTCDateTime(DatePurchase) ?? null,
                mfgdate: this.toUTCDateTime(MfgDate) ?? null,
                size: Size ?? '',
                quantity: Quantity ?? '',
                cost: Cost ?? '0',
                insclaimno: InsuranceNo ?? '',
                reusefrom: Replace ?? '',
                remark: Remark ?? '',
                pono: PONo ?? '',

                action: action,
                seqno: seqNo ?? '',

                takenfrom: TakenFrom ?? '',

                tyreindex: TyreIndex ?? '',
                noofretread: noOfRetread ?? '',

                parttypedesc: partTypeDesc ?? '',
                expensecode: expenseCode ?? '',
                isexpense: isExpense ?? '',

                prodtxnid: prodTxnID ?? '',
                allocguid: AllocGUID ?? '',
                gitno: GitNo ?? '',
                grnguid: GRNGuid ?? '',
                binloc: BinLoc,
                plno: PLNo ?? '',
                plguid: PLGuid ?? '',

                partinventoryno: partinventoryno ?? this.jobInventoryValue['Inventory #'],
              };
            }),

            tyres: findTyres.map((item) => {
              const {
                serialno,
                oldserialno,
                actualSerial,
                tyreposition,
                storein,
                inventoryno,
                seqno,
                tyresize,
                tyreindex,
                repair,
                inuse,
                datechange,
                align,
                rowno,
                columnno,
                whsecode,
                prodtxnid,
                action,
                actualtyreguid,
                tyreguid,

                type,
                mfgdate,
                datepurchase,
                noofretread,
                model,
                madein,
                manufacturer,
                unitprice,
                sppcode,
                allocguid,

                jobno,
                partno,

                tyretype,

                startmileage,

                revertgrn,
              } = item;
              return {
                newserial: serialno ?? '',
                currentserial: oldserialno ?? '',
                actualserial: actualSerial ?? '',
                tyreposition: tyreposition ?? '',
                storein: storein ?? '',
                inventoryno: inventoryno ?? '',
                seqno: seqno ?? '',
                tyresize: tyresize ?? '',
                tyreindex: tyreindex ?? '',
                repair: repair ?? '',
                inuse: inuse ?? '',
                datechange:
                  datechange ? (this.toUTCDateTime(datechange) ?? null) : this.toUTCDateTime(this.jobDateDate),
                align: align ?? '',
                rowno: rowno ?? '',
                columnno: columnno ?? '',
                whsecode: whsecode ?? '',
                prodtxnid: prodtxnid ?? '',
                action: action ?? '',
                actualtyreguid: actualtyreguid ?? '',
                currenttyreguid: tyreguid ?? '',
                type: type ?? '',
                mfgdate: this.toUTCDateTime(mfgdate) ?? null,
                datepurchase: this.toUTCDateTime(datepurchase) ?? null,
                noofretread: noofretread ?? '',
                model: model ?? '',
                madein: madein ?? '',
                manufacturer: manufacturer ?? '',
                unitprice: unitprice ?? '',
                sppcode: sppcode ?? '',
                allocguid: allocguid ?? '',
                tyretype: tyretype ?? '',

                jobno: jobno ?? '',
                partno: partno ?? '',

                startmileage: startmileage ?? '0',

                revertgrn: revertgrn ?? false,
              };
            }),
          };
        }),
      );

      const updateOtherInv = [...otherNewInv, ...attachToJobNo];

      for (const inv of updateOtherInv) {
        const resultOther = await this.$apiCallV2('/WorkshopJob/SaveEMSJob', inv);

        const { Results, ErrMsg, EMSJOB } = resultOther?.[0] || {};

        if (Results == '1') {
          this.popupEntryErrorMessages.push(ErrMsg);
        }
        const currentUrl = new URL(window.location.href);
        const newParams = new URLSearchParams();
        newParams.set('JobNo', EMSJOB);

        successMessages.push(
          ErrMsg.replace(
            EMSJOB,
            `<a href="${currentUrl.origin}${currentUrl.pathname}?JobNo=${EMSJOB}}" target="_blank">${EMSJOB}</a>`,
          ),
        );
      }

      this.populatePopupEntryFields(EMSJOB);
      this.viewMode = true;
      this.menuMode = 'View';
      this.popupEntrySuccessMessages.push(...successMessages);

      await Promise.all([
        this.getWorkshopJobs(),
        this.getSerialNo(),
        this.getTakenFrom(),
        this.getJobPartTyreList(),
        this.getJobPartTyreSaveList(),
      ]);

      const selectedIndex = this.workshopJobsTableData.findIndex((item) => item['Job #'] == EMSJOB);
      this.workshopJobsTableSelectedRows = [
        { ...this.workshopJobsTableData.find((item) => item['Job #'] == EMSJOB), _rowid: `row${selectedIndex}` },
      ];

      this.$fullscreenLoading();
    },

    async populateTyreConfigFields(tyreInfo, partInfo, updateStatusValue = true) {
      const {
        oldserialno,
        tyreindex,
        serialno,
        partno,
        partprice,
        prodtxnid,
        tyresize,
        tyretype,
        datepurchase,
        action,
        otherinventoryno,
      } = tyreInfo;
      const { 'Replace Type': type } = this.tyreConfigTableSelectedRow?.[0] || {};
      const {
        'Taken From': TakenFrom,
        MfgDate,
        Model,
        'Made In': MadeIn,
        'Part No': PartNo,
        Manufacturer,
        'Purchase From': Supplier,
        PONo,
        DatePurchase,
        'Cost (RM)': JobPrice,
        'Invoice No': InvoiceNo,
        'Insurance Claim No': InsuranceNo,
        Remark,
        prodTxnID,
      } = partInfo;

      const prevTyre = oldserialno ? this.tyreConfigLayout.findLast((item) => item.index == tyreindex) : null;

      const { whsecode, seqno } = prevTyre?.tyreInfo || {};

      const { inuse: trueInuse } = this.tyreConfigTyresList.findLast((item) => item.seqno == seqno) || {};

      if (updateStatusValue)
        if (type == 'OTHER') {
          this.tyreStatusValue = this.tyreStatusOptions.find((item) => item.label == 'Rotate');
        } else if (prevTyre) {
          if (type == 'ROTATE') this.tyreStatusValue = this.tyreStatusOptions.find((item) => item.label == 'Rotate');
          else this.tyreStatusValue = this.tyreStatusOptions.find((item) => item.keyValue == trueInuse ?? '0');
        } else {
          this.tyreStatusValue = this.tyreStatusOptions[0];
        }
      this.tyreStoreInValue = this.warehouseList.find((item) => item['Warehouse Code'] == whsecode);

      if (type && updateStatusValue) {
        this.tyreReplaceValue = this.tyreReplaceRadio.find((item) => item.keyValue == type);
        this.tyreConfigRotateSelectedTyre = null;
        if (type == 'ROTATE' || type == 'OTHER') {
          let inventoryToLoad = null;
          let serialToSelect = serialno;

          if (action == 'N') {
            if (otherinventoryno) {
              inventoryToLoad = tyreInfo.otherinventoryno;
            } else {
              await this.tyreConfigRadioChanged();
              serialToSelect = this.tyreConfigTableSelectedRow[0]?.['New Serial No'] ?? serialno;
            }
          } else if (otherinventoryno) {
            inventoryToLoad = otherinventoryno;
          } else {
            await this.tyreConfigRadioChanged();
            serialToSelect = this.tyreConfigTableSelectedRow[0]?.['New Serial No'] ?? serialno;
          }

          if (inventoryToLoad) {
            this.tyreConfigOtherVehicleValue = this.vehicleList.find((item) => item['Inventory #'] == inventoryToLoad);
            await this.getTyreConfigOtherVehicle();
          }

          this.tyreConfigRotateSelectedTyre = this.tyreConfigSelectedVehicleLayout?.findLast(
            (item) => item.tyreInfo?.serialno == serialToSelect,
          );
          this.tyreConfigSelectedVehicleDropdownValue = this.tyreConfigSelectedVehicleDropdownList?.findLast(
            (item) => item.tyreInfo?.serialno == serialToSelect,
          );
        } else {
          this.tyreConfigRotateLayoutChange(type);
        }
      }

      this.tyreConfigSerialNoValue = serialno ?? '';

      this.tyreExistingValue = this.tyreNewList.find((item) => item['Serial No'] == serialno) || null;

      let findTyreType = this.tyreTypeList.find((item) => item.ProdTxnID == prodTxnID);
      if (!findTyreType) findTyreType = this.tyreTypeList.find((item) => item.ProdTxnID == prodtxnid);
      if (!findTyreType) findTyreType = this.tyreTypeList.find((item) => item['Product Code'] == tyretype);

      if (!findTyreType) {
        this.tyreConfigTyreTypeDisabled = false;
      } else {
        this.tyreConfigTyreTypeDisabled = true;
        this.tyreConfigTyreTypeValue = findTyreType || null;
      }

      this.tyreConfigPartNoValue = partno ?? PartNo ?? '';
      this.tyreConfigSizeValue = tyresize || (findTyreType?.Size ?? '');
      this.tyreConfigTakenFromValue = this.warehouseList.find((item) => item['Warehouse Code'] == TakenFrom) || {};
      this.tyreConfigUnitPriceValue = this.$currencyFormat(partprice ?? JobPrice ?? '0');
      this.tyreConfigMfgDateValue = MfgDate ? new Date(MfgDate) : null;
      this.tyreConfigModelValue = Model ?? '';
      this.tyreConfigMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) || {};
      this.tyreConfigManufacturerValue = this.manufacturerList.find((item) => item.MFGCode == Manufacturer) || {};
      this.tyreConfigSupplierValue = this.supplierList.find((item) => item['Supplier Code'] == Supplier) || {};
      this.tyreConfigPONoValue = PONo ?? '';
      if (datepurchase) this.tyreConfigDatePurchaseValue = new Date(datepurchase);
      else if (DatePurchase) this.tyreConfigDatePurchaseValue = new Date(DatePurchase);
      else this.tyreConfigDatePurchaseValue = null;

      this.tyreConfigJobPriceValue = this.$currencyFormat(JobPrice ?? partprice ?? '0');
      this.tyreConfigInvoiceNoValue = InvoiceNo ?? '';
      this.tyreConfigInsuranceNoValue = InsuranceNo ?? '';
      this.tyreConfigRemarksValue = Remark ?? '';
    },

    tyreConfigUnitPriceOnInput(newValue, _oldValue) {
      if (this.tyreReplaceValue?.keyValue == 'NEW') {
        this.tyreConfigJobPriceValue = newValue;
      }
    },

    //#endregion

    //#region misc

    async inventoryNoSelected(newInv, oldInv) {
      const { 'Inventory #': invNo } = newInv || {};
      const { 'Inventory #': invNoOld } = oldInv || {};
      if (invNo == invNoOld) return;
      if (oldInv) {
        const discard = await this.cancelEdit(
          'Changing the Inventory will reset all fields!<br>Do you wish to discard the changes?',
        );
        if (!discard) {
          this.jobInventoryValue = oldInv;
          return;
        }
      }
      if (invNo) {
        this.clearPopupFieldsWithoutJobNo();
        this.jobInventoryValue = newInv;
        await this.populateVehicleInfo(invNo);
        const vehicleWithDriver = await this.getVehicleDriver(invNo);
        const { CurrDriver } = vehicleWithDriver?.[0] || {};
        this.requestedByValue = this.employeeList.find((item) => item.EmployeeID == CurrDriver) ?? null;
      }
    },

    parsePartAmount(value) {
      const normalized = typeof value == 'string' ? value.replace(/,/g, '').trim() : value;
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    },

    partHasWarehouseLink(part) {
      if (part?.HasGRNDetail !== null && part?.HasGRNDetail !== undefined && part?.HasGRNDetail !== '') {
        return part.HasGRNDetail == 1 || part.HasGRNDetail == '1';
      }

      const warehouseLinkValue = part?.GRNGuid;
      return Boolean(String(warehouseLinkValue ?? '').trim());
    },

    normalizePartQuantityValue(value) {
      const quantityValue = String(value ?? '').trim();
      return quantityValue ? this.parsePartAmount(quantityValue).toString() : '';
    },

    warehouseItemEffectiveQuantityBalance(item) {
      const rawQuantityBalance = item?.warehouseItemRawQuantityBalance ?? item?.['Quantity Balance'];
      let quantityBalance = this.parsePartAmount(rawQuantityBalance);
      const savedPart = this.partFields.partWarehouseItemSavedValue;
      const isMergedWarehouseItem = Array.isArray(item?.warehouseItemRows) && item.warehouseItemRows.length > 1;
      if (savedPart && this.warehouseItemMatchesPart(item, savedPart) && !isMergedWarehouseItem) {
        quantityBalance += this.parsePartAmount(savedPart.Quantity);
      }
      return quantityBalance;
    },

    warehouseItemMatchesPart(item, part) {
      if (!item || !part) return false;

      const normalizeIdentifier = (value) => String(value ?? '').trim().toUpperCase();
      const itemAllocation = normalizeIdentifier(item.AllocGUID);
      const partAllocation = normalizeIdentifier(part.AllocGUID);
      const itemGitNo = normalizeIdentifier(item.GitNo);
      const partGitNo = normalizeIdentifier(part.GitNo);
      const itemPLGuid = normalizeIdentifier(item.PLGuid);
      const partPLGuid = normalizeIdentifier(part.PLGuid);
      const itemPLNo = normalizeIdentifier(item.PLNo);
      const partPLNo = normalizeIdentifier(part.PLNo);
      const itemGRNGuid = normalizeIdentifier(item.GRNGuid);
      const partGRNGuid = normalizeIdentifier(part.GRNGuid);
      if (itemAllocation && partAllocation) return itemAllocation == partAllocation;
      if (itemPLGuid && partPLGuid) {
        return !itemGitNo || !partGitNo || itemGitNo == partGitNo ? itemPLGuid == partPLGuid : false;
      }
      if (itemPLNo && partPLNo) return itemPLNo == partPLNo;
      if (itemGRNGuid && partGRNGuid) return itemGRNGuid == partGRNGuid;

      const itemWarehouse = normalizeIdentifier(item.Warehouse);
      const partWarehouse = normalizeIdentifier(part.Warehouse || part['Taken From']);
      if (!itemWarehouse || itemWarehouse != partWarehouse) return false;

      const partSerialNo = normalizeIdentifier(part['Serial No']);
      const itemSerialNo = normalizeIdentifier(item['Serial No']);
      if (partSerialNo) return itemSerialNo == partSerialNo;

      const partNo = normalizeIdentifier(part['Part No']);
      return Boolean(partNo) && itemSerialNo == '' && normalizeIdentifier(item['Part No']) == partNo;
    },

    warehouseItemPrimaryLabel(item) {
      const warehouse = String(item?.Warehouse || item?.['Warehouse Code'] || '').trim();
      const serialNo = String(item?.['Serial No'] || '').trim();
      if (serialNo && warehouse) return `${serialNo} (${warehouse})`;
      if (serialNo) return serialNo;
      return warehouse ? `(${warehouse})` : '';
    },

    mergeWarehouseItemRows(items) {
      const mergedItems = [];
      items.forEach((item) => {
        const allocation = String(item?.AllocGUID ?? '')
          .trim()
          .toUpperCase();
        if (!allocation) {
          mergedItems.push(item);
          return;
        }

        const existingIndex = mergedItems.findIndex(
          (existing) =>
            String(existing?.AllocGUID ?? '')
              .trim()
              .toUpperCase() == allocation,
        );
        if (existingIndex == -1) {
          mergedItems.push({ ...item, warehouseItemRows: [item] });
          return;
        }

        const existing = mergedItems[existingIndex];
        const mergedItem = { ...existing };
        Object.keys(item).forEach((key) => {
          const existingValue = mergedItem[key];
          const itemValue = item[key];
          if (existingValue === null || existingValue === undefined || String(existingValue).trim() === '') {
            mergedItem[key] = itemValue;
          }
        });
        mergedItem.warehouseItemRawQuantityBalance =
          this.parsePartAmount(existing.warehouseItemRawQuantityBalance) +
          this.parsePartAmount(item.warehouseItemRawQuantityBalance);
        mergedItem['Quantity Balance'] = mergedItem.warehouseItemRawQuantityBalance;
        mergedItem.warehouseItemRows = [...(existing.warehouseItemRows || [existing]), item];
        mergedItem.label = this.warehouseItemPrimaryLabel(mergedItem);
        mergedItems[existingIndex] = mergedItem;
      });
      return mergedItems;
    },

    warehouseItemFromSavedPart(part) {
      const quantity = this.parsePartAmount(part.Quantity) || 1;
      const cost = this.parsePartAmount(part['Cost (RM)']);
      const warehouse = part['Taken From'] || part.Warehouse || '';
      const serialNo = part['Serial No'] || '';
      const manufactureDate = part.MfgDate || '';
      const purchaseDate = part['Date Purchase'] || '';

      return {
        Warehouse: warehouse,
        'Serial No': serialNo,
        'Part Type': part.Type || part['Part Type'] || '',
        'Part No': part['Part No'] || '',
        'Quantity Balance': 0,
        warehouseItemRawQuantityBalance: 0,
        HasGRNDetail: 1,
        'Unit Price': this.$currencyFormat(quantity ? cost / quantity : 0),
        ProdTxnID: part.prodTxnID || part.ProdTxnID || '',
        Supplier: part['Purchase From'] || '',
        'Date In': purchaseDate,
        'Date Purchase': purchaseDate ? new Date(purchaseDate).formatString('yyyy-MM-dd') : '',
        'Model No': part.Model || '',
        Model: part.Model || '',
        MadeIn: part['Made In'] || '',
        Manufacturer: part.Manufacturer || '',
        ProdDate: manufactureDate,
        'Manufacture Date': manufactureDate,
        InvoiceNo: part['Invoice No'] || '',
        PONo: part.PONo || '',
        BinLoc: part.BinLoc || '',
        AllocGUID: part.AllocGUID || '',
        GitNo: part.GitNo || '',
        PLGuid: part.PLGuid || '',
        PLNo: part.PLNo || '',
        GRNGuid: part.GRNGuid || '',
        label: this.warehouseItemPrimaryLabel({ Warehouse: warehouse, 'Serial No': serialNo }),
      };
    },

    partWarehouseValue(item) {
      const warehouseCode = String(item?.Warehouse || item?.['Warehouse Code'] || '').trim();
      if (!warehouseCode) return null;

      const warehouse = this.warehouseList.find(
        (warehouseItem) => String(warehouseItem['Warehouse Code'] ?? '').trim() == warehouseCode,
      );
      const selectedWarehouse = warehouse ? { ...warehouse } : { 'Warehouse Code': warehouseCode, label: warehouseCode };
      selectedWarehouse.Warehouse = warehouseCode;
      if (item?.AllocGUID) selectedWarehouse.AllocGUID = item.AllocGUID;
      if (item?.BinLoc) selectedWarehouse.BinLoc = item.BinLoc;
      return selectedWarehouse;
    },

    partSerialNoInputChanged(value) {
      const selectedSerialNo = String(this.partFields.partSerialNoValue?.['Serial No'] ?? '').trim();
      if (selectedSerialNo != String(value ?? '').trim()) {
        this.partFields.partSerialNoValue = null;
      }
      this.trySelectWarehouseItemFromManualFields();
    },

    partWarehouseSelected() {
      this.trySelectWarehouseItemFromManualFields();
    },

    trySelectWarehouseItemFromManualFields() {
      if (this.partWarehouseItemSelected || !this.partFields.partTypeValue) return;

      const warehouse = String(
        this.partFields.partTakenFromValue?.Warehouse ||
          this.partFields.partTakenFromValue?.['Warehouse Code'] ||
          this.partFields.partTakenFromInputValue ||
          '',
      ).trim();
      const serialNo = String(this.partFields.partSerialNoInputValue || '').trim();
      if (!warehouse || !serialNo) return;

      const matches = this.warehouseItemListFiltered.filter(
        (item) => String(item.Warehouse ?? '').trim() == warehouse && String(item['Serial No'] ?? '').trim() == serialNo,
      );
      if (matches.length == 1) this.warehouseItemSelected(matches[0]);
    },

    partTypeSelected() {
      this.clearPartFieldsWithoutType();
      const { partTypeValue } = this.partFields || {};
      const { Size, 'Unit Price': UnitPrice } = partTypeValue;
      this.partFields.partSizeValue = Size ?? '';
      this.partFields.partUnitPriceValue = this.$currencyFormat(UnitPrice ?? '0');
    },

    serialNoSelected() {
      const { partSerialNoInputValue } = this.partFields || {};
      const findSerial = this.serialNoListFiltered.find((item) => item['Serial No'] == partSerialNoInputValue) ?? null;
      if (!findSerial) {
        return;
      }
      this.partFields.partSerialNoValue = findSerial;

      this.partFields.partTakenFromValue = null;
      this.partFields.partTakenFromInputValue = '';
      this.partFields.partModelValue = '';
      this.partFields.partMadeInValue = null;
      this.partFields.partMFGDateValue = null;
      this.partFields.partDatePurchaseValue = null;
      this.partFields.partPurchaseFromValue = null;
      this.partFields.partPartNoValue = '';
      this.partFields.partUnitPriceValue = '';
      this.partFields.partQuantityValue = '';

      const { Warehouse, Model, MadeIn, ProdDate, PurcDate, PalletNo, UnitPrice, Supplier, Manufacturer } = findSerial;

      this.partFields.partTakenFromInputValue = Warehouse;
      this.partFields.partTakenFromValue = this.takenFromListFiltered.find(
        (item) => item.Warehouse == Warehouse && item['Serial No'] == partSerialNoInputValue,
      );
      this.partFields.partModelValue = Model;
      this.partFields.partMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) ?? null;
      this.partFields.partMFGDateValue = ProdDate ? new Date(ProdDate) : null;
      this.partFields.partDatePurchaseValue = PurcDate ? new Date(PurcDate) : null;
      this.partFields.partPartNoValue = PalletNo;
      this.partFields.partUnitPriceValue = this.$currencyFormat(UnitPrice ?? '0');
      this.partFields.partQuantityValue = '1';
      this.partFields.partManufacturerValue =
        this.manufacturerList.find((item) => item.MFGCode == Manufacturer) ?? null;
      this.partFields.partPurchaseFromValue =
        this.supplierList.find((item) => item['Supplier Code'] == Supplier) ?? null;
    },

    warehouseItemSelected(item) {
      if (!item) {
        this.partFields.partWarehouseItemValue = null;
        return;
      }

      const serialItem = this.serialNoList.find((serial) => serial['Serial No'] == item['Serial No']) || {};
      const manufacturer = item.Manufacturer || serialItem.Manufacturer;
      const madeIn = item.MadeIn || serialItem.MadeIn;
      const model = item['Model No'] || serialItem.Model || '';
      const partNo = item['Part No'] || serialItem.PalletNo || '';
      const unitPrice = this.parsePartAmount(item['Unit Price'] ?? serialItem.UnitPrice ?? '0');
      const supplier = item.Supplier || serialItem.Supplier;
      const manufactureDate = item.ProdDate || serialItem.ProdDate;
      const purchaseDate = item['Date In'] || serialItem.PurcDate;

      this.partFields.partWarehouseItemValue = item;
      this.partFields.partWarehouseItemSearch = item.label || '';
      this.partFields.partSerialNoValue = serialItem['Serial No'] ? serialItem : null;
      this.partFields.partSerialNoInputValue = item['Serial No'] || '';
      const selectedWarehouse = this.partWarehouseValue(item);
      this.partFields.partTakenFromValue = selectedWarehouse;
      this.partFields.partTakenFromInputValue = selectedWarehouse?.label ?? item.Warehouse ?? '';
      this.partFields.partPartNoValue = partNo;
      this.partFields.partModelValue = model;
      this.partFields.partMadeInValue = this.countryList.find((country) => country['Country Code'] == madeIn) ?? null;
      this.partFields.partMFGDateValue = manufactureDate ? new Date(manufactureDate) : null;
      this.partFields.partDatePurchaseValue = purchaseDate ? new Date(purchaseDate) : null;
      this.partFields.partUnitPriceValue = this.$currencyFormat(unitPrice);
      this.partFields.partManufacturerValue =
        this.manufacturerList.find((manufacturerItem) => manufacturerItem.MFGCode == manufacturer) ?? null;
      this.partFields.partPurchaseFromValue =
        this.supplierList.find((supplierItem) => supplierItem['Supplier Code'] == supplier) ?? null;
      this.partFields.partPONoValue = item.PONo || '';
      this.partFields.partInvoiceNoValue = item.InvoiceNo || '';

      const quantityBalance = this.partQuantityMaximum;
      const currentQuantity = this.parsePartAmount(this.partFields.partQuantityValue);
      if (quantityBalance !== null && quantityBalance > 0 && (!currentQuantity || currentQuantity > quantityBalance)) {
        this.partFields.partQuantityValue = quantityBalance >= 1 ? '1' : quantityBalance.toString();
      }
      const normalizedQuantity = this.normalizePartQuantityValue(this.partFields.partQuantityValue);
      if (normalizedQuantity) this.partFields.partQuantityValue = normalizedQuantity;
    },

    takenFromSelected() {
      const { partTakenFromValue, partTakenFromInputValue } = this.partFields || {};

      const findWarehouse = this.takenFromListFiltered.find((item) => item.Warehouse == partTakenFromInputValue);
      if (!findWarehouse && !partTakenFromValue) return;

      this.partFields.partSerialNoValue = null;
      this.partFields.partSerialNoInputValue = '';
      this.partFields.partModelValue = '';
      this.partFields.partMadeInValue = null;
      this.partFields.partMFGDateValue = null;
      this.partFields.partDatePurchaseValue = null;
      this.partFields.partPurchaseFromValue = null;
      this.partFields.partPartNoValue = '';
      this.partFields.partUnitPriceValue = '';
      this.partFields.partQuantityValue = '';
      this.partFields.partPONoValue = '';
      this.partFields.partInvoiceNoValue = '';

      const {
        'Serial No': SerialNo,
        Model,
        MadeIn,
        ProdDate,
        'Date In': PurchDate,
        Supplier,
        'Unit Price': UnitPrice,
        'Part No': PartNo,
        PONo,
        InvoiceNo,
        PickQty,
      } = partTakenFromValue || {};

      this.partFields.partSerialNoInputValue = SerialNo;
      this.partFields.partModelValue = Model;
      this.partFields.partMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) ?? null;
      this.partFields.partMFGDateValue = ProdDate ? new Date(ProdDate) : null;
      this.partFields.partDatePurchaseValue = PurchDate ? new Date(PurchDate) : null;
      this.partFields.partPurchaseFromValue =
        this.supplierList.find((item) => item['Supplier Code'] == Supplier) ?? null;
      this.partFields.partPartNoValue = PartNo;
      this.partFields.partUnitPriceValue = this.$currencyFormat(UnitPrice ?? '0');
      this.partFields.partQuantityValue = this.normalizePartQuantityValue(PickQty ?? '1') || '1';
      this.partFields.partPONoValue = PONo;
      this.partFields.partInvoiceNoValue = InvoiceNo;
    },

    async tyreStatusSelected(_new, oldValue) {
      if (!oldValue) return;
      const discard = await this.$showAlert(
        'warning',
        'Changing this will discard your current changes!',
        'Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );

      if (discard == 'Cancel') {
        this.tyreStatusValue = oldValue;
        return false;
      }
      this.tyreConfigSelectedVehicleLayout = null;
      this.tyreConfigSelectedVehicleNo = '';
      this.tyreReplaceValue = null;
      this.clearTyreConfigFieldsOnly();
    },
    tyreTypeSelected() {
      const { keyValue: replaceType } = this.tyreReplaceValue || {};
      if (replaceType != 'NEW') return;
      const { 'Unit Price': UnitPrice } = this.tyreConfigTyreTypeValue || {};
      this.tyreConfigUnitPriceValue = this.$currencyFormat(UnitPrice ?? '0');
      this.tyreConfigJobPriceValue = this.tyreConfigUnitPriceValue;
    },
    //#endregion

    //#region helpers
    async cancelEdit(warningText = null) {
      if (!warningText) warningText = 'You have unsaved changes.<br>Do you wish to discard the changes?';
      const discard = await this.$showAlert('warning', 'Discard Changes', warningText, [
        { text: 'Discard', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);

      if (discard == 'Cancel') return false;
      return true;
    },

    async tabClick(_tabItem, tabIndex) {
      if (this.fieldsChanged) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }
      this.selectedTabIndex = tabIndex;
      this.tabFieldsCancel();
    },

    async tableRowClicked(_buttonText, rowClicked) {
      if (this.fieldsChanged && !this.viewMode) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }
      this.tabFieldsAction = this.viewMode ? 'V' : 'E';
      this.tabFieldsCancel();
      switch (this.tabItems[this.selectedTabIndex].title) {
        case this.docTabTitle:
          this.docTableSelectedRows = [rowClicked];
          if (this.docSelectedItem != rowClicked) {
            this.populateDocumentFields(rowClicked);
            if (this.tabFieldsAction == 'E') this.docTableData = this.reorderTableNo(this.docTableData);
            this.docSelectedItem = rowClicked;
            this.docTableSelectedRows = [rowClicked];
          }
          break;
        case this.taskTabTitle:
          this.taskTableSelectedRows = [rowClicked];
          if (this.taskSelectedItem != rowClicked) {
            this.populateTaskFields(rowClicked);
            this.taskSelectedItem = rowClicked;
            this.taskTableSelectedRows = [rowClicked];
          }
          break;
        case this.partTabTitle:
          this.partTableSelectedRows = [rowClicked];
          if (this.partSelectedItem != rowClicked) {
            const part = this.partTableData.find((item) => item.seqNo == rowClicked.seqNo);
            this.populatePartFields(part);
            this.partSelectedItem = part;
            this.partTableSelectedRows = [rowClicked];
          }
          break;
      }

      this.tabsFields = true;
    },

    convertToArray(item) {
      if (!item) return [];
      return Array.isArray(item) ? item : [item];
    },

    combineDateTime(date, time) {
      //the time is to be specifically HHMM
      const dateToArray = date?.split('T')[0].split('-') || null;
      return dateToArray ?
          new Date(dateToArray[0], dateToArray[1] - 1, dateToArray[2], time.substring(0, 2), time.substring(2, 4))
        : null;
    },

    reorderTableNo(table) {
      return table.map((item, idx) => {
        const copy = { ...item };
        delete copy['#'];
        return { '#': idx + 1, ...copy };
      });
    },

    tyrePositionInfo(tyreIndex) {
      const align =
        tyreIndex <= 49 ? 'L'
        : tyreIndex <= 99 ? 'R'
        : 'S';

      const rowNo = tyreIndex < 100 ? (tyreIndex % 10) + 1 : 0;

      let tyrePosition = '';
      if (align == 'L') {
        tyrePosition = rowNo >= 1 && rowNo <= 5 ? 'FL' : 'RL';
      } else if (align == 'R') {
        tyrePosition = rowNo >= 1 && rowNo <= 5 ? 'FR' : 'RR';
      } else if (align == 'S') {
        tyrePosition = 'SP';
      } else {
        tyrePosition = rowNo == 0 ? 'FC' : 'RC';
      }

      let columnNo = 0;
      if (tyreIndex < 100) {
        const strIndex = tyreIndex.toString();
        const firstDigit = strIndex.length == 1 ? 0 : parseInt(strIndex[0], 10);
        columnNo = firstDigit + 1;
      }

      return { align, tyrePosition, rowNo, columnNo };
    },

    tyreConfigToStructure(tyreConfig, tyreLayout) {
      const tyreConfigList = this.convertToArray(tyreConfig).map((tyre) => {
        const {
          TyreIndex,
          SeqNo,
          'Serial No': serialNo,
          'Part No': PartNo,
          Model,
          Manufacturer,
          MadeIn,
          Size,
          Position,
          'Mfg Date': mfgdate,
          DatePurchase,
          'Date Change': dateChange,
          Align,
          '# of Retread': noOfRetread,
          'In Use': inuse,
          Remark,
          InventoryNo,
          TyreGUID,
          JobNo,
          RepairType,
          WHSE_Code,
          OldSerialNo,
          StartMileage,
          EndMileage,
          TyreType,
          UnitPrice,
          Supplier,
          WhseCode,
          ProdTxnId,
          PartPrice,
          DateUpdated,
          OtherInventoryNo,
          OtherJobNo,
        } = tyre || {};

        const { description } = tyreLayout.find((item) => item.index == TyreIndex) || {};
        const repairType = cleanText(RepairType);

        return {
          serialno: cleanText(serialNo),
          partno: PartNo,
          model: Model,
          manufacturer: Manufacturer,
          madein: MadeIn,
          tyresize: Size,
          tyreposition: description || Position,
          mfgdate: mfgdate?.split('T')[0] || null,
          datepurchase: DatePurchase?.split('T')[0] || null,
          datechange: dateChange?.split('T')[0] || null,
          align: Align,
          noofretread: noOfRetread,
          inuse,
          remark: Remark,
          inventoryno: cleanText(InventoryNo),
          seqno: parseInt(SeqNo),
          tyreguid: TyreGUID,
          tyreindex: cleanText(TyreIndex),
          label: `${description} - ${cleanText(serialNo)}`,
          action: 'O',
          jobno: cleanText(JobNo),
          type: typeof repairType == 'string' ? repairType.toUpperCase() : repairType,
          storein: cleanText(WHSE_Code),
          oldserialno: cleanText(OldSerialNo),
          startmileage: StartMileage,
          endmileage: EndMileage,
          tyretype: TyreType,
          unitprice: UnitPrice,
          supplier: Supplier,
          whsecode: WhseCode,
          prodtxnid: ProdTxnId,
          partprice: PartPrice,
          dateupdated: DateUpdated,
          otherinventoryno: cleanText(OtherInventoryNo),
          otherjobno: cleanText(OtherJobNo),
        };
      });

      return tyreConfigList;
    },

    toUTCDateTime(localDateStr) {
      if (localDateStr && localDateStr instanceof Date && !isNaN(localDateStr)) {
        return localDateStr.formatString('yyyy-MM-ddTHH:mm:ss');
      }
      return localDateStr;
    },
    //#endregion

    getFilterStartDate(endDate) {
      const startDate = new Date(endDate);
      const weeks = Math.max(0, Math.floor(Number(this.defaultWeeksToShow) || 0));
      startDate.setDate(startDate.getDate() - weeks * 7);
      return startDate;
    },

    async getDefaultWeeksToDisplay() {
      const result = await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=EMS_JobFilterMonth');
      const { SettingsValue } = result[0] || {};
      if (SettingsValue === null || SettingsValue === undefined || SettingsValue === '') return;
      this.defaultWeeksToShow = Number(SettingsValue);
      this.activeStartDate = this.getFilterStartDate(this.activeEndDate);
      this.filterStartDate = this.getFilterStartDate(this.filterEndDate);
    },
    async openFromParams() {
      const params = new URLSearchParams(window.location.search);

      const newValue = params.get('New');
      if (newValue != null) this.menuClicked({ key: 'New' });

      const View = params.get('View');

      const Edit = params.get('Edit');

      if (View || Edit) {
        this.jobNoFilterActive = true;
        this.dateFilterActive = false;
        this.filterStartJobNo = this.EMSJobNoList.find((jobno) => jobno.JobNo == (View || Edit));
        await this.filterRefreshClick();
        this.workshopJobsTableSelectedRows = this.workshopJobsTableData;
        if (View) this.menuClicked({ key: 'View' });
        if (Edit) this.menuClicked({ key: 'Edit' });
      }
    },
  },

  async mounted() {
    await this.getSAS();
    const promiseList = [
      this.getVehicleList(),
      this.getEmployeeList(),
      this.getSupplierList(),
      this.getDocType(),
      this.getDocCode(),
      this.getServices(),
      this.getServicesTask(),
      this.getPartType(),
      this.getSerialNo(),
      this.getTakenFrom(),
      this.getCountry(),
      this.getManufacturer(),
      this.getWarehouse(),
      this.getJobPartTyreList(),
      this.getJobPartTyreSaveList(),
      this.getLocalCurrency(),
      this.getDefaultWeeksToDisplay(),
    ];
    let finished = 0;
    for (const p of promiseList) {
      p.then(() => {
        finished++;
        this.$topLoading((finished / promiseList.length) * 0.9);
      });
    }
    await Promise.all(promiseList);
    //If you open items before the other lists are fetched, items might not get populated properly.
    //get workshop jobs last.
    await this.getWorkshopJobs();
    this.$topLoading(1);

    this.openFromParams();
  },

  watch: {
    tyreRotateOtherVehicleWorkshopJobValue() {
      if (this.tyreRotateOtherVehicleWorkshopJobInputValue) this.getTyreConfigOtherVehicle();
    },

    tyreConfigSelectedVehicleLayout() {
      const findInLayout = this.tyreConfigSelectedVehicleLayout?.find(
        (item) => this.tyreConfigRotateSelectedTyre?.index == item.index,
      );
      if (findInLayout?.state != 'saved') {
        this.clearTyreConfigFieldsOnly();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.main {
  padding: 10px 45px;
  padding-right: 4px;
  display: flex;
  flex-direction: row;
  gap: 4px;
  font-size: 12px;

  > .left-menu-container {
    height: 100%;
    width: fit-content;
  }

  .right-container {
    display: flex;
    flex-direction: column;
    gap: 4px;

    width: 100%;
    height: 100%;

    min-width: 0;
    .right-inner-container {
      width: 100%;
      padding: 6px;
      border-radius: 5px;
      background-color: white;
    }
  }
}

.popup-entry {
  .top-info-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 0 5px;
    gap: 10px;
  }

  .tab-component-container {
    margin-top: 5px;
    height: calc(100% - 30px); // deduct height of Inventory No and Inventory Code inputs

    > .tab-component {
      height: 100%;

      .tab-container {
        height: 100%;
      }
    }
  }

  .table-tab-container {
    display: grid;
    grid-template-columns: 3fr 2fr;
    gap: 10px;
    > *:only-child {
      grid-column: 1/-1;
    }

    .cta-group {
      display: flex;
      gap: 4px;
      justify-content: end;
      margin-bottom: 10px;
      align-items: center;
    }

    .table-container {
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
    }

    .part-fields-container {
      min-width: 0;
      min-height: 0;
      align-self: start;
    }
  }
}

.cta-group {
  display: flex;
  gap: 4px;
  justify-content: end;
  align-items: center;
  margin-bottom: 8px;
}

.tyre-config-tab {
  display: flex;
  flex-direction: column;
  gap: 8px;
  .tyre-history-notice {
    align-self: start;
    height: fit-content;
    padding: 8px 10px;
    border-left: 3px solid #d6a700;
    background: #fff4b8;
    color: #5f4700;
    font-size: 12px;
  }
}

.tyre-config-tab-container {
  min-height: 0;
  flex: 1;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  .tyre-config-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .table-wrapper {
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    .table-box-main {
      min-height: 0;
    }
  }
}

.popup-entry-main {
  padding-right: 4px;
}

.tyre-config {
  position: fixed;
  z-index: 9999;
  background: rgba(0, 0, 0, 50%);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  .tyre-config-inner {
    background: white;
    height: fit-content;
    max-height: 85dvh;
    min-width: 600px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 5px;
    position: relative;
    top: 20px;
    padding: 0px 8px 0 8px;
    border-radius: 5px;

    .cta-group {
      display: flex;
      gap: 5px;
      justify-content: end;
      align-items: center;
      margin-bottom: 0;
      position: sticky;
      bottom: 0;
      padding: 8px 0;
      background-color: white;

      margin-top: -4px;
    }

    .tyre-config-list {
      display: flex;

      height: 100%;
      // max-height: 400px;
      gap: 5px;
      width: 100%;
    }

    .tyre-info-container {
      min-width: 560px;
      gap: 4px;
    }
  }
}

.rotate-other-vehicle-popup {
  position: fixed;
  z-index: 9999;
  background: rgba(0, 0, 0, 50%);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  .popup-title {
    font-size: 1.05rem;
    font-weight: bold;
    margin-bottom: 8px;
  }

  .rotate-other-vehicle-popup-inner {
    background: white;
    height: fit-content;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 5px;
    position: relative;
    top: 20px;
    padding: 8px;
    border-radius: 5px;
  }

  .cta-group {
    display: flex;
    gap: 5px;
    justify-content: end;
    align-items: center;
    margin-bottom: 0;
  }
}

.flex-container {
  display: flex;
  gap: 8px;
}

.align-items-center {
  align-items: center;
}

.flex-column {
  flex-direction: column;
}

.min-height-0 {
  min-height: 0;
}

.flex-1 {
  flex: 1;
}

.error-text {
  color: red;
}
</style>
